// SHELL DEL MODO MODELO (`/interfaz-v2/modo/modelo`).
//
// Nació como copia congelada de `components/PresetDetailSheet.tsx` para poder rediseñar
// el shell del modo sin tocar el sheet que comparten todos los demás (catálogo,
// automática, video, plantillas…). Era el shell de la variante v2 del banco de pruebas
// (ADR-2026-08-21-modelo-shell-por-variante) y se quedó como EL shell del modo cuando esa
// variante pasó a ser el modo (ADR-2026-09-02-modelo-v2-es-el-modo-modelo).
//
// Sigue separado de `PresetDetailSheet` a propósito: su layout es de una sola pantalla
// —configuración a la izquierda, productos en un panel lateral— y volver a fusionarlo
// sería el sheet compartido lleno de banderas por modo.
//
// Lo que este shell NO forkea —y por lo tanto sigue siendo compartido con el resto de los
// modos— son los componentes que monta adentro: `ChooseProductPanel`, `PickProductPanel`,
// `ConfigurePanel`, `VistaPickerFullscreen`, `SubirProductoDrawer` y compañía. Tocar uno
// de ésos acá cambia también los otros modos: si el cambio es sólo para Modelo, hay que
// forkearlo primero.
//
// La columna derecha del paso 2 sí está forkeada aparte: `ModeloPanel`, que `ConfigurePanel`
// elige por `kind`.

// Sheet del flow de cualquier preset: cubre toda la pantalla (mobile)
// o el área a la derecha del sidebar (desktop) y maneja todos los
// `step` del state machine: preview → choose-product → pick-product →
// upload-product → configure (con la rama paralela select-vistas →
// configure cuando es 'catalogo-ia').
//
// Ojo: este componente es el "núcleo" del browse de los modos
// `template`/`design`/`design-modelo`/`design-auto`/`catalogo-ia`. El
// modo `asistente` usa su propio sheet (AsistenteMarketingSheet) en
// vez de éste — el padre conmuta entre los dos en la rama del JSX.

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Minus,
  Play,
  Plus,
  Sparkles,
} from 'lucide-react';
import type { MockProductV2, PresetV2 } from '../mockData';
import { getFormatLabel } from '../mockData';
import type {
  ModelPickerView,
  ModelSelection,
} from '../ModelPicker';
import { modelSelectionLabel } from '../ModelPicker';
import type {
  FondoPickerView,
  FondoSelection,
} from '../FondoPicker';
import { fondoSelectionLabel } from '../FondoPicker';
// Cuánto ocupa la barra inferior de mobile: el primer paso del modo le deja el hueco
// (ADR-2026-08-04-el-modo-se-lleva-la-barra).
import { ALTO_NAV_INFERIOR } from '../components/NavInferiorMobile';
import type { PoseV2 } from '../posesApi';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
import type { ModelV2 } from '../modelsApi';
import type {
  VistaCatalogo,
  VistaProductoConjuntoOption,
} from '../vistasCatalogo';
import { ChooseProductPanel } from '../components/ChooseProductPanel';
import { PickProductPanelModelo } from './PickProductPanelModelo';
import { usePalabraDeModelo } from '../../../lib/nombreDeModelo';
import {
  CardProductos,
  CardsModeloPose,
  PanelCompacto,
} from './PanelCompacto';
import { BarraInferiorMobile } from './BarraInferiorMobile';
// Los mismos pickers que monta el paso 2 de las otras variantes, acá dentro del panel
// lateral — ADR-2026-08-21-modelo-v2-un-solo-panel-lateral.
import { ModelPickerInline } from './ModelPickerInline';
import { PosePickerInline } from './PosePickerInline';
import { FondoGrilla } from './FondoGrilla';
import { PanelMoodboardModelo, type SeleccionDeMoodboard } from './PanelMoodboardModelo';
import { FormatPickerGrid } from '../FormatPicker';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import {
  ANCHO_PANEL_CONFIG,
  COLUMNA_RESULTADOS_AIRE_INTERNO,
  COLUMNA_RESULTADOS_AL_BORDE,
  COLUMNA_SECCION,
  FILA_MODO_REDISENO,
} from '../components/layoutModo';

/**
 * Columnas de las grillas del panel lateral — las MISMAS para los tres pickers.
 *
 * Productos, modelo y pose se abren en el mismo panel y del mismo ancho: con distinta
 * densidad de grilla, cada uno parecía una pantalla aparte. Tres es lo que pide la foto
 * vertical de cuerpo entero de modelos y poses —a más columnas, la cara o la postura que
 * se está eligiendo queda del tamaño de una miniatura— y es lo que se unificó.
 */
const COLUMNAS_PANEL_LATERAL = 3;

/**
 * Columnas de la grilla de productos en la pantalla de selección de MOBILE.
 *
 * Dos, no las tres del panel lateral (ADR-2026-09-04-los-productos-de-mobile-van-de-a-dos):
 * ese número está calculado para una columna de 615px de desktop, y aplicado al ancho de
 * un teléfono deja tarjetas de ~110px donde la foto del producto —que suele ser una
 * prenda sobre una cama o un colgador— no se distingue de la de al lado, y el nombre
 * sobre la foto se corta en "Nuevo produ…" en todas por igual. De a dos entran enteras.
 */
const COLUMNAS_PRODUCTOS_MOBILE = 2;

/**
 * Ancho del panel lateral, en px — el MISMO para los tres pickers.
 *
 * Productos llegó a estar más ancho (820) porque su grilla es larga y va a tarjetas
 * chicas, pero abrir un picker y que el panel cambiara de tamaño se leía como cambiar de
 * pantalla, no como cambiar de contenido. Un solo ancho: lo que varía adentro es la
 * cantidad de columnas.
 */
const ANCHO_PANEL_LATERAL = 615;


import { ProductosLateral } from './ProductosLateral';
import { SwitchSeleccionMultiple } from '../components/SwitchSeleccionMultiple';
import {
  SubirProductoDrawer,
  SubirProductoInline,
} from '../components/SubirProductoDrawer';
import {
  ModalDeSubirProductos,
  type ModoDeSubida,
} from '../../../components/ModalDeSubirProductos';
import {
  AjustesRapidosProducto,
  CALIDAD_OPCIONES,
  type CalidadImagen,
} from '../components/AjustesRapidosProducto';
// El costo del upscale que encadena la Calidad — el mismo número que cobra
// `aplicarCalidadALaTanda` en InterfazV2 (ADR-2026-09-02-la-calidad-se-resuelve-con-el-upscale).
import { creditosPorResolucion } from '../../../lib/resolucionImagen';
import { ContadorImagenes } from '../components/ContadorImagenes';
import { UploadProductPanel } from '../components/UploadProductPanel';
import { VistaPickerFullscreen } from '../components/VistaPickerFullscreen';
import {
  CatalogoCardsFullscreen,
  construirCardsDeCatalogo,
  type CatalogoCard,
} from '../components/CatalogoCardsFullscreen';
import { ConfigurePanel } from '../panels/ConfigurePanel';
import {
  isVoiceGuionIncomplete,
  getGuionDurationSec,
  INFLUENCER_GUION_BASE_COST,
  type VoiceGuionValue,
} from '../components/VoiceGuionSection';
import { scrollToAndShake } from '../scrollShake';
import {
  MAX_MODELOS_SERIE,
  resolveTandaModelo,
  resumenTandaModelo,
} from '../designModeloCount';
import { isSinGancho } from '../ganchosCatalog';
import { FillBarSliderV2 } from '../components/FillBarSliderV2';
import {
  VIDEO_INFLUENCER_DURATION_MIN,
  VIDEO_INFLUENCER_DURATION_MAX,
  VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO,
} from '../components/VideoConfigSheet';
import { MultiPresetCarousel, MultiSelectedStrip } from '../components/MultiPresetUI';
import { ProductHeroCollage } from '../components/ProductHeroCollage';
import { type Miga } from '../../../components/Migas';
import {
  MODE_KIND_TO_PATH,
  esCatalogoIA,
  // Las tres variantes tienen que entrar por TODAS las comparaciones de flujo que el
  // original hace contra `'design-modelo'`: si no, el modo arranca con el layout viejo
  // (una columna) en vez del de dos columnas que se está rediseñando.
  esDesignModelo,
  usaBibliotecaDeFondos,
  nombreCortoModo,
} from '../modesCatalog';
import { RUBRO_HOGAR_DECO, rubroVeMoodboards } from '../../../lib/rubro';
import { useUserGroup } from '../../../hooks/useUserGroup';
// El nombre corto de un modo vive en `COPY_TIRA`, dentro del shell, y se expone por
// esta función — mismo camino que usa `AsistenteMode` para su breadcrumb. No hay
// ciclo: `EstiloMinimal` no importa esta pantalla ni `InterfazV2`.
import {
  EncabezadoPantalla,
  MARCO_PANTALLA,
  BORDE_PANTALLA,
} from '../../../components/PantallaChrome';
// El back del chrome flotante de mobile: la segunda pantalla del modo se lo toma prestado
// para volver al paso 1 en vez de dibujar su propia flecha
// (ADR-2026-09-04-modelo-mobile-un-solo-back-en-el-paso-2).
import { useSidebar } from '../../../context/SidebarContext';

/**
 * Heroes tipo "1 producto → sus resultados" para las pantallas de selección de
 * producto de los modos catálogo/infografía (mismo espíritu que la del polo del
 * modo automático, pero como componente). Cada entrada: la foto original + los
 * resultados apilados. Si un kind no está acá, cae al `<img>` de siempre. Los
 * assets los dropea Imanol en /public/images/<carpeta>/. Ver ADR-817.
 */
const HERO_COLLAGE: Record<
  string,
  { original: string; results: string[]; layout?: 'fan3-wide' | 'fan2-tall' }
> = {
  // Catálogo (ropero, producto ALTO → fan2-tall): la principal es el packshot
  // sobre fondo blanco; la flecha apunta a la foto en contexto + la infografía.
  'catalogo-muebles': {
    original: '/images/modo_catalogo_muebles/hero_packshot.png',
    results: [
      '/images/modo_catalogo_muebles/hero_infografia.png',
      '/images/modo_catalogo_muebles/hero_contexto.jpg',
    ],
    layout: 'fan2-tall',
  },
  // Infografía (mesa ratona, producto ANCHO → fan3-wide): original + los 3
  // estilos (boceto / línea / fotográfica).
  infografia: {
    original: '/images/modo_infografia/hero_original.png',
    results: [
      '/images/modo_infografia/hero_boceto.png',
      '/images/modo_infografia/hero_linea.png',
      '/images/modo_infografia/hero_foto.png',
    ],
    layout: 'fan3-wide',
  },
};

/**
 * El hero también sigue al RUBRO —
 * ADR-2026-08-05-el-hero-de-catalogo-sigue-al-rubro.
 *
 * En Hogar y Decoración, "Catálogo" se presenta con el collage de muebles (packshot →
 * infografía + foto en contexto) en vez del suyo, que es una remera sobre tres vistas de
 * un modelo: adentro del modo, esa imagen es lo que explica QUÉ va a pasar con el
 * producto, y con un ropero no explica nada.
 *
 * Remapea el `kind` a otro que YA tiene collage en vez de repetir los archivos: si el de
 * muebles cambia, cambian los dos juntos.
 */
const HERO_COLLAGE_POR_RUBRO: Record<string, Record<string, string>> = {
  [RUBRO_HOGAR_DECO]: {
    'catalogo-ia': 'catalogo-muebles',
  },
};

// ─── Preset detail sheet ────────────────────────────────────────────────
// Cubre toda la pantalla. Arriba: la imagen del preset en grande (hero).
// Abajo: un container que cambia entre dos vistas según `step`:
//   • choose-product → "Subí la imagen del producto" + CTA "Ver mis productos"
//   • configure      → "¿Querés ajustar la generación?" + chips Pose/Formato
//                      + textarea libre + botón Generar
//
// Backdrop fade-in + sheet con slide-up de 300ms ease-out. Cerrar
// disponible vía X arriba a la derecha y tap en el backdrop.
/** Paso del flujo que dibuja el sheet. */
export type PanelStep =
  | 'preview'
  | 'choose-product'
  | 'pick-product'
  | 'upload-product'
  | 'select-vistas'
  | 'configure'
  | 'asistente'
  // Como 'asistente': el paso existe en `InterfazV2` pero este shell nunca lo monta
  // (ADR-2026-09-07-modo-prompt-libre, ADR-2026-09-07-modo-creador-video).
  | 'prompt-libre'
  | 'creador-video';

export interface PresetDetailSheetModeloProps {
  preset: PresetV2;
  animated: boolean;
  /**
   * Breadcrumb del header de desktop — ADR-2026-08-03-breadcrumbs-pantallas-de-modo.
   *
   * Lo arma `InterfazV2`, que es quien sabe si este sheet es una **pantalla de
   * modo** (una de las cards sintéticas de "Diseña tu imagen") o el detalle de una
   * plantilla, y en qué paso del flujo está. Acá sólo se dibuja.
   *
   * Cuando viene, REEMPLAZA al botón "Volver": el primer nivel hace lo mismo que
   * hacía él. Cuando no viene —plantillas, poses—, el header sigue como estaba.
   */
  migas?: Miga[];
  step: PanelStep;
  /**
   * El modo se está reabriendo con una configuración ya hecha —el "Atrás" desde los
   * resultados, "Seguir generando"— y no en frío
   * (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
   *
   * Lo mira el paso 1: sus productos EXISTEN, pero `InterfazV2` los resuelve contra el
   * catálogo en otro efecto, así que cuando este sheet monta la lista todavía está vacía
   * y sin esto el usuario que vuelve de generar cae en la grilla de productos.
   */
  configuracionRestaurada?: boolean;
  /** Lista de presets para swipear en el step 'preview'. */
  previewList: PresetV2[];
  /** Lista de productos elegidos (multi-selección). Vacío = ninguno aún. */
  selectedProducts: MockProductV2[];
  /** El anuncio salió de una creación y no de un producto —
   *  ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo. Sólo se muestra; el id
   *  que viaja al backend lo pone `InterfazV2` al generar. */
  imagenBase?: { url: string } | null;
  modelSelection: ModelSelection | null;
  fondoVal: string | null;
  formatoVal: string | null;
  otraConfig: string;
  sidebarWidth: number;
  onClose: () => void;
  /** El user tocó "Utilizar" en preview — avanza a choose-product con ese preset. */
  onUtilizarPreset: (p: PresetV2) => void;
  /** El user activó multi-select en preview y eligió N plantillas. Solo
   *  aplica al kind 'template'. La primera se usa como anchor visual. */
  onUtilizarMultiple: (presets: PresetV2[]) => void;
  /** IDs de plantillas seleccionadas en multi-select. Cuando hay más de
   *  una, la imagen del preset detail sheet se reemplaza por un carrusel
   *  de las plantillas seleccionadas. */
  multiPresetIds: string[];
  onShowProducts: () => void;
  /** "Añadir producto al outfit" desde configure — abre el picker de
   *  productos con la selección actual premarcada para sumar más. */
  onAddProducts: () => void;
  /** El user confirmó su selección (uno o varios productos). */
  onConfirmProducts: (products: MockProductV2[]) => void;
  /**
   * La selección de productos EN VIVO, sin confirmar y sin avanzar de paso.
   *
   * La usa "Automático", que no tiene paso 2 (ADR-2026-08-21-automatico-con-el-diseno-nuevo):
   * ahí el CTA del panel es Generar, no Confirmar, así que lo elegido tiene que estar en
   * el estado de arriba en el momento del tap — con el confirm de siempre, el tap
   * generaba con la selección vacía.
   */
  onProductosEnVivo?: (products: MockProductV2[]) => void;
  onBackToChoose: () => void;
  /** Vacía la selección actual y vuelve al step 'choose-product'. */
  onClearProducts: () => void;
  // ── Upload de producto nuevo (step 'upload-product') ────────────
  /** Disparado desde ChooseProductPanel al seleccionar archivos. */
  onStartUpload: (files: FileList | File[]) => void;
  /** "Subir nuevo producto" → entra al paso `upload-product` SIN archivos
   *  (pantalla explicativa) en vez de abrir el file picker directo. */
  onStartNewProduct: () => void;
  /** "Generar sin producto" (ADR-673) — saltea la elección y va directo a
   *  configurar. Solo lo renderizan los paneles cuando el preset es
   *  `video-influencer`. */
  onSkipProduct?: () => void;
  /** "Sin producto" activo (ADR-673) — cambia el chip de Producto del
   *  configurador y saca el toggle de modos de guion. */
  sinProducto?: boolean;
  /** Autogeneración del diálogo en curso / fallida (ADR-674). */
  dialogoLoading?: boolean;
  dialogoError?: string | null;
  /** Alta en bloque: abre el popup "Subir varios productos" de Mi Marca (lo
   *  monta InterfazV2). Va SOLO en el estado vacío de `upload-product` —la
   *  pantalla "Nuevo producto"—, no en las pantallas de elegir producto
   *  (ADR-688 ajusta ADR-684). Antes era un file picker directo exclusivo de
   *  Catálogo; ahora abre el popup y está en todos los modos. */
  onBulkUpload?: () => void;
  /** Plan del usuario. Este sheet no lo usa directo: lo pasa a
   *  CatalogoCardsFullscreen → MultiSlotProductPickerSheet, donde define el
   *  tope del popup de alta en bloque (ADR-684). */
  userPlan?: string | null;
  onAddMoreFiles: (files: FileList | File[]) => void;
  onRemovePendingFile: (idx: number) => void;
  onCancelUpload: () => void;
  onConfirmUpload: () => void;
  pendingProductFiles: File[];
  pendingProductName: string;
  onPendingNameChange: (v: string) => void;
  uploadingProduct: boolean;
  uploadError: string | null;
  /** Subida en bloque (catálogo, 2+ archivos) en curso → mostramos un
   *  spinner DENTRO de la card (donde viven el grid + "Subir nuevo
   *  producto"), no un overlay que tape toda la pantalla. */
  bulkUploadingProducts?: boolean;
  /** Setter del modelo (preset/auto/custom/upload). */
  onModelChange: (sel: ModelSelection) => void;
  /**
   * Reemplaza el contenido de la columna de CONFIGURACIÓN del `configure` a dos
   * columnas. Lo usa el panel de la tanda: al generar, Modelo + Pose dejan lugar
   * a los recuadros de las imágenes en curso
   * (ADR-2026-08-20-tanda-en-el-panel). Sin esto, el sheet no sabe nada de
   * generaciones — el nodo lo arma el padre.
   */
  columnaConfiguracion?: React.ReactNode;
  /**
   * Vuelve de las tandas a la configuración (Modelo + Pose) desde los ajustes rápidos
   * del panel izquierdo — ADR-2026-08-20-varias-tandas-en-el-panel.
   *
   * Sin esto la celda no se dibuja: con la configuración ya a la vista, un control para
   * ir hacia ella no dice nada.
   */
  onCambiarModeloYPose?: () => void;
  /** Baja el z del panel desktop para que el visor de creaciones (lightbox
   *  `fixed z-50` portalizado al body) pueda quedar por encima. */
  cederZIndex?: boolean;
  /** Serie de modelos (grilla multi-select de design-modelo): los que siguen
   *  al principal. Cada uno genera su propia imagen — multiplica el costo. */
  modelSelectionsSerie?: ModelSelection[];
  /** Tap en una card de la grilla: acumula o quita de la serie. */
  onToggleModelSerie?: (sel: ModelSelection) => void;
  /**
   * "Utilizar varios modelos" activo (Modelo v2).
   *
   * Apagado —el default— la grilla del panel es de selección ÚNICA: tocar otra card
   * cambia el modelo. Encendido, acumula y los modelos elegidos salen todos en la MISMA
   * foto (ADR-2026-08-28-modelo-v2-varios-modelos, sobre
   * ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto).
   */
  variosModelos?: boolean;
  onVariosModelosChange?: (v: boolean) => void;
  // ── Modo "varios modelos" (UI-only) ─────────────────────────────
  modelSelectionsExtra: (ModelSelection | null)[];
  onModelChangeExtras: (next: (ModelSelection | null)[]) => void;
  multiModelEnabled: boolean;
  onMultiModelEnabledChange: (v: boolean) => void;
  /** Abre el sheet de modelo en mobile. */
  onOpenModelPicker: () => void;
  /** Abre el sheet directo a una vista. Lo usa design-modelo desktop
   *  cuando el user toca "Creá tu modelo" o "Cargá modelo". */
  onOpenModelSheet: (initialView: ModelPickerView) => void;
  /** Variante del anterior para el sub-flow de multi: abre el sheet
   *  pre-configurado para editar un slot específico con la sub-vista
   *  pedida ('create' o 'upload'). */
  onOpenModelSheetForMultiSlot: (
    slotIdx: number,
    initialView: 'create' | 'upload',
  ) => void;
  /** Productos asignados por slot (modo varios modelos). */
  multiProductsPerSlot: Record<number, MockProductV2[]>;
  /** Tap en "Utilizando"/miniaturas de un slot del MultiView. */
  onTapSlotProducts: (slotIdx: number) => void;
  /**
   * Escribe el outfit de UN modelo del grupo — ADR-2026-08-25-que-lleva-cada-modelo-en-el-panel.
   *
   * Separado de `onTapSlotProducts` porque son dos cosas distintas: aquél ABRE el
   * sheet full-screen del resto del modo, éste guarda lo elegido desde el panel
   * lateral de v2, sin paso de confirmación.
   */
  onSlotProductsChange?: (
    slotIdx: number,
    /** `null` = borrar la asignación propia y volver a heredar los de la foto. */
    products: MockProductV2[] | null,
  ) => void;
  /** Abre el FondoPickerSheet directo a una vista — para "Describí el
   *  fondo" o "Cargá tu fondo" en desktop. */
  onOpenFondoSheet: (initialView: FondoPickerView) => void;
  onFondoChange: (v: string | null) => void;
  onFormatoChange: (v: string | null) => void;
  /** Abre el picker de formato (sheet/side panel). */
  onOpenFormatPicker: () => void;
  onOtraChange: (v: string) => void;
  /** Gancho elegido (modo video-influencer) — controlado desde InterfazV2 para
   *  poder mandarlo como `gancho_id` en la generación. */
  ganchoId?: string | null;
  onGanchoIdChange?: (id: string | null) => void;
  /** Voz + velocidad + guion del modo video-influencer — pass-through
   *  InterfazV2 → ConfigurePanel (sección "Voz y guion"). ADR-525. */
  voiceGuion?: VoiceGuionValue;
  onVoiceGuionChange?: (next: VoiceGuionValue) => void;
  /** Setter de `videoDuration` — barra de duración inline del modo
   *  video-influencer (dentro de "Voz y guion"). ADR-528. */
  onVideoDurationChange?: (v: number) => void;
  /** `cost` = créditos que consumirá la generación (lo que muestra el pill).
   *  InterfazV2 lo usa para chequear saldo suficiente. Ver ADR-518. */
  /**
   * Calidad de la tanda (1080p / 2K / 4K).
   *
   * Llega de `InterfazV2` —donde viven fondo y formato— y no de un `useState` de acá:
   * desde `ADR-2026-09-02-la-calidad-se-resuelve-con-el-upscale` tiene efecto real (el
   * código de generación la lee para encadenar el upscale) y además cambia lo que cuesta
   * la tanda, así que el pill de créditos de este panel la suma.
   */
  calidad: CalidadImagen;
  onCalidadChange: (v: CalidadImagen) => void;
  onGenerate: (cost: number) => void;
  /** Solo plantillas: generación normal + carrusel automático. Se pasa
   *  tal cual al ConfigurePanel. `carruselCount` = cuántas imágenes pedirle
   *  al carrusel (= imágenes elegidas en el stepper − 1). `cost` = créditos
   *  totales de la producción. */
  onGenerateProduction?: (carruselCount: number, cost: number) => void;
  // ── Modo 'design-auto' (cantidad de imágenes) ────────────────────
  /** Cantidad de imágenes a generar — solo se usa cuando el preset es
   *  'design-auto'. Controla el +/- arriba del Generar. */
  autoImageCount: number;
  onAutoImageCountChange: (v: number) => void;
  /** Tope superior del autoImageCount (mínimo siempre es 1). */
  autoImageCountMax: number;
  /** Modelos reales (para el ModelPickerInline desktop). */
  userModels: ModelV2[];
  userAuthId: string | null;
  userCompanyId: string | null;
  onModelCreated: () => void;
  // ── Modo 'design-modelo' ─────────────────────────────────────────────
  allPoses: PoseV2[];
  // ── Catálogo: tab "Mis poses" del vista picker (poses custom) ─────────
  /** Poses custom (muñeco) de la empresa, filtradas a 1 persona. */
  customPoses: PoseV2[];
  /** Crear una pose custom desde el vista picker (sube la foto → muñeco). */
  onUploadCustomPose: (file: File) => void;
  /** true mientras se procesa la subida de una pose custom. */
  uploadingCustomPose: boolean;
  /** Borrar una pose custom propia. */
  onDeleteCustomPose: (pose: PoseV2) => void;
  poseIds: string[];
  onPoseIdsChange: (ids: string[]) => void;
  // ── Modo 'fotos-producto' ──────────────────────────────────────────
  /** Vistas solo-producto compatibles con el producto elegido (ya
   *  filtradas + thumb resuelto). Pass-through a ConfigurePanel. */
  vistasProducto?: VistaProductoConjuntoOption[];
  /** Slugs de las vistas elegidas — una imagen generada por cada una. */
  vistaIds?: string[];
  onVistaIdsChange?: (ids: string[]) => void;
  /** Toggle "Seleccionar múltiples" del menú de poses — pass-through
   *  InterfazV2 → ConfigurePanel. El switch vive en el `PosePickerSheet` (mobile) y,
   *  desde ADR-2026-09-04-el-interruptor-de-varias-poses-tambien-en-desktop, también en
   *  la grilla de poses del panel lateral. */
  multiPoseEnabled: boolean;
  /**
   * Commit del toggle. Opcional para no obligar al fork del Catálogo, que comparte el
   * juego de props (los dos se montan como `SheetDelModo`) y no dibuja el interruptor.
   */
  onMultiPoseEnabledChange?: (enabled: boolean) => void;
  /**
   * La generación ARRANCÓ de verdad: los guards pasaron y la pantalla de carga está
   * arriba — ADR-2026-09-04-el-generar-de-mobile-espera-a-que-la-carga-este-arriba.
   *
   * Lo usa la segunda pantalla de mobile para saber CUÁNDO cerrarse. Cerrarse en el tap
   * mostraba el paso 1 durante todo el chequeo de plan y créditos, que es una ida a la
   * base.
   */
  generacionEnCurso?: boolean;
  onOpenPosePicker: () => void;
  /** Abre el sheet de poses directo en "Subí tu propia pose" — la tarjeta que cierra
   *  la grilla de poses (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). */
  onSubirPose?: () => void;
  bibliotecaFondos: FondoBiblioteca[];
  fondosGuardados: FondoGuardado[];
  fondoSelection: FondoSelection | null;
  onFondoSelectionChange: (sel: FondoSelection) => void;
  onOpenFondoPicker: () => void;
  fondoView: FondoPickerView;
  onFondoViewChange: (v: FondoPickerView) => void;
  onFondoSaved: () => void;
  /** Rubro de la empresa — para que el product picker active la sección
   *  "Sugeridos" solo en rubros de moda. */
  rubro: string | null;
  /**
   * Rubro de la MARCA ACTIVA (con el de la empresa como respaldo) — decide con qué
   * imagen se presenta el modo (ADR-2026-08-05-el-hero-de-catalogo-sigue-al-rubro).
   *
   * Va aparte de `rubro` y no lo reemplaza: ese de arriba es el de la EMPRESA y decide
   * otra cosa (los "Sugeridos" del picker). Unificarlos es probablemente lo correcto,
   * pero es un cambio de comportamiento en una pantalla que no es la de este pedido.
   */
  rubroMarca?: string | null;
  /** Si true, el ConfigurePanel bloquea el add/remove de slots del
   *  multi-modelo. Usado por E-commerce (kind: 'design') con poses
   *  de cantidad fija. */
  lockSlotCount?: boolean;
  // ── Modo 'catalogo-ia' ───────────────────────────────────────────────
  /** Vistas elegidas en el step `select-vistas`. */
  catalogoVistas: VistaCatalogo[];
  /** Setter del array de vistas elegidas. */
  onCatalogoVistasChange: (next: VistaCatalogo[]) => void;
  /** Confirma la selección de vistas y pasa al step `configure`. */
  onConfirmCatalogoVistas: () => void;
  /** Vuelve del step `configure` (cards review) al `select-vistas`. */
  onBackFromCatalogoConfigure: () => void;
  /** Dispara la generación del catálogo con las combinaciones que
   *  quedaron activas después del review (post-X de descartes). */
  onGenerateCatalog: (cards: CatalogoCard[]) => void;
  /** Marca activa — para el sheet de productos secundarios del catálogo
   *  (asociar producto nuevo subido). */
  brandId: string | null;
  /** Refresca el contexto global de userProducts tras subir uno nuevo
   *  desde el sheet de productos secundarios. */
  onProductsRefreshed: () => void;
  // ── Modo video (toggle "Imagen / Video" en el header) ──────────────
  /** Tab seleccionado en el header. Cuando es 'video' y el preset es
   *  'template', el ConfigurePanel agrega el botón "Configurar video"
   *  y la generación encadena imagen → prompt → video. */
  previewMediaTab: 'imagen' | 'video';
  /** Cambia el tab — usado por el toggle desktop dentro del preview
   *  (el mobile lo controla via mobileHeaderCenter). */
  onPreviewMediaTabChange: (tab: 'imagen' | 'video') => void;
  /** Segundos por escena para el video. Se usa al calcular el costo y
   *  al armar el payload de generación. */
  videoDuration: number;
  /** Si true, se genera multi-shot (Kling vía atlas /v1/videos/from-image,
   *  ADR-582), que cuesta el doble de créditos. */
  videoMultiScene: boolean;
  /** Costo en créditos del VIDEO por cada imagen generada (duración *
   *  multiplicador). El total final = creditCost base × cantidad imágenes
   *  + videoCreditCostPerImage × cantidad imágenes. */
  videoCreditCostPerImage: number;
  /** Abre el sheet de configuración de video. */
  onOpenVideoConfig: () => void;
  // ── Modo 'anuncio' ──────────────────────────────────────────────────
  /** State + handlers de los 3 campos textuales del modo Anuncio. Cada
   *  uno es `{ mode: 'auto'|'custom'|'hidden', value: string }`. */
  anuncioTitulo?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioTituloChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioDescripcion?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioDescripcionChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioCta?: import('../panels/AnuncioPanel').AnuncioFieldState;
  onAnuncioCtaChange?: (
    next: import('../panels/AnuncioPanel').AnuncioFieldState,
  ) => void;
  anuncioColors?: string[];
  onAnuncioColorsChange?: (next: string[]) => void;
  /** Paleta de la marca activa (`brand_profiles.colors`) — default del
   *  color picker. */
  anuncioBrandColors?: string[];
}
export const PresetDetailSheetModelo: React.FC<PresetDetailSheetModeloProps> = ({
  preset,
  animated,
  migas,
  step,
  configuracionRestaurada = false,
  previewList,
  rubro,
  rubroMarca,
  selectedProducts,
  imagenBase,
  modelSelection,
  fondoVal,
  formatoVal,
  otraConfig,
  sidebarWidth,
  onClose,
  onUtilizarPreset,
  onUtilizarMultiple,
  multiPresetIds,
  onShowProducts,
  onAddProducts,
  onConfirmProducts,
  onProductosEnVivo,
  onBackToChoose,
  onClearProducts,
  onStartUpload,
  onStartNewProduct,
  onSkipProduct,
  sinProducto = false,
  dialogoLoading = false,
  dialogoError = null,
  onBulkUpload,
  userPlan = null,
  onAddMoreFiles,
  onRemovePendingFile,
  onCancelUpload,
  onConfirmUpload,
  pendingProductFiles,
  pendingProductName,
  onPendingNameChange,
  uploadingProduct,
  uploadError,
  bulkUploadingProducts = false,
  onModelChange,
  columnaConfiguracion,
  onCambiarModeloYPose,
  cederZIndex = false,
  modelSelectionsSerie = [],
  onToggleModelSerie,
  variosModelos = false,
  onVariosModelosChange,
  modelSelectionsExtra,
  onModelChangeExtras,
  multiModelEnabled,
  onMultiModelEnabledChange,
  onOpenModelPicker,
  onOpenModelSheet,
  onOpenModelSheetForMultiSlot,
  multiProductsPerSlot,
  onTapSlotProducts,
  onSlotProductsChange,
  onOpenFondoSheet,
  onFondoChange,
  onFormatoChange,
  onOpenFormatPicker,
  onOtraChange,
  ganchoId,
  onGanchoIdChange,
  voiceGuion,
  onVoiceGuionChange,
  onVideoDurationChange,
  calidad,
  onCalidadChange: setCalidad,
  onGenerate,
  onGenerateProduction,
  autoImageCount,
  onAutoImageCountChange,
  autoImageCountMax,
  userModels,
  userAuthId,
  userCompanyId,
  onModelCreated,
  allPoses,
  customPoses,
  onUploadCustomPose,
  uploadingCustomPose,
  onDeleteCustomPose,
  poseIds,
  onPoseIdsChange,
  vistasProducto = [],
  vistaIds = [],
  onVistaIdsChange,
  multiPoseEnabled,
  onMultiPoseEnabledChange,
  generacionEnCurso = false,
  onOpenPosePicker,
  onSubirPose,
  bibliotecaFondos,
  fondosGuardados,
  fondoSelection,
  onFondoSelectionChange,
  onOpenFondoPicker,
  fondoView,
  onFondoViewChange,
  onFondoSaved,
  lockSlotCount = false,
  catalogoVistas,
  onCatalogoVistasChange,
  onConfirmCatalogoVistas,
  onBackFromCatalogoConfigure,
  onGenerateCatalog,
  brandId,
  onProductsRefreshed,
  previewMediaTab,
  onPreviewMediaTabChange,
  videoDuration,
  videoMultiScene,
  videoCreditCostPerImage,
  onOpenVideoConfig,
  anuncioTitulo,
  onAnuncioTituloChange,
  anuncioDescripcion,
  onAnuncioDescripcionChange,
  anuncioCta,
  onAnuncioCtaChange,
  anuncioColors,
  onAnuncioColorsChange,
  anuncioBrandColors,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  // Helpers de kind:
  //  • isDesignFlow agrupa los kinds que comparten layout del sheet
  //    (sin preview, sin imagen full-bleed, image scaled object-contain
  //    en choose-product/upload-product, configure mobile full-screen y
  //    sticky Generate). Incluye design-modelo, design-auto y anuncio.
  //  • isFullScreenConfigure identifica los kinds cuyo step `configure`
  //    ocupa TODO el panel en DESKTOP (aside full-width transparente, sin
  //    imagen a la izquierda, scroll de ventana y Generar flotante en el
  //    header). Aplica a 'design-modelo' (muchas opciones: Modelo + Pose +
  //    Fondo + Formato), a 'anuncio' (productos + 3 textos + colores +
  //    formato) y a 'video-influencer' (Gancho + Voz y guion + opciones,
  //    ADR-533). 'design-auto' NO — tiene pocas opciones (solo Formato +
  //    Instrucción) y se mantiene en el aside lateral derecho. Esto solo
  //    afecta el layout desktop; mobile usa `fullScreenConfigureMobile`.
  const isDesignFlow =
    esDesignModelo(preset.kind) ||
    preset.kind === 'design-auto' ||
    preset.kind === 'fotos-producto' ||
    preset.kind === 'anuncio';
  const isFullScreenConfigure =
    esDesignModelo(preset.kind) ||
    // "Fotos de producto" tiene 4 secciones (producto + vistas + fondo +
    // opciones): en el aside lateral la grilla de vistas queda impracticable.
    preset.kind === 'fotos-producto' ||
    preset.kind === 'anuncio' ||
    preset.kind === 'video-influencer';

  /**
   * Modos con el rediseño de la pantalla "Elegí el producto", en DESKTOP.
   *
   * Mantiene las dos columnas de siempre (muestra a la izquierda, productos a la
   * derecha) y cambia lo de adentro: la muestra va más grande con una bajada corta, el
   * panel de productos ocupa el 35% del ancho, pierde el bloque de "Subir nuevo
   * producto" de arriba —el alta pasó a ser la primera tarjeta de la grilla, y abre en un
   * drawer— y las tarjetas van a dos columnas, sin marco y con el nombre encima de la
   * foto.
   *
   * Empezó en `catalogo-ia` y se replicó a `design-modelo`, a `design-auto`
   * (ADR-2026-08-21-automatico-con-el-diseno-nuevo) y a `video-influencer`
   * (ADR-2026-08-21-video-con-el-diseno-nuevo). A propósito NO alcanza a muebles /
   * infografía / anuncio, que comparten este render pero no el pedido.
   */
  const modoConDisenoNuevo =
    preset.kind === 'catalogo-ia' ||
    esDesignModelo(preset.kind) ||
    preset.kind === 'design-auto' ||
    preset.kind === 'video-influencer';

  /**
   * UNA sola pantalla, con la configuración entera en la columna de la izquierda y los
   * productos en un panel lateral — ADR-2026-08-21-modelo-v2-una-sola-columna.
   *
   * El modo no tiene dos pasos (elegir producto → Siguiente → configurar): muestra todo
   * junto, con el "Generar" disponible desde el principio. La grilla de productos no es
   * una columna fija: se abre sobre el área de la derecha cuando hace falta.
   *
   * Hoy es siempre true —este shell lo monta sólo el modo Modelo—, pero sigue siendo un
   * gate por `kind` y no una constante: es lo que marca, línea por línea, qué partes del
   * JSX heredado de `PresetDetailSheet` son de esta pantalla y cuáles quedaron del shell
   * de dos pasos.
   */
  const unaColumna = esDesignModelo(preset.kind);

  /**
   * PASO 1 del modo: la grilla de productos ocupa la columna —
   * ADR-2026-09-08-el-modo-modelo-elige-los-productos-en-una-pantalla-propia.
   *
   * Antes el modo abría directo en la configuración (modelo, pose, fondo…) y los
   * productos —lo primero que hay que elegir— vivían escondidos detrás de una card que
   * abría el panel lateral. Ahora se entra por la grilla desplegada, como en Catálogo y
   * Fotos de producto, y recién con los productos elegidos aparece la configuración.
   *
   * Arranca en `false` si ya hay productos, y también si la pantalla se está
   * RESTAURANDO —volver desde un resultado, "Seguir generando"—: ahí el paso 1 ya está
   * hecho. El paso no puede mirar sólo los productos porque el restore los resuelve en
   * OTRO efecto, esperando a que cargue el catálogo: al montarse el sheet la lista
   * todavía está vacía, y el usuario que volvía de generar caía en la grilla.
   *
   * Hasta ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar eso lo tapaba
   * el bug: el restore abría el modo en `configure`, donde este paso no existe. Ahora el
   * restore respeta el paso del modo (`choose-product`) y quien avisa que la
   * configuración viene puesta es `configuracionRestaurada`.
   *
   * En la configuración los productos se siguen cambiando desde su card —el panel
   * lateral de siempre—, así que este paso no vuelve a aparecer.
   *
   * Sólo desktop: en mobile la columna no existe y el modo ya abre su pantalla de
   * productos a pantalla completa desde la card del pie.
   */
  const [eligiendoProductos, setEligiendoProductos] = useState(
    () =>
      esDesignModelo(preset.kind) &&
      step === 'choose-product' &&
      selectedProducts.length === 0 &&
      !configuracionRestaurada,
  );

  /**
   * "Siguiente" del paso 1: guarda los productos y abre la configuración.
   *
   * NO usa `onConfirmProducts` —el confirm del shell de dos pasos— porque ése mueve
   * `panelStep` a `configure`, y este modo vive entero en `choose-product`: moverlo
   * cambiaría el layout de la pantalla entera. `onProductosEnVivo` deja los productos
   * arriba sin tocar el paso, que es justo lo que hace falta.
   */
  const confirmarProductosDelPaso1 = (products: MockProductV2[]) => {
    (onProductosEnVivo ?? onConfirmProducts)(products);
    setEligiendoProductos(false);
  };

  /**
   * Mobile: el pie de la pantalla es `BarraInferiorMobile` y va EN EL FLUJO, como hermana
   * del scroller, no encima como la barra de navegación del shell.
   *
   * La distinción decide dos cosas del scroller: que no reserve el hueco de una barra que
   * no lo tapa, y que baje su contenido al pie con `mt-auto`
   * (ADR-2026-09-04-modelo-mobile-la-card-de-productos-baja-al-pie). Es exactamente la
   * condición con la que se dibuja la barra más abajo.
   */
  const barraEnFlujo = unaColumna && step === 'choose-product';

  /**
   * Qué se está eligiendo en el panel lateral, o `null` si está cerrado.
   *
   * Los tres pickers de la columna —productos, modelo y pose— se abren en el MISMO
   * panel: mismo lugar, misma forma, y lo único que cambia es la grilla de adentro
   * (ADR-2026-08-21-modelo-v2-un-solo-panel-lateral). Modelo y Pose ya no abren los
   * sheets de `InterfazV2`, que se montaban a pantalla completa y tapaban la columna
   * que el usuario acababa de mirar para decidir.
   */
  const [panelLateral, setPanelLateral] = useState<
    null | 'productos' | 'modelo' | 'pose' | 'fondo' | 'formato' | 'productos-modelo' | 'moodboard'
  >(null);
  /**
   * El moodboard elegido en su card (ADR-2026-10-18-moodboard-en-el-modo-modelo). SÓLO SE
   * VE: todavía no viaja a la generación ni lo ve el agente. Cuando se conecte, sube a
   * `InterfazV2` junto a `fondoSelection` —el mismo setter para el panel y para el
   * controlador— y entra a `camposDeModo` + `useControladorDelModo` + las skills.
   */
  const [moodboard, setMoodboard] = useState<SeleccionDeMoodboard | null>(null);
  // Sólo dev testers: la card todavía no cambia la imagen, y un usuario común vería un
  // control que no hace nada (ADR-2026-10-19-estilo-en-modelo-solo-dev-testers).
  const { isDevTester } = useUserGroup();
  const veMoodboards = rubroVeMoodboards(rubroMarca) && isDevTester === true;

  /**
   * Qué modelo del grupo está editando el panel de productos, cuando se abrió desde
   * "Qué lleva cada modelo" — ADR-2026-08-25-que-lleva-cada-modelo-en-el-panel.
   *
   * Es el índice en `modelosGrupo`, que es el mismo orden que `modelo_config`: el
   * payload le asigna los productos a `modelo_config[idx]`, así que si acá se
   * corriera uno, el buzo se lo pondría el modelo de al lado.
   */
  const [slotProductosLateral, setSlotProductosLateral] = useState<number | null>(
    null,
  );

  /**
   * Mobile: ¿está abierta la SEGUNDA pantalla, la de Modelo y Pose?
   *
   * En un teléfono el modo se recorre en dos pantallas —primero el producto, después con
   * qué se lo genera— y no en una sola columna como en desktop: acá no hay lugar para
   * mostrarlo todo junto (ADR-2026-08-24-modelo-v2-mobile-segunda-pantalla).
   *
   * Estado LOCAL y no `panelStep`: mover el step real arrastraría el layout de desktop,
   * el "atrás" y el resto de lo que ese paso decide, y v2 se apoya justamente en que su
   * step no cambia nunca.
   *
   * Arranca ABIERTA cuando el modo se reabre con la configuración puesta —el "Atrás"
   * desde los resultados, "Seguir generando"— porque ésta es la pantalla de la que se
   * venía: es donde están el Modelo, la Pose y el Generar
   * (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar). Aterrizar en el
   * paso 1 sería mandar a elegir de nuevo los productos que ya están elegidos. El paso 1
   * sigue a un toque, con el back de arriba.
   *
   * La pregunta por el ancho es porque esta pantalla existe sólo en mobile: en desktop
   * el modo entra en una columna y esto no se dibuja (`md:hidden`).
   */
  const [mobilePaso2, setMobilePaso2] = useState(
    () =>
      configuracionRestaurada &&
      esDesignModelo(preset.kind) &&
      typeof window !== 'undefined' &&
      !window.matchMedia('(min-width: 768px)').matches,
  );

  /**
   * La segunda pantalla se cierra cuando la generación ARRANCÓ, no al tocar Generar —
   * ADR-2026-09-04-el-generar-de-mobile-espera-a-que-la-carga-este-arriba.
   *
   * Para entonces la pantalla de carga (`z-[100000]`) ya la tapa entera, así que el
   * cierre no se ve; lo que queda atrás cuando esa carga se va es la pantalla de
   * resultados, que es lo que corresponde.
   */
  useEffect(() => {
    if (!generacionEnCurso) return;
    // …y no en el mismo instante en que se prende: la pantalla de carga ENTRA
    // deslizándose desde la derecha (300ms, `LoadingAnimation`), y cerrar esta pantalla
    // apenas empieza ese viaje deja ver el paso 1 por detrás mientras dura —el
    // parpadeo que se reportó—. Se espera a que la carga esté puesta.
    //
    // Un timer y no un `transitionend`: la animación es de OTRO componente, montado en
    // otra rama del árbol, y engancharse a su transición desde acá sería un acople
    // bastante peor que este número.
    const t = window.setTimeout(() => setMobilePaso2(false), 350);
    return () => window.clearTimeout(t);
  }, [generacionEnCurso]);

  /**
   * Salir del selector de productos QUEDA PENDIENTE hasta que el paso 2 terminó de
   * entrar — ADR-2026-09-04-el-confirmar-de-mobile-no-muestra-el-paso-1-al-pasar.
   *
   * El "Confirmar" hacía las dos cosas juntas: cerrar el selector (`onBackToChoose`) y
   * abrir el paso 2. El paso 2 entra deslizándose desde la derecha (280ms), así que
   * durante ese viaje el selector ya no estaba y por detrás se veía el paso 1 — el
   * parpadeo que se reportó. Dejándolo montado hasta que la animación termina, lo que
   * hay atrás es la pantalla de la que se viene, que es lo que corresponde: el paso 2
   * la tapa entera (z-36 contra z-35).
   */
  const salirDelPickerRef = useRef<(() => void) | null>(null);
  const cerrarPickerPendiente = () => {
    const salir = salirDelPickerRef.current;
    salirDelPickerRef.current = null;
    salir?.();
  };

  /**
   * Mientras la segunda pantalla de mobile está abierta, el back del chrome vuelve al
   * PASO 1 del modo en vez de hacer lo que hacía la pantalla de abajo (que era cerrar el
   * sheet).
   *
   * La pantalla tenía su propio "Volver" al lado del título y el chrome flotante ya dibuja
   * el suyo justo encima: dos flechas, una arriba de la otra, para el mismo movimiento.
   * Se saca la de adentro y la de arriba toma el trabajo
   * (ADR-2026-09-04-modelo-mobile-un-solo-back-en-el-paso-2).
   *
   * `prevBackRef` guarda el override de la pantalla de abajo y lo RESTAURA al cerrar, en
   * vez de dejarlo en `null`: el effect de `InterfazV2` que lo puso no se vuelve a
   * ejecutar —su `panelStep` no cambió—, así que el paso 1 quedaría sin botón de volver.
   * Es el mismo patrón que usa `AnuncioFromImageSheet`.
   */
  const { setMobileBackOverride, mobileBackOverride } = useSidebar();
  const prevBackRef = useRef<(() => void) | null>(null);
  const miBackRef = useRef<(() => void) | null>(null);

  /**
   * Se RE-APLICA cada vez que el override cambia y no es el nuestro, no una sola vez al
   * abrir: `InterfazV2` tiene su propio effect sobre el mismo override, con `panelStep`
   * entre las dependencias, y el "Siguiente" que trae a esta pantalla llama a
   * `onBackToChoose()` —que mueve `panelStep`— justo antes de abrirla. Los dos effects
   * corren en el mismo commit y el del padre corre DESPUÉS, así que ponerlo una vez al
   * montar dejaba ganando al padre y el back se llevaba al usuario fuera del modo.
   *
   * La guarda por identidad es lo que evita el ciclo: si el override ya es el nuestro, no
   * se toca nada.
   */
  useEffect(() => {
    if (!mobilePaso2) return;
    if (mobileBackOverride === miBackRef.current) return;
    prevBackRef.current = mobileBackOverride;
    const mio = () => setMobilePaso2(false);
    miBackRef.current = mio;
    // Doble flecha: el setter de `useState` trata una función como updater, así que para
    // GUARDAR un handler hay que devolverlo desde el updater.
    setMobileBackOverride(() => mio);
  }, [mobilePaso2, mobileBackOverride, setMobileBackOverride]);

  /** Al cerrar la pantalla, el override vuelve a ser el del paso de abajo. */
  useEffect(() => {
    if (!mobilePaso2) return;
    return () => {
      setMobileBackOverride(() => prevBackRef.current);
      miBackRef.current = null;
    };
  }, [mobilePaso2, setMobileBackOverride]);

  /**
   * Vista del picker de modelos DENTRO del panel lateral (lista / crear / subir).
   *
   * Local: la de `InterfazV2` (`modelPickerInitialView`) es la del sheet full-screen,
   * que en esta variante ya no se abre desde la columna.
   */
  const [modelViewLateral, setModelViewLateral] =
    useState<ModelPickerView>('list');

  /**
   * El área de las dos columnas. La usa el panel lateral para saber qué clicks son "por
   * fuera": los de acá adentro lo cierran, los de los sheets que se abren desde él —que
   * viven en portales colgados del `body`— no.
   */
  const areaDosColumnasRef = useRef<HTMLDivElement>(null);

  /**
   * Dónde arranca el panel lateral flotante: pegado al borde derecho de la columna de
   * configuración, que el panel NO tapa.
   *
   * Se MIDE en vez de escribirse. Estaba escrito como `calc(35% + 2rem)`, que valía
   * cuando el panel medía 35% de la fila; desde que pasó a medir lo suyo
   * (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660) el flotante arrancaba
   * donde habría terminado un panel que ya no existe y se le montaba encima —tapaba las
   * filas de Modelo, Pose y Fondo, que es justo lo que el panel lateral existe para
   * dejar a la vista—. Con la medición sigue al panel mida lo que mida, incluso ahora
   * que su ancho es elástico y cambia con la ventana
   * (ADR-2026-09-02-el-panel-de-configuracion-achica-con-la-ventana). Es la misma
   * solución que ya usaba el Catálogo en `components/PresetDetailSheet`.
   */
  const panelConfigRef = useRef<HTMLElement | null>(null);
  const [offsetPanelLateral, setOffsetPanelLateral] = useState<number | null>(
    null,
  );
  useLayoutEffect(() => {
    const contenedor = areaDosColumnasRef.current;
    const panel = panelConfigRef.current;
    if (!contenedor || !panel) return;
    const medir = () => {
      const c = contenedor.getBoundingClientRect();
      const p = panel.getBoundingClientRect();
      // + el `gap-6` de la fila: el flotante arranca donde arrancaría la columna de al
      // lado, no pegado al borde del panel.
      setOffsetPanelLateral(Math.round(p.right - c.left + 24));
    };
    medir();
    // El ancho del panel cambia con el PASO y con la ventana, y el paso no pasa por un
    // resize del contenedor: se observan los dos elementos para cubrir las dos causas.
    const ro = new ResizeObserver(medir);
    ro.observe(contenedor);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [step, panelLateral]);

  /**
   * v2 entra con el modelo YA puesto en "Automático".
   *
   * El modo arrancaba sin modelo (`modelSelection` en `null`), y eso dejaba la pantalla
   * en un estado en el que el Generar está apagado desde el arranque por algo que el
   * modo sabe resolver solo: sin modelo elegido, la IA elige uno. La card de Modelo ya
   * decía "Automático" con `null`, así que era además una selección que se veía hecha
   * pero no contaba para `modelosElegidos`.
   *
   * Se emite UNA vez por montaje, y **pisa lo que haya**. Esto último es a propósito:
   * `InterfazV2` tiene un efecto de defaults que, con la selección vacía, restaura el
   * modelo de la ÚLTIMA generación (`lastState?.modelo`). Con un guard de "sólo si está
   * vacío", ese efecto ganaba siempre y la pantalla abría con el último modelo usado en
   * vez de en Automático — que es justo el default que se pidió para esta variante.
   *
   * Una sola vez, no en cada render: si el usuario elige otro modelo, se respeta. Vuelve
   * a Automático recién la próxima vez que entre al modo.
   *
   * Vive acá y no en `InterfazV2` (donde está el estado) porque el default es de ESTA
   * variante: cambiarlo en el origen se lo llevaba puesto a todos los modos.
   * Ver ADR-2026-08-21-modelo-v2-arranca-en-automatico.
   */
  /**
   * ¿El usuario eligió un modelo A MANO?
   *
   * Mientras sea `false`, cualquier modelo que aparezca vino de otro lado —el efecto de
   * defaults de `InterfazV2`, que restaura el de la última generación— y esta variante lo
   * devuelve a "Automático".
   */
  const modeloElegidoPorElUsuario = useRef(false);
  /**
   * Abrir el picker de modelo cuenta como "el usuario eligió".
   *
   * En mobile el picker es el sheet de `InterfazV2`, no el panel lateral: la selección
   * vuelve por `onModelChange` desde AFUERA de este archivo, así que acá no hay ningún
   * tap que marcar. Sin esta marca, el efecto de abajo trataba a ese modelo como si lo
   * hubiera puesto el default de `InterfazV2` y lo devolvía a "Automático" en el mismo
   * commit — elegir un modelo en el teléfono no cambiaba nada
   * (ADR-2026-09-04-en-mobile-el-modelo-elegido-se-quedaba-en-automatico).
   *
   * Se marca al ABRIR y no al elegir porque abrir es lo último que este componente ve:
   * a partir de ahí, lo que llegue salió de una pantalla que el usuario tiene delante.
   * Si cierra sin elegir, lo único que se pierde es que el modo deje de forzar
   * "Automático" en ese montaje — y para eso ya fue a mirar los modelos.
   */
  const abrirPickerDeModelo = () => {
    modeloElegidoPorElUsuario.current = true;
    onOpenModelPicker();
  };
  useEffect(() => {
    if (!unaColumna || modeloElegidoPorElUsuario.current) return;
    if (modelSelection?.mode === 'auto') return;
    // Corre cada vez que algo externo cambia la selección, y no una sola vez al montar:
    // el default de `InterfazV2` se aplica TARDE —espera a que carguen `userModels` para
    // resolver el modelo guardado—, así que un efecto de un solo disparo perdía la
    // carrera y la pantalla abría con el último modelo usado en vez de en Automático.
    onModelChange({ mode: 'auto' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unaColumna, modelSelection]);

  /**
   * "Automático" con el diseño nuevo: UNA sola pantalla
   * (ADR-2026-08-21-automatico-con-el-diseno-nuevo).
   *
   * En este modo la escena entera la decide la IA, así que no hay Modelo ni Pose que
   * configurar: todo lo que se elige —Calidad, Fondo, Formato, Cantidad e Instrucción—
   * ya vive en la bandeja del panel de productos. Sin nada que poner en un paso 2, el
   * "Siguiente" desaparece: el CTA es Generar desde el principio y la columna de al lado
   * pasa de la muestra del modo a las tandas.
   */
  const autoUnSoloPaso =
    preset.kind === 'design-auto' && step === 'choose-product';

  /**
   * ...y además estamos en un paso que dibuja las dos columnas.
   *
   * El gate por step importa para `design-modelo`, que tiene el `configure` a pantalla
   * completa: sin él, el 35% de ancho del panel se le aplicaría también a esa pantalla,
   * que no es de elegir producto y usa todo el ancho a propósito.
   *
   * Sólo desktop: esta rama del render ya es la de desktop (mobile arma su propio
   * bottom sheet más arriba), así que no hace falta preguntar por el breakpoint.
   */
  const disenoElegirProducto =
    modoConDisenoNuevo &&
    (step === 'choose-product' ||
      step === 'pick-product' ||
      step === 'upload-product');

  /**
   * `configure` a DOS COLUMNAS: la configuración a la izquierda —donde hasta el paso
   * anterior estaba la imagen de muestra— y el panel de productos intacto a la derecha.
   *
   * Antes, tocar "Siguiente" en estos modos cambiaba la pantalla ENTERA: el panel de
   * productos desaparecía y la configuración se abría a todo el ancho. Con esto el paso
   * se lee como un avance sobre la misma pantalla —la mitad que no cambió sigue ahí, y
   * los productos elegidos quedan a la vista mientras se configura— en vez de como una
   * pantalla nueva.
   *
   * Es lo único que separa a estos modos del `configure` full-screen del resto; el
   * `configureFullScreen` de abajo es la condición vieja con esta excepción restada, y
   * es la que gobierna el layout de una sola columna.
   */
  const configureDosColumnas =
    // `design-modelo` y `video-influencer` (ADR-2026-08-21-video-con-el-diseno-nuevo),
    // y no todo `modoConDisenoNuevo`: el `configure` del Catálogo no es este panel sino
    // el review de cards a pantalla completa (`CatalogoCardsFullscreen`), que se monta
    // aparte y tapa esta rama entera; y `design-auto` directamente no tiene paso 2.
    (esDesignModelo(preset.kind) || preset.kind === 'video-influencer') &&
    step === 'configure';

  /** El paso 2 del modo Video, que reparte sus secciones entre las dos columnas. */
  const videoDosColumnas =
    preset.kind === 'video-influencer' && configureDosColumnas;
  const esVideoInfluencer = preset.kind === 'video-influencer';

  /**
   * La Duración, en los ajustes rápidos del panel de productos
   * (ADR-2026-08-21-video-con-el-diseno-nuevo). Dos formas, porque el modo tiene dos:
   *
   *   • guion **Automático** → la barra de siempre (`FillBarSliderV2`), editable.
   *   • guion **escrito o grabado** → un dato, no un control: ahí la duración la marca
   *     el guion (ADR-528), y un slider prometería mover algo que no se mueve. Con el
   *     guion todavía vacío no hay número que mostrar, así que no se dibuja nada.
   *
   * Hoy el default es 'texto' (ADR-674 sacó "Automático" de los modos elegibles), así
   * que el caso normal es el segundo.
   */
  const duracionEnAjustes = (() => {
    if (!esVideoInfluencer) return undefined;
    if (voiceGuion?.guionMode === 'auto' && onVideoDurationChange) {
      return (
        <FillBarSliderV2
          label="Duración"
          min={VIDEO_INFLUENCER_DURATION_MIN}
          max={
            isSinGancho(ganchoId)
              ? VIDEO_INFLUENCER_DURATION_MAX_SIN_GANCHO
              : VIDEO_INFLUENCER_DURATION_MAX
          }
          step={1}
          value={videoDuration}
          onChange={onVideoDurationChange}
          formatValue={(v) => `${v}s`}
        />
      );
    }
    const seg = voiceGuion ? getGuionDurationSec(voiceGuion) : null;
    if (seg == null) return undefined;
    return (
      <div className="rounded-xl bg-black/[0.035] px-3 py-2">
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#3C3C43]/45">
          Duración
        </span>
        <span className="mt-0.5 block text-[13.5px] font-medium text-brand-black/90">
          {isSinGancho(ganchoId) ? seg : seg + INFLUENCER_GUION_BASE_COST}s
          <span className="ml-1 font-normal text-[12px] text-[#3C3C43]/55">
            · la marca tu guion
          </span>
        </span>
      </div>
    );
  })();

  /**
   * El mismo layout de dos columnas, en el paso de VISTAS del Catálogo.
   *
   * El pedido fue que el Catálogo se comporte como "Imagen con modelo": que al avanzar,
   * el panel de productos siga ahí y lo que cambie sea la columna de al lado, donde se
   * eligen las poses. En el Catálogo ese paso no es `configure` —el `configure` es el
   * review de cards— sino `select-vistas`, que hasta ahora se abría como un overlay a
   * pantalla completa y se llevaba puesta la pantalla entera, productos incluidos.
   *
   * Sólo `catalogo-ia`: la V2 es el banco de pruebas de la pantalla vieja y tiene que
   * seguir mostrándola tal cual, y muebles usa el mismo step con otro catálogo de
   * vistas (3 fijas) que no pidió esto.
   */
  const catalogoVistasDosColumnas =
    preset.kind === 'catalogo-ia' && step === 'select-vistas';

  /** Cualquiera de los dos pasos que se dibujan como panel + columna que cambia. */
  const pasoDosColumnas = configureDosColumnas || catalogoVistasDosColumnas;

  /**
   * Pasos donde lo que se toca en el panel de productos vale YA, sin confirmar.
   *
   * En el paso 1 el "Siguiente" confirma la selección; a partir del paso 2 ese botón pasa
   * a ser "Generar", así que si el panel sigue ahí —y sigue: es la mitad de la pantalla—
   * agregar o sacar un producto no tendría dónde aplicarse y la tanda saldría con la
   * selección vieja: se cobraría una cosa y se generaría otra.
   */
  const seleccionEnVivo = autoUnSoloPaso || pasoDosColumnas;

  /**
   * Resumen del pie del CTA en "Automática": cuántas imágenes salen y de qué.
   *
   * Las tres frases responden la misma pregunta —qué voy a recibir si aprieto— con lo que
   * cambia en cada caso: sin productos, que falta elegir uno; con uno, que la escena no la
   * elegís vos (que es TODO el modo); con varios, que se combinan en una sola imagen y no
   * sale una por producto.
   */
  const resumenAutomatica = (imagenes: number, productos: number): string => {
    if (productos === 0) return 'Elegí un producto para generar.';
    const cuantas = `${imagenes} ${imagenes === 1 ? 'imagen' : 'imágenes'}`;
    if (productos === 1)
      return `${cuantas} del producto elegido — la escena la arma la IA`;
    return `${cuantas} con los ${productos} productos juntos, como un outfit`;
  };

  /** Modelos elegidos en la grilla: el principal + la serie. Puede ser 0 — la grilla deja
   *  deseleccionar todo, y ahí no hay nada que generar. */
  /**
   * El grupo de modelos en el orden del payload: el principal primero y los extras
   * después, que es cómo lo arma `modelo_config` (ver `handleToggleModelGrupal` en
   * `InterfazV2`). Lo usa la lista de "Qué lleva cada modelo", donde el índice de la
   * fila TIENE que ser el mismo que el del slot para que el picker le asigne los
   * productos al modelo correcto.
   */
  const modelosGrupo = React.useMemo(
    () =>
      [modelSelection, ...modelSelectionsExtra].filter(
        (m): m is ModelSelection => !!m,
      ),
    [modelSelection, modelSelectionsExtra],
  );

  const modelosElegidos =
    (modelSelection ? 1 : 0) + modelSelectionsSerie.length;

  /**
   * Qué falta para poder generar en el modo Video, con el id del bloque adonde mandar
   * el foco. Prioridad: gancho → modelo → voz → guion (la misma que el hint del
   * ConfigurePanel). Los ids existen SÓLO en el render desktop del panel (prop
   * `domIds`, para no duplicarlos con el mobile).
   *
   * Vive acá arriba —y no adentro del pill flotante, que fue donde nació— porque con el
   * diseño nuevo el Generar es el CTA del panel de productos y necesita lo mismo
   * (ADR-2026-08-21-video-con-el-diseno-nuevo).
   */
  const faltaEnInfluencer: { msg: string; targetId: string } | null =
    preset.kind !== 'video-influencer'
      ? null
      : !ganchoId
        ? {
            msg: 'Elegí un gancho para generar.',
            targetId: 'influencer-desktop-gancho-card',
          }
        : !modelSelection || modelSelection.mode === 'auto'
          ? {
              // En este modo no existe "Automático": modelo sí o sí.
              msg: `Elegí un ${palabra.singular} para generar.`,
              targetId: 'influencer-desktop-modelo-card',
            }
          : voiceGuion &&
              voiceGuion.guionMode !== 'auto' &&
              voiceGuion.vozId == null
            ? {
                msg: 'Elegí una voz para generar.',
                targetId: 'influencer-desktop-voz-row',
              }
            : voiceGuion && isVoiceGuionIncomplete(voiceGuion)
              ? {
                  msg:
                    voiceGuion.guionMode === 'audio'
                      ? 'Grabá tu guion para generar.'
                      : 'Escribí tu guion para generar.',
                  targetId: 'influencer-desktop-guion-block',
                }
              : null;

  /** Paso 2 de "Imagen con modelo" con una de las dos grillas vacía: no hay imagen
   *  posible, así que el CTA se apaga y en lugar del resumen de la tanda dice qué falta.
   *
   *  Sólo ese modo: Video comparte el layout de dos columnas pero no tiene grilla de
   *  poses, así que su `poseIds` vacío no significa que falte nada — lo que le falta lo
   *  dice `faltaEnInfluencer` (ADR-2026-08-21-video-con-el-diseno-nuevo). */
  const faltaModeloOPose =
    configureDosColumnas &&
    esDesignModelo(preset.kind) &&
    (modelosElegidos === 0 || poseIds.length === 0);

  /**
   * Las imágenes que saldrían de la selección actual: una por cada vista × producto
   * compatible.
   *
   * El Catálogo genera DESDE el paso de vistas, sin review en el medio, así que el costo
   * que se muestra y el payload que se manda se arman acá. Sale del mismo helper que usa
   * el review para que las dos entradas a la generación no puedan divergir.
   */
  const cardsDeLaTanda = React.useMemo(
    () =>
      catalogoVistasDosColumnas
        ? construirCardsDeCatalogo(catalogoVistas, selectedProducts)
        : [],
    [catalogoVistasDosColumnas, catalogoVistas, selectedProducts],
  );

  /**
   * Qué falta para poder generar en el paso de vistas del Catálogo, o null si está listo.
   *
   * El botón se APAGA mientras falte algo, en vez de dejarse apretar y sacudir la grilla:
   * el sacudón sirve cuando lo que falta está a la vista en la misma columna, y acá el
   * panel y las vistas son dos columnas distintas —con el panel comprimido, los productos
   * ni siquiera se ven—. Apagado, más una línea que dice qué falta, se entiende sin
   * probar.
   */
  const faltaEnCatalogo: string | null = !catalogoVistasDosColumnas
    ? null
    : selectedProducts.length === 0
      ? 'Elegí al menos un producto.'
      : catalogoVistas.length === 0
        ? 'Elegí al menos una vista.'
        : cardsDeLaTanda.length === 0
          ? // Hay productos y hay vistas, pero ninguna combinación es compatible (p. ej.
            // quedó el maniquí invisible elegido y el producto que quedó es un zapato).
            // Sin este caso el botón se apagaría sin decir por qué.
            'Las vistas elegidas no aplican a estos productos.'
          : null;

  const configureFullScreen =
    isFullScreenConfigure && step === 'configure' && !configureDosColumnas;
  /**
   * Estos modos se presentan como una SECCIÓN y no como un panel flotante —
   * ADR-2026-08-03-anuncio-se-ve-como-seccion, extendido a Imagen automática y
   * Imagen con modelo por ADR-2026-08-03-seccion-tambien-automatica-y-modelo, a
   * Catálogo por ADR-2026-08-03-seccion-tambien-catalogo y a Video con influencer por
   * ADR-2026-08-03-seccion-tambien-video-influencer.
   *
   * Sigue siendo el mismo panel `fixed` de todos los modos: lo que cambia es lo que
   * se ve. Sin fondo ni borde propios —el lienzo lo pone el shell— y con breadcrumb
   * + `<h1>` como `/plantillas`, en vez de un recuadro sin título.
   *
   * La lista va explícita y no atada a `isDesignFlow` (que describe el FLUJO: mismos
   * steps, misma preview chica): son dos cosas distintas y esto es cómo se ve la
   * pantalla. Catálogo lo confirma — entró por
   * ADR-2026-08-03-seccion-tambien-catalogo y NO es un `isDesignFlow`, así que
   * atarlas lo habría dejado afuera. El día que un modo nuevo entre al flujo tampoco
   * tiene por qué heredar la presentación sin que alguien lo decida.
   *
   * Lo que sigue con el marco flotante en esta pantalla: los presets de plantillas
   * (`template` / `design`). El asistente no está en la lista porque no pasa por acá —
   * tiene pantalla propia (`AsistenteMode`).
   */
  const esSeccion =
    preset.kind === 'anuncio' ||
    preset.kind === 'design-auto' ||
    esDesignModelo(preset.kind) ||
    esCatalogoIA(preset.kind) ||
    // Infografía entró con ADR-2026-08-05-infografia-se-ve-como-seccion: quedó afuera
    // porque cuando se escribió esta lista el modo no lo veía nadie (flag global en
    // `false`), no porque su pantalla fuera distinta. Con el modo visible para su rubro
    // era la única que abría en un recuadro flotante y sin título.
    preset.kind === 'infografia' ||
    // Catálogo de muebles, por lo mismo y con una razón de más: en Hogar y Decoración
    // ES el Catálogo —reemplaza a `catalogo-ia`, que ahí se oculta, y se llama igual
    // (ADR-2026-08-05-el-catalogo-de-muebles-se-llama-catalogo)—. Dejarlo con el marco
    // flotante hacía que "Catálogo" se viera distinto según el rubro de la marca.
    preset.kind === 'catalogo-muebles' ||
    preset.kind === 'video-influencer';
  /**
   * Título de la pantalla en la presentación de sección — el nombre CORTO del modo,
   * el mismo que la tira de la home, la sidebar, el sheet de Generar en mobile y la
   * hoja del breadcrumb (ADR-2026-08-03-un-solo-nombre-por-modo).
   *
   * No es `preset.title`: en Anuncio los dos dicen "Anuncio" y no se notaba, pero en
   * estos dos el `title` es la frase de la card del carrusel ("Generar imagen
   * automáticamente") y el `<h1>` habría contradicho al breadcrumb que tiene tres
   * líneas más arriba, en la misma pantalla. `preset.title` queda de respaldo para
   * los kinds que no son un modo.
   */
  /**
   * El paso en el que la pantalla scrollea por dentro — el único en el que el
   * contenido puede pasar el alto de la ventana (ver el `overflow-y-auto` de más
   * abajo, en el wrapper que envuelve al encabezado y al contenido).
   *
   * Sale como constante porque de él dependen tres cosas que tienen que estar de
   * acuerdo: quién es el scroller, que el encabezado viva adentro de él
   * (ADR-2026-08-04-el-breadcrumb-no-se-pega) y quién se queda con los 72px de aire
   * de arriba (ADR-2026-08-04-el-encabezado-pegado-se-lleva-el-aire).
   */
  // A dos columnas NO scrollea el wrapper: cada columna maneja su propio scroll, igual
  // que en los pasos de elegir producto.
  const contenidoScrollea = configureFullScreen;

  /**
   * La columna de resultados se sale de su caja y llega al marco —
   * ADR-2026-09-06-la-columna-de-resultados-llega-al-marco.
   *
   * Sólo cuando la columna sostiene las TANDAS y sólo en los modos del rediseño: los
   * márgenes negativos de `COLUMNA_RESULTADOS_AL_BORDE` cancelan paddings CONCRETOS
   * —el `lg:!pr-[52px]` de `FILA_MODO_REDISENO` y el `lg:pt-[52px]` que el panel pone
   * en la presentación de sección—, así que fuera de esa combinación no hay nada que
   * cancelar y la columna se pasaría de largo. Importa porque una tanda vieja sin
   * `modo` guardado se cuela en cualquier modo (`tandasDelModo` la deja pasar), y con
   * la condición atada sólo a `columnaConfiguracion` eso la sacaba de la pantalla.
   */
  const columnaResultadosAlBorde =
    !!columnaConfiguracion && esSeccion && modoConDisenoNuevo;
  /**
   * El título de la pantalla es el ÚLTIMO nivel del breadcrumb —
   * ADR-2026-08-04-el-titulo-es-la-hoja-del-breadcrumb.
   *
   * Era siempre el nombre del modo, así que en `configure` el breadcrumb decía
   * "Inicio / Modelo / Configurar" y el `<h1>` de abajo decía "Modelo": el rastro
   * marcaba un nivel que el título contradecía. Es el mismo criterio que ya cumplían
   * las otras secciones del shell —`/plantillas` titula "Plantillas", que es su hoja—,
   * sólo que acá la hoja cambia con el paso.
   *
   * El fallback cubre a los presets sin breadcrumb (una plantilla, un modo que no está
   * en `MODE_KIND_TO_PATH`): ahí `migas` viene `undefined` y el nombre del modo sigue
   * siendo lo mejor que se puede decir.
   */
  const tituloSeccion =
    migas?.[migas.length - 1]?.label ??
    nombreCortoModo((preset.kind && MODE_KIND_TO_PATH[preset.kind]) || '', preset.title);
  // Configure MOBILE a pantalla completa (sin hero, panel = 100dvh, Generar
  // sticky): los design-flow desde siempre + video-influencer (ADR-533).
  const fullScreenConfigureMobile =
    isDesignFlow || preset.kind === 'video-influencer';
  // Preview del preset como imagen CHICA y centrada (object-contain con
  // padding), no full-bleed. Lo usan los modos de diseño/catálogo y también
  // 'video-influencer', cuyo asset del preview es la miniatura compuesta
  // (producto → influencer): a tamaño hero se veía desproporcionada frente a
  // los otros modos. Ver ADR-507.


  /** Drawer de alta de producto — lo abre la tarjeta placeholder de la grilla. */
  const [subirProductoAbierto, setSubirProductoAbierto] = useState(false);
  // El pop-up de subir uno o varios productos, que abren las dos tarjetas de subida de la
  // grilla (ADR-2026-10-14-subir-productos-en-un-pop-up-desde-modelo).
  const [subirEnVentana, setSubirEnVentana] = useState<ModoDeSubida | null>(null);

  /**
   * ¿El panel lateral está mostrando el alta de un producto?
   *
   * En esta pantalla el alta NO abre un drawer encima: pasa a ocupar el panel lateral que
   * ya estaba abierto, igual que "crear" y "subir" en el picker de modelos
   * (ADR-2026-08-25-subir-producto-en-el-mismo-panel). Un drawer flotante sobre el panel
   * eran dos capas de lo mismo, una tapando a la otra.
   */
  const [subiendoProductoEnPanel, setSubiendoProductoEnPanel] = useState(false);


  /**
   * ¿La preview del paso 2 está comprimida?
   *
   * Arranca comprimida cada vez que se entra a `configure` —ahí el panel es un
   * recordatorio, no la herramienta— y el control del título la abre para cambiar la
   * selección sin volver al paso anterior. Se resetea al cambiar de paso: si quedara
   * abierta, volver a entrar mostraría el panel ancho y la configuración apretada.
   */
  const [previewComprimida, setPreviewComprimida] = useState(true);
  useEffect(() => {
    setPreviewComprimida(true);
  }, [step]);
  /** Paso 2 y sin descomprimir: el panel va angosto y en modo preview. */
  const panelComprimido = pasoDosColumnas && previewComprimida;

  const smallCenteredPreview =
    isDesignFlow ||
    esCatalogoIA(preset.kind) ||
    preset.kind === 'catalogo-muebles' ||
    preset.kind === 'infografia' ||
    preset.kind === 'video-influencer';
  /**
   * La cabecera (breadcrumb + `<h1>`) baja a la COLUMNA de la imagen —
   * ADR-2026-08-05-la-cabecera-de-seccion-baja-a-su-columna.
   *
   * Sólo en los pasos que dibujan las dos columnas (imagen a la izquierda, panel de
   * productos a la derecha). En `preview` el layout es un carrusel a todo lo ancho y en
   * el `configure` full-screen la imagen no está, así que ahí la cabecera se queda
   * arriba, cruzando la pantalla, que es lo que corresponde cuando abajo no hay dos
   * columnas que respetar.
   */
  const cabeceraEnColumna =
    esSeccion &&
    step !== 'preview' &&
    smallCenteredPreview &&
    !configureFullScreen &&
    // Con las columnas invertidas la cabecera queda en la de la DERECHA, y un breadcrumb
    // que arranca a mitad de pantalla no se lee como el rastro de dónde estás. Vuelve
    // arriba, cruzando las dos columnas, que es donde está en el resto del shell.
    !modoConDisenoNuevo;
  // Hero collage "producto → resultados" para catálogo-muebles / infografía
  // (reemplaza al `<img>` en el preview chico). Undefined = kind sin collage.
  // El rubro puede remapear qué collage se muestra (ver `HERO_COLLAGE_POR_RUBRO`).
  const kindDelHero =
    (rubroMarca && preset.kind
      ? HERO_COLLAGE_POR_RUBRO[rubroMarca]?.[preset.kind]
      : undefined) ?? preset.kind;
  const heroCollage = kindDelHero ? HERO_COLLAGE[kindDelHero] : undefined;
  // Modos que generan a partir de UN solo producto: el video con influencer
  // (ADR-563) y las fotos de producto (el modo son las vistas de un producto,
  // no un outfit combinado). El picker va en `singleSelect`.
  const singleProductMode =
    preset.kind === 'video-influencer' || preset.kind === 'fotos-producto';

  // Costo en créditos/tokens del preset — se muestra en el pill al
  // costado del texto en todos los botones "Generar" (el del
  // ConfigurePanel y el flotante de design-modelo desktop).
  //   • unidad 'segundo' → ratePer × durationSeconds (video).
  //   • cualquier otra unidad → ratePer × 1.
  //   • design-auto → multiplicamos también por autoImageCount
  //     (1-6 imágenes que pide el usuario).
  //   • design-modelo con multi-modelo → la generación produce 1
  //     imagen con todos los modelos juntos, no multiplicamos.
  //   • design-modelo con SERIE de modelos (grilla multi-select) → sí:
  //     cada modelo es una generación aparte (× poses).
  //   • multi-preset (templates) → multiplicamos por la cantidad
  //     seleccionada.
  // ¿Está activo el modo video? Solo aplica al kind 'template' — los
  // otros kinds tienen su propio flow y no respetan este toggle.
  const isVideoModeActive =
    (previewMediaTab === 'video' && preset.kind === 'template') ||
    // El modo "Video con influencer" siempre está en modo video (reusa el
    // layout de plantilla-video).
    preset.kind === 'video-influencer';
  /**
   * La tanda de v2: cuántas imágenes salen de verdad.
   *
   * Es UNA sola cuenta para el número del botón, el costo y el `expectedCount` del poll
   * (que la rehace con el mismo helper en `InterfazV2`). Antes eran tres cálculos
   * distintos y decían tres cosas: con 3 modelos en composición grupal y el stepper en 3,
   * el botón decía "Generar 1 imagen" y el pill cobraba 3
   * (ADR-2026-08-28-modelo-v2-la-cantidad-manda).
   */
  const tandaV2 = resolveTandaModelo({
    rawCount: autoImageCount,
    poseCount: poseIds.length,
    // De DÓNDE salen los modelos depende del flujo, y los dos estados son
    // excluyentes (`handleToggleModelGrupal` limpia la serie al armar el grupo):
    // en la composición grupal son `modelSelection` + los extras —lo mismo que
    // dibuja "Qué lleva cada modelo"—, y fuera de ella la serie, que fan-outea un
    // request por modelo. Antes se pasaba SIEMPRE la serie, que en el grupal está
    // vacía: la tanda creía que había un solo modelo justo en el caso donde hay
    // tres (ADR-2026-09-04-los-modelos-de-la-foto-grupal-se-cobran).
    modelCount: multiModelEnabled
      ? Math.max(1, modelosGrupo.length)
      : 1 + modelSelectionsSerie.length,
    grupal: multiModelEnabled,
    max: autoImageCountMax,
  });
  const creditCost = (() => {
    // ── Video con influencer (ADR-671) ──────────────────────────────────
    // Regla ÚNICA del modo: 1 crédito por segundo de video FINAL. Con gancho
    // el video final suma los 4s del clip de apertura; sin gancho arranca
    // directo con el principal y esos 4 créditos no se cobran.
    //   • Guion escrito/grabado (ADR-547): la duración la marca el guion
    //     (duración del audio o estimación de lectura, tope 14s) y el gancho
    //     suma sus 4s aparte (`INFLUENCER_GUION_BASE_COST`).
    //   • Automático: la barra de Duración YA es el total (con gancho el
    //     backend recorta el principal a barra-4), así que el costo es la
    //     barra pelada. Antes de ADR-671 el automático cobraba además 1
    //     crédito fijo por el frame — se sacó para que la regla sea una sola.
    // Si el guion todavía no está (texto vacío / sin audio) cae al valor de
    // la barra; el Generar está bloqueado igual, así que es solo cosmético.
    if (preset.kind === 'video-influencer') {
      const conGancho = !isSinGancho(ganchoId);
      const guionDur = voiceGuion ? getGuionDurationSec(voiceGuion) : null;
      if (guionDur != null)
        return (conGancho ? INFLUENCER_GUION_BASE_COST : 0) + guionDur;
      return videoDuration;
    }
    const ratePer = preset.creditosPorUnidad;
    if (ratePer == null) return null;
    const unitMul =
      preset.unidadConsumo === 'segundo'
        ? preset.durationSeconds || 1
        : 1;
    const presetCount = multiPresetIds.length > 0 ? multiPresetIds.length : 1;
    // 'design-auto' y 'anuncio' generan `autoImageCount` imágenes (el
    // control de Cantidad), así que el costo escala con esa cantidad.
    const autoCountMul =
      preset.kind === 'design-auto' || preset.kind === 'anuncio'
        ? autoImageCount
        : 1;
    // design-modelo (single-modelo) genera 1 imagen por pose seleccionada
    // — el backend itera el array de `pose` y devuelve 1 imagen por cada
    // entry. En multi-modelo no aplica: ese flow tiene pose single-select.
    // Con UNA sola pose el multiplicador es el contador de cantidad
    // (`designModeloCount`), que en ese caso pide N variaciones del mismo
    // prompt (ADR-2026-07-30-imagen-personalizada-cantidad).
    // design-modelo en v2: el costo sale de la MISMA cuenta que el botón
    // (`tandaV2.total` = cantidad × poses × modelos que multiplican). Los tres
    // multiplicadores de abajo quedan en 1 para este modo: hacían la cuenta por su lado
    // y por eso el pill y el botón podían discrepar
    // (ADR-2026-08-28-modelo-v2-la-cantidad-manda).
    const esModeloDeEsteFork = esDesignModelo(preset.kind);
    // `unidadesCobradas` y no `total`: el precio de la tanda sale de un solo lugar
    // (`resolveTandaModelo`), que es quien sabe si la composición grupal cobra sus
    // modelos o la foto entregada. HOY cobra la foto —el backend debita por entrega y
    // para `modelo_mode: 'multiple'` eso es 1
    // (ADR-2026-09-04-el-precio-del-grupo-espera-al-backend)—, así que las dos cuentas
    // coinciden; el día que el backend cobre por modelo, este pill se entera solo.
    const tandaMul = esModeloDeEsteFork ? tandaV2.unidadesCobradas : 1;
    const poseCountMul = 1;
    // En multi-modelo (design-modelo con varios modelos, o design /
    // E-commerce con pose 2+ personas) el backend genera 1 imagen por
    // cada modelo extra. El multiplicador es `1 + extras` = total de
    // personas / slots en la composición.
    // En E-commerce (`design`) con pose de 2+ personas el backend sí genera una imagen
    // por slot, así que ese multiplicador se queda. En design-modelo NO: los modelos van
    // todos en la misma foto y multiplicar acá cobraba 3 por una sola imagen.
    const modelCountMul =
      preset.kind === 'design' && multiModelEnabled
        ? 1 + modelSelectionsExtra.length
        : 1;
    // Serie de modelos (grilla multi-select de design-modelo): cada modelo es
    // una generación aparte — un request por modelo, con las poses elegidas
    // dentro de cada uno. Multiplica sobre `poseCountMul`
    // (ADR-2026-08-20-modelos-multi-select-serie).
    // Ya va dentro de `tandaMul` para design-modelo; se conserva la constante para no
    // reordenar la multiplicación de abajo.
    const modelSerieMul = 1;
    // 'fotos-producto' genera UNA imagen por vista elegida (un request por
    // vista, ADR-2026-08-13-fotos-producto-fan-out-por-vista). Sin vistas el
    // Generar está bloqueado, así que el piso de 1 es solo cosmético.
    const vistaCountMul =
      preset.kind === 'fotos-producto' ? Math.max(1, vistaIds.length) : 1;
    const imageCost =
      ratePer *
      unitMul *
      presetCount *
      autoCountMul *
      tandaMul *
      poseCountMul *
      modelCountMul *
      modelSerieMul *
      vistaCountMul;
    // Cuando el modo es video, sumamos el costo de animar cada imagen
    // generada (1 video por imagen). El total que ve el user en el botón
    // de Generar = imágenes + videos.
    const totalImages =
      presetCount *
      autoCountMul *
      poseCountMul *
      modelCountMul *
      modelSerieMul *
      vistaCountMul;
    const videoCost = isVideoModeActive
      ? videoCreditCostPerImage * totalImages
      : 0;
    // La Calidad multiplica el costo de CADA imagen entregada
    // (ADR-2026-08-25-resolucion-de-generacion-2k-4k). Se aplica sobre las imágenes
    // REALES de la tanda —`totalImages` para los modos que fan-outean, y para
    // design-modelo lo que ya cobra `tandaV2`—, no sobre la cantidad del stepper.
    // En design-modelo esa base son las `unidadesCobradas` (ver `tandaMul`): así, el día
    // que la foto grupal cobre sus modelos, el extra de Calidad los cobra a todos en 4K
    // y no la composición sola.
    const imagenesDeLaTanda = esModeloDeEsteFork ? tandaMul : totalImages;
    const extraResolucion =
      (creditosPorResolucion(calidad) - 1) * imagenesDeLaTanda;
    return imageCost + videoCost + extraResolucion;
  })();

  // Costo de "Generar producción" = generación normal + carrusel. El
  // carrusel (generar_nuevos_angulos) produce 2 imágenes, así que sumamos
  // 2 × el costo por imagen del preset.
  const CARRUSEL_IMAGE_COUNT = 2;
  const productionCreditCost =
    creditCost != null && preset.creditosPorUnidad != null
      ? creditCost + CARRUSEL_IMAGE_COUNT * preset.creditosPorUnidad
      : creditCost;

  // Último hint del pill flotante del influencer — se conserva para que el
  // texto no desaparezca de golpe durante el fade-out de la card (ADR-552).
  const lastInfluencerHintRef = useRef<string>('');

  // Index para navegar el preview en DESKTOP. Solo se inicializa con el
  // preset que abrió el sheet — en mobile cada card se renderiza
  // independiente, no se usa este state.
  const [previewIndex, setPreviewIndex] = useState<number>(() => {
    const i = previewList.findIndex((p) => p.id === preset.id);
    return i >= 0 ? i : 0;
  });

  // `panelOpen` controla el slide-up del panel inferior (steps !=
  // preview) animando max-height de 0 → valor final. Eso causa que la
  // imagen (flex-1 arriba) también anime su altura por el cambio de
  // flex space, dando el efecto "panel sube mientras la imagen se
  // acomoda al espacio restante" pedido por el user. Reset a false
  // cuando volvemos a preview.
  const [panelOpen, setPanelOpen] = useState<boolean>(step !== 'preview');

  // Ref al scroll container del preview mobile — necesario para
  // sincronizar el scroll inicial con el `previewIndex` resuelto
  // desde el preset tappeado.
  const mobilePreviewScrollRef = useRef<HTMLDivElement | null>(null);

  // Step "renderizado": sigue a `step` pero retrasa la transición
  // hacia 'preview' por 300ms para que el panel inferior alcance a
  // animar su salida (max-height → 0) ANTES de que el render
  // condicional cambie de rama. Sin esto, al volver de
  // choose-product el panel desaparecía instantáneo.
  const [renderedStep, setRenderedStep] = useState(step);

  // Effect 1: sync `renderedStep` con `step`.
  //   OUT (→ preview): primero cerrar el panel (panelOpen=false), 300ms
  //   después cambiar renderedStep para que el render cambie de rama.
  //   IN (preview → otro): cambiar renderedStep inmediato → la rama
  //   else se monta. La animación de entrada la dispara Effect 2.
  useEffect(() => {
    if (step === 'preview') {
      setPanelOpen(false);
      const id = window.setTimeout(() => setRenderedStep('preview'), 300);
      return () => window.clearTimeout(id);
    }
    setRenderedStep(step);
  }, [step]);

  // Effect 2: animación de entrada del panel cuando se viene de
  // 'preview'. useLayoutEffect garantiza que corre síncrono justo
  // después del commit que monta el panel — así setPanelOpen(false)
  // se aplica antes del paint, y luego el rAF dispara setPanelOpen(true)
  // en el siguiente frame para que CSS transition detecte el cambio
  // 0 → 55dvh. Sin el useLayoutEffect, React podía batchear los dos
  // setStates en un único commit y la animación se "saltaba".
  const prevRenderedStepRef = useRef(renderedStep);
  useLayoutEffect(() => {
    const prev = prevRenderedStepRef.current;
    prevRenderedStepRef.current = renderedStep;
    if (renderedStep === 'preview') return;
    // Solo animamos cuando venimos de preview (mount fresco del panel).
    // Transiciones entre non-preview steps (choose-product → configure)
    // no resetean panelOpen — el panel sigue abierto y solo cambia
    // max-height de 55dvh a 78dvh por la transición CSS existente.
    if (prev !== 'preview') return;
    setPanelOpen(false);
    let rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => setPanelOpen(true));
    });
    return () => cancelAnimationFrame(rafId);
  }, [renderedStep]);

  // Detección de dirección del cambio de step para elegir la animación
  // del contenido al cambiar entre steps non-preview (choose-product ↔
  // configure ↔ upload-product, etc.). Adelante = el contenido nuevo
  // entra de abajo (`animate-mode-swap`); atrás = de arriba
  // (`animate-mode-swap-back`), dando la sensación de "desandar" el
  // movimiento del panel. La transición preview↔step no usa esto — ahí
  // el panel mismo desliza con max-height.
  const stepOrder = (s: typeof step): number => {
    switch (s) {
      case 'preview':
        return 0;
      case 'choose-product':
        return 1;
      case 'pick-product':
      case 'upload-product':
      case 'select-vistas':
        return 2;
      case 'configure':
        return 3;
      default:
        return 0;
    }
  };
  /**
   * ¿El último cambio de paso fue hacia ATRÁS? Decide de qué lado entra el contenido.
   *
   * Se congela EN EL RENDER del cambio, en un ref, en vez de derivarse en cada render
   * comparando contra un ref que actualiza un `useEffect`. Con aquella versión el valor
   * era correcto en el render del cambio, pero el efecto corría después y dejaba
   * `prev === step`: cualquier re-render posterior —y hay varios: el refresh de
   * productos, el resize del grid— recalculaba `isBack` en `false` y le cambiaba la
   * clase al nodo YA montado, lo que en CSS no es un no-op sino una animación NUEVA. En
   * la práctica, volver de "Configurar" mostraba la animación de avanzar.
   */
  const prevStepForAnimRef = useRef(step);
  const isBackStepRef = useRef(false);
  if (step !== prevStepForAnimRef.current) {
    isBackStepRef.current =
      stepOrder(step) < stepOrder(prevStepForAnimRef.current);
    prevStepForAnimRef.current = step;
  }
  const isBackStepTransition = isBackStepRef.current;
  const swapAnimClass = isBackStepTransition
    ? 'animate-mode-swap-back'
    : 'animate-mode-swap';
  /**
   * Transición de la columna que cambia al pasar de "Elegí el producto" a "Configurar".
   *
   * Se dispara por REMONTAJE: el contenedor lleva un `key` distinto en cada paso, así que
   * React tira el nodo viejo y monta el nuevo, y la animación de entrada corre sola. No
   * hay animación de SALIDA —el nodo viejo se va en el mismo frame—: mantenerlo vivo para
   * desvanecerlo obligaría a duplicar el árbol de la configuración (con sus pickers y sus
   * requests) durante la transición, que es mucho costo para 300ms.
   */
  const pasoAnimClass = isBackStepTransition
    ? 'animate-paso-atras'
    : 'animate-paso-adelante';

  // Sincronizar el scroll con `previewIndex` cada vez que entramos al
  // step preview (mount inicial + volver desde choose-product).
  // Usamos `renderedStep` (no `step`) porque la transición OUT tiene
  // delay: el scroll container del preview branch recién se monta
  // cuando `renderedStep` pasa a 'preview', después de que el panel
  // terminó de animar.
  useEffect(() => {
    if (renderedStep !== 'preview') return;
    const el = mobilePreviewScrollRef.current;
    if (!el || el.clientHeight <= 0) return;
    el.scrollTop = previewIndex * el.clientHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedStep]);

  // Si el user vino del flow multi-select, resolvemos los preset objects
  // a partir de los ids (lookup en previewList) — se usan para mostrar
  // un carrusel en vez de una sola imagen en los steps post-preview.
  const multiPresets = React.useMemo<PresetV2[]>(() => {
    if (multiPresetIds.length < 2) return [];
    return multiPresetIds
      .map((id) => previewList.find((p) => p.id === id))
      .filter((p): p is PresetV2 => !!p);
  }, [multiPresetIds, previewList]);
  const isMultiPreset = multiPresets.length > 1;
  const desktopPreviewPreset = previewList[previewIndex] || preset;

  const canPrev = previewIndex > 0;
  const canNext = previewIndex < previewList.length - 1;

  // Multi-select: aplica a kind 'template' siempre y a kind 'design'
  // cuando la pose tiene UNA sola persona. Las poses multi-persona no
  // admiten multi-select porque el backend solo soporta una pose
  // multi-persona por job (mezclar dos poses con N personas no tiene
  // semántica clara). En multiMode el botón "Utilizar" se reemplaza
  // por "Utilizar seleccionadas (N)" y los cards muestran un check
  // overlay. Tap en card toggle la selección en vez de utilizar.
  //
  // `canMultiSelect` ahora depende del PRESET ACTUAL (currentPreset al
  // navegar en preview), no del preset inicial — para que al swipear
  // entre poses de e-commerce, una pose 2-persona oculte el botón
  // mientras una 1-persona lo muestre.
  // TEMP — multi-selección de PLANTILLAS desactivada y oculta a pedido. El
  // botón "Seleccionar varias" (ícono Copy) del preview de plantillas no se
  // renderiza y su lógica de multi-add queda deshabilitada. Poné en `true` para
  // reactivar. Las poses de "Fondo blanco" (`design`) tampoco la tienen desde
  // ADR-2026-10-12-fondo-blanco-como-las-plantillas: se elige una y se eligen
  // calidad y cantidad, como en el resto.
  const TEMPLATE_MULTISELECT_ENABLED = false;
  const isPresetMultiSelectable = (p: PresetV2 | null | undefined): boolean => {
    if (!p) return false;
    if (p.kind === 'template') return TEMPLATE_MULTISELECT_ENABLED;
    if (p.kind === 'design') return false;
    // 'design-producto' (vistas solo-producto de "Fondo blanco") queda
    // fuera a propósito: su generación es 1 vista = 1 request y el flow
    // multi de e-commerce arma `poseIds`, que estas cards no usan.
    return false;
  };
  const [multiMode, setMultiMode] = useState<boolean>(false);
  const [multiSelectedIds, setMultiSelectedIds] = useState<string[]>([]);
  const isMultiSelected = (id: string) => multiSelectedIds.includes(id);
  const toggleMultiId = (id: string) => {
    setMultiSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  // Tap en "Seleccionar varias" arranca el modo y agrega el preset
  // actualmente visible (el que el user está mirando). Sino el user ve
  // checks vacíos y no entiende qué pasa.
  const enterMultiMode = (currentId: string) => {
    setMultiMode(true);
    setMultiSelectedIds([currentId]);
  };
  // Salir de multi mode resetea la selección. Lo llamamos cuando el user
  // toca cancelar o cuando deselect lleva a N=0.
  const exitMultiMode = () => {
    setMultiMode(false);
    setMultiSelectedIds([]);
  };
  const multiSelectedPresets = multiSelectedIds
    .map((id) => previewList.find((p) => p.id === id))
    .filter((p): p is PresetV2 => !!p);
  const handleConfirmMulti = () => {
    if (multiSelectedPresets.length === 0) return;
    onUtilizarMultiple(multiSelectedPresets);
  };
  // Si el user salió del step 'preview' o cambió de colección (previewList
  // distinto), reseteamos el modo para no arrastrar selección stale.
  useEffect(() => {
    if (step !== 'preview') {
      setMultiMode(false);
      setMultiSelectedIds([]);
    }
  }, [step]);

  // Wheel/trackpad navigation en el carousel desktop. Mantenemos un lock
  // corto para que un fling continuo no skippee 5 plantillas de una.
  // Usamos addEventListener con passive: false para poder preventDefault y
  // así el browser no haga back/forward gesture en mac.
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const wheelLockRef = useRef<boolean>(false);
  useEffect(() => {
    if (step !== 'preview') return;
    const el = carouselRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Si el trackpad da deltaX (swipe horizontal) lo preferimos. Sino
      // caemos a deltaY (mouse wheel) — así también con scroll vertical
      // se puede navegar de plantilla, que es el feedback que pidió el user.
      const horizIntent = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const delta = horizIntent ? e.deltaX : e.deltaY;
      if (Math.abs(delta) < 12) return;
      e.preventDefault();
      if (wheelLockRef.current) return;
      if (delta > 0) {
        setPreviewIndex((i) =>
          i < previewList.length - 1 ? i + 1 : i,
        );
      } else {
        setPreviewIndex((i) => (i > 0 ? i - 1 : i));
      }
      wheelLockRef.current = true;
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 450);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [step, previewList.length]);
  // El step content lo compartimos entre mobile y desktop. Para el step
  // 'configure', PASAMOS un flag `useStickyGenerate` para que en desktop
  // el botón Generar quede pegado al bottom del panel (mt-auto). En
  // mobile el panel es un bottom sheet limitado a 55dvh con scroll
  // interno — no aplica el sticky bottom.
  /**
   * `pasoForzado` dibuja el contenido de OTRO paso sin mover el estado. Lo usa el
   * `configure` a dos columnas, que necesita el panel de productos —el contenido de
   * `choose-product`— a la derecha mientras el paso real ya es `configure`.
   */
  /**
   * Cabecera de la pantalla (sólo el título) para meterla DENTRO del panel de
   * productos, en su borde de arriba.
   *
   * Afuera se come una franja del alto por encima de las dos columnas; adentro, la fila
   * arranca pegada al techo y ese alto se lo quedan las columnas, que es lo que se
   * quiere en una pantalla donde la grilla y la configuración pelean por espacio
   * vertical. El `<h1>` va en 17px y no en los 32 de una sección a todo lo ancho: acá
   * vive en una columna angosta y compite con el título propio del panel.
   *
   * SIN BREADCRUMB (variante v2 del banco de pruebas): el rastro "Inicio / Modelo"
   * repetía, arriba del `<h1>`, el mismo nombre que el título — dos líneas para decir
   * dónde estás en una columna que le pelea el alto a la grilla de productos. El
   * título solo alcanza, y el paso se lee en el CTA. Ver
   * ADR-2026-08-21-modelo-v2-cabecera-sin-breadcrumb.
   */
  /**
   * Las migas hasta el MODO, sin el nivel del paso —
   * ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso. Ver la nota del mismo
   * nombre en `PresetDetailSheet`: el encabezado del panel es la identidad de la
   * pantalla y no puede cambiar al avanzar; el paso lo dice la columna de al lado.
   * En este modo el paso casi siempre es `choose-product` (las migas ya vienen sin
   * nivel extra), así que el recorte es un backstop — pero mantiene a los dos forks
   * comportándose igual.
   */
  const migasDelModo = migas?.slice(0, 2);
  const tituloDelModo =
    migasDelModo?.[migasDelModo.length - 1]?.label ?? tituloSeccion;
  const cabeceraEnPanel = modoConDisenoNuevo ? (
    <div className="px-6 pt-5 pb-1">
      {/* El breadcrumb vuelve — ADR-2026-09-04-el-modo-modelo-recupera-el-breadcrumb,
          supersede ADR-2026-08-21-modelo-v2-cabecera-sin-breadcrumb.

          Lo había sacado el banco de pruebas: en `choose-product` la hoja del rastro es
          el modo, así que "Inicio / Modelo" y el título debajo decían dos veces lo mismo
          en una columna angosta. Pero el resto de los modos del rediseño —Catálogo, Fotos
          de producto, Automático— lo tienen ahí, y ahora Modelo es el único que no dice
          en qué parte de la app está ni ofrece la vuelta a Inicio de un tap.

          Mismo armado que en el otro shell (`PresetDetailSheet`): `EncabezadoPantalla`
          arriba del título, sin padding propio —lo pone esta caja— y con el "Volver" de
          respaldo por si un modo llega sin migas. */}
      <EncabezadoPantalla
        migas={migasDelModo}
        className="!px-0 !pt-0 !pb-2"
        respaldo={
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
        }
      />
      {/* `key` por título: al cambiar de paso el título se remonta y hace su fade. Sin
          desplazamiento — el movimiento lo lleva la columna de al lado, y esta caja es
          justamente la que NO se mueve. */}
      {/* `<h1>`, como el resto de los modos — ADR-2026-09-08-el-titulo-del-modo-modelo-mide-lo-mismo-que-el-resto.
          `.shell-seccion h1` (index.css) le da 32px/700 con `!important`, que es el
          tamaño con el que se leen "Catálogo" o "Fotos de producto"; con `<h2>` la regla
          no lo alcanzaba y este título salía a 23px, más chico que el de todos los demás.
          Las clases de tamaño quedan como respaldo para cuando esta cabecera se monta
          fuera del shell: la regla global gana cuando está adentro.
          Supersede la nota de ADR-2026-08-21-modelo-v2-cabecera-sin-breadcrumb, que
          usaba `<h2>` justamente para esquivar esa regla. */}
      <h1
        key={tituloDelModo}
        className="animate-paso-titulo text-[32px] font-bold leading-tight tracking-[-0.02em] text-brand-black/90"
      >
        {tituloDelModo}
      </h1>
      {/* Qué se hace con varios productos elegidos. Va SÓLO en el paso 1 —donde la
          pregunta existe— y no en la configuración, que ya muestra lo elegido: en el
          rediseño esta línea se había sacado justamente por repetirse en las dos
          (ADR-2026-08-21-modelo-v2-cabecera-sin-breadcrumb). Que la multi-selección arma
          un OUTFIT no lo dice la grilla: es lo que separa elegir tres productos
          esperando tres fotos de esperar una sola con los tres puestos. */}
      {eligiendoProductos && (
        <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
          Elegí una o varias imágenes: con más de una, Atlas arma el outfit completo.
        </p>
      )}
    </div>
  ) : undefined;

  const renderStep = (
    variant: 'mobile' | 'desktop',
    pasoForzado?: PanelStep,
  ) => {
    // En mobile, los steps 'pick-product' y 'upload-product' viven en una
    // pantalla full-screen aparte (portal sobre la top bar). El preset
    // sheet inline debajo se queda con el contenido de choose-product como
    // fallback (es lo que el usuario verá cuando cierre esa pantalla).
    const pasoBase = pasoForzado ?? step;
    const effectiveStep =
      variant === 'mobile' &&
      (pasoBase === 'pick-product' || pasoBase === 'upload-product')
        ? 'choose-product'
        : pasoBase;

    // Subida en bloque de catálogo en curso → el spinner ocupa SOLO la card
    // (este panel, donde viven el grid + "Subir nuevo producto"), no toda la
    // pantalla. Mismo patrón visual que ModelProcessingView (ModelPicker).
    if (bulkUploadingProducts) {
      return (
        <div className="flex flex-col items-center justify-center text-center gap-5 h-full min-h-[300px] px-6 py-10">
          <div className="relative w-16 h-16">
            <div
              className="absolute inset-0 rounded-full border-4 border-brand-primary/15 border-t-brand-primary animate-spin"
              style={{ animationDuration: '1s' }}
            />
          </div>
          <div>
            <p className="text-[16px] font-semibold text-brand-black/90">
              Subiendo productos…
            </p>
            <p className="text-[13px] text-[#3C3C43]/55 mt-1.5 max-w-sm">
              Estamos creando un producto por cada foto. Puede tardar unos
              segundos.
            </p>
          </div>
        </div>
      );
    }

    return effectiveStep === 'choose-product' ? (
      // DESKTOP (modos de diseño Y plantillas): fusionamos el "Elegí el
      // producto" (header + Subir) con la lista de productos en un solo panel
      // scrolleable. Mobile mantiene el ChooseProductPanel (flujo de dos pasos
      // / pantalla full-screen separada).
      variant === 'desktop' ? (
        <PickProductPanelModelo
          // Re-montar cuando cambia la selección confirmada (ej. tras una
          // subida en bloque de catálogo, que setea `selectedProducts` SIN
          // navegar de paso): así el grid fusionado re-inicializa su selección
          // interna desde `initialSelectedIds` y muestra lo recién subido
          // tildado. Durante el uso normal (toggles internos) `selectedProducts`
          // no cambia → el key es estable → no remonta.
          //
          // …salvo en los pasos donde la selección viaja EN VIVO (Automática y los pasos a
          // dos columnas): ahí cada tap cambia `selectedProducts`, así que un key derivado
          // de la selección remontaría el panel en cada toque —perdiendo el scroll de la
          // grilla—. En esos pasos la fuente de verdad es la selección interna del panel y
          // el host la espeja, así que no hay nada que re-inicializar.
          key={seleccionEnVivo ? 'choose-pick-vivo' : `choose-pick-${selectedProducts.map((p) => p.id).join('-')}`}
          variant="desktop"
          // En este modo el "Siguiente" abre la configuración de al lado y no un paso
          // nuevo del shell — ver `confirmarProductosDelPaso1`.
          onConfirm={unaColumna ? confirmarProductosDelPaso1 : onConfirmProducts}
          onBack={onBackToChoose}
          initialSelectedIds={selectedProducts.map((p) => p.id)}
          tiposProductoSugeridos={preset.tiposProductoSugeridos}
          rubro={rubro}
          onStartUpload={onStartUpload}
          onStartNewProduct={onStartNewProduct}
          presetKind={preset.kind}
          onSkipProduct={onSkipProduct}
          // Sin el bloque de "Elegí el producto para generar" + "Subir nuevo producto"
          // arriba de la grilla: la imagen de muestra de al lado ya presenta el modo, y
          // el bloque repetía el mensaje empujando los productos fuera de la pantalla.
          ocultarBloqueSubida={modoConDisenoNuevo}
          // El alta arranca desde la primera tarjeta de la grilla y corre en un drawer,
          // en vez de navegar al step `upload-product` y llevarse la pantalla.
          // Dos tarjetas, "Un producto" y "Subir varios productos", que abren el pop-up de subir
          // (ADR-2026-10-14-subir-productos-en-un-pop-up-desde-modelo).
          onSubirProducto={
            modoConDisenoNuevo
              ? () => setSubirEnVentana('uno')
              : undefined
          }
          onSubirVarios={
            modoConDisenoNuevo
              ? () => setSubirEnVentana('varios')
              : undefined
          }
          // Tarjetas sin marco, con el nombre en blanco sobre un degradado encima de la
          // foto en vez de en un pie aparte.
          nombreSobreLaFoto={modoConDisenoNuevo}
          // Tres columnas fijas: tarjetas más chicas, tres productos por fila. En el
          // preview del paso 2 el panel es angosto y baja a dos.
          columnasFijas={
            panelComprimido ? 1 : modoConDisenoNuevo ? 3 : undefined
          }
          // Paso 2: el panel se comprime a una preview de lo elegido, con un control para
          // volver a abrirlo sin salir del paso.
          compacto={panelComprimido}
          onAlternarCompacto={
            pasoDosColumnas ? () => setPreviewComprimida((v) => !v) : undefined
          }
          // Ajustes rápidos al pie: lo que casi siempre se toca, sin tener que pasar por
          // la pantalla de configuración. Fondo y Formato abren los pickers que ya
          // existen; Calidad se resuelve en la fila.
          // El CTA se ve desde el principio, deshabilitado hasta que haya un producto
          // elegido: así se lee cuál es el paso siguiente antes de tocar nada.
          footerSiempreVisible={modoConDisenoNuevo}
          // La línea de "elegí uno o varios" y el degradado del pie del scroll.
          pistasDeUso={modoConDisenoNuevo}
          // Comprimido no lleva cabecera: el breadcrumb y el título de la pantalla ya
          // están en la columna de al lado, y repetirlos en 280px se comía el alto que
          // necesitan las miniaturas.
          cabecera={panelComprimido ? undefined : cabeceraEnPanel}
          // Automático genera desde acá, así que lo elegido tiene que viajar hacia arriba
          // en el momento del tap y no al confirmar (que en ese modo no existe).
          onSelectionChange={seleccionEnVivo ? onProductosEnVivo : undefined}
          // Paso 1 "Siguiente" (confirma los productos y abre la configuración); paso 2
          // "Generar" con el costo — el mismo botón, en el mismo lugar, avanzando.
          confirmLabel={
            videoDosColumnas
              ? 'Generar video'
              : configureDosColumnas || catalogoVistasDosColumnas || autoUnSoloPaso
                ? 'Generar'
                : modoConDisenoNuevo
                  ? 'Siguiente'
                  : undefined
          }
          onConfirmOverride={
            videoDosColumnas
              ? // Con algo pendiente el CTA está APAGADO
                // (ADR-2026-08-21-el-generar-de-video-se-apaga), así que esto no debería
                // correr; el guard queda igual por las dudas y, si corre, manda el foco
                // al bloque que falta y lo sacude (ADR-552/553).
                () => {
                  if (faltaEnInfluencer) {
                    scrollToAndShake(faltaEnInfluencer.targetId);
                    return;
                  }
                  onGenerate(creditCost != null && creditCost > 0 ? creditCost : 1);
                }
              : configureDosColumnas || autoUnSoloPaso
              ? () => onGenerate(creditCost != null && creditCost > 0 ? creditCost : 1)
              : catalogoVistasDosColumnas
                ? // El botón del panel GENERA: este paso es el ÚLTIMO del modo, sin
                  // pantalla de review en el medio. Las cards salen del mismo armador que
                  // usaba el review (`construirCardsDeCatalogo`), así que lo que se genera
                  // es exactamente lo que se generaba pasando por ahí.
                  //
                  // El guard queda por las dudas: el botón ya está apagado sin vistas
                  // (`faltaEnCatalogo`), así que esto no debería poder llamarse en cero.
                  () => {
                    if (cardsDeLaTanda.length === 0) return;
                    onGenerateCatalog(cardsDeLaTanda);
                  }
                : undefined
          }
          // Resumen de la tanda arriba del CTA: cuántas imágenes salen y de qué
          // combinación (modelos × poses). A dos columnas el Generar es ESTE
          // botón, no el del ConfigurePanel.
          confirmHint={
            faltaEnCatalogo
              ? faltaEnCatalogo
              : catalogoVistasDosColumnas
                ? // Listo para generar: lo que va a salir. Es el mismo número de la
                  // pastilla de créditos, escrito de manera que se entienda de dónde sale
                  // (una imagen por cada combinación vista × producto).
                  `${cardsDeLaTanda.length} ${
                    cardsDeLaTanda.length === 1 ? 'imagen' : 'imágenes'
                  } · ${selectedProducts.length} ${
                    selectedProducts.length === 1 ? 'producto' : 'productos'
                  } × ${catalogoVistas.length} ${
                    catalogoVistas.length === 1 ? 'vista' : 'vistas'
                  }`
                : videoDosColumnas
              ? // Con algo pendiente, el resumen sobra: lo que hay que decir es qué falta.
                faltaEnInfluencer
                ? faltaEnInfluencer.msg
                : `1 video · ${creditCost ?? videoDuration} segundos`
              : faltaModeloOPose
              ? // El resumen de la tanda no sirve acá: contaría "1 modelo × 1 pose"
                // —trata el 0 como "automático"— justo cuando no hay nada elegido.
                modelosElegidos === 0
                ? `Elegí al menos un ${palabra.singular}.`
                : 'Elegí al menos una pose.'
              : configureDosColumnas && esDesignModelo(preset.kind)
              ? // También con composición grupal: ahí el resumen es lo único que explica
                // por qué tres modelos no son tres imágenes
                // (ADR-2026-08-28-modelo-v2-la-cantidad-manda).
                resumenTandaModelo(tandaV2, multiModelEnabled)
              : // Automático no tiene modelo ni pose que resumir —la escena la decide la
                // IA—, pero sí tiene una combinación que no es obvia: con varios
                // productos elegidos NO sale una imagen por producto, sale UNA con todos
                // juntos. Decirlo acá evita la sorpresa de pagar una imagen esperando
                // tres.
                autoUnSoloPaso
                ? resumenAutomatica(autoImageCount, selectedProducts.length)
                : undefined
          }
          // Paso 2 de "Imagen con modelo": sin modelo o sin pose no hay imagen posible, así
          // que el "Generar" se apaga en vez de disparar una tanda que el backend
          // resolvería por su cuenta y el usuario pagaría igual.
          //
          // Video, igual: sin gancho / modelo / voz / guion el botón queda apagado y lo
          // que falta lo dice la línea de arriba
          // (ADR-2026-08-21-el-generar-de-video-se-apaga).
          confirmDisabled={
            faltaModeloOPose ||
            (videoDosColumnas && !!faltaEnInfluencer) ||
            faltaEnCatalogo != null
          }
          confirmBadge={
            catalogoVistasDosColumnas ? (
              cardsDeLaTanda.length > 0 ? (
                // Lo que va a costar la tanda: 1 crédito por imagen, y las imágenes son
                // las combinaciones vista × producto. NO es la cantidad de vistas: con
                // dos productos, cada vista vale dos imágenes.
                <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                  <img
                    src="/images/token_icon.png"
                    alt=""
                    aria-hidden="true"
                    className="w-3.5 h-3.5 object-contain"
                  />
                  {cardsDeLaTanda.length}
                </span>
              ) : undefined
            ) : (configureDosColumnas || autoUnSoloPaso) &&
              !faltaModeloOPose &&
              creditCost != null &&
              creditCost > 0 ? (
              // Pastilla BLANCA con el texto en azul, no la translúcida sobre el botón: el
              // costo es el dato que hay que poder leer de una antes de apretar, y a
              // 12.5px sobre azul el blanco al 25% no daba contraste.
              //
              // El ícono de token no hace falta recolorearlo: el asset YA es un círculo
              // azul con la estrella blanca calada, así que sobre blanco lee igual de
              // bien que sobre el botón.
              <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                />
                {creditCost}
              </span>
            ) : undefined
          }
          pie={
            // En el paso 1 del modo Modelo el pie NO va: Calidad, Fondo, Formato y la
            // cantidad son exactamente las celdas de la pantalla siguiente, que se abre
            // apenas se toca "Siguiente" — repetirlas acá es pedir dos veces lo mismo y
            // le come a la grilla el alto que este paso vino a darle. En los modos que
            // eligen productos y generan de una (Automática, Video) el pie sigue siendo
            // el único lugar donde están esos ajustes.
            modoConDisenoNuevo && !unaColumna ? (
              <AjustesRapidosProducto
                // Video no elige ni Calidad (la resolución la fija el pipeline) ni Fondo
                // (la escena la arma el influencer): las dos celdas no se dibujan
                // (ADR-2026-08-21-video-con-el-diseno-nuevo).
                calidad={esVideoInfluencer ? undefined : calidad}
                onCalidadChange={esVideoInfluencer ? undefined : setCalidad}
                onCambiarModeloYPose={onCambiarModeloYPose}
                fondoLabel={
                  esVideoInfluencer
                    ? undefined
                    : fondoSelectionLabel(fondoSelection)
                }
                onOpenFondo={
                  esVideoInfluencer
                    ? undefined
                    : () =>
                        onOpenFondoSheet(
                          usaBibliotecaDeFondos(preset.kind) ? 'all' : 'list',
                        )
                }
                // La barra de Duración, la misma que vive dentro de "Voz y guion": acá
                // sólo cuando el guion es Automático, porque con guion escrito o grabado
                // la duración la marca el guion (ADR-528).
                duracion={duracionEnAjustes}
                formatoLabel={getFormatLabel(formatoVal)}
                onOpenFormato={onOpenFormatPicker}
                compacto={panelComprimido}
                // Instrucción libre — SÓLO en automático, que no tiene paso 2 donde
                // ponerla (ADR-2026-08-21-automatico-con-el-diseno-nuevo). En los modos
                // con configuración aparte sigue viviendo allá, con su textarea grande.
                instruccion={
                  preset.kind === 'design-auto' ? otraConfig : undefined
                }
                onInstruccionChange={
                  preset.kind === 'design-auto' ? onOtraChange : undefined
                }
                // El contador va como una celda más de los ajustes, no en el pie: es un
                // control del mismo rango que Calidad o Formato y se lee mejor con la
                // misma etiqueta arriba. Sólo donde EXISTE de verdad: `design-modelo`
                // genera N variaciones del mismo prompt y ya tiene su estado
                // (`autoImageCount`) y su tope. En Catálogo no aparece porque ahí la
                // cantidad no se elige — sale de (vistas × productos), y un contador acá
                // prometería un control que la pantalla siguiente ignora.
                cantidad={
                  // En automático el contador es el del propio modo (`autoImageCount`) y
                  // siempre aplica: no hay poses ni modelos que lo puedan anular. En
                  // Video no existe: sale UN video, y su largo lo fija la Duración.
                  !esVideoInfluencer &&
                  // En v2 el contador NO se apaga con 2+ poses o 2+ modelos: la cantidad
                  // multiplica siempre y el fan-out del front la cumple
                  // (ADR-2026-08-28-modelo-v2-la-cantidad-manda). Apagarlo dejaba el
                  // número a la vista sin efecto.
                  (preset.kind === 'design-auto' || modoConDisenoNuevo) ? (
                    <ContadorImagenes
                      valor={autoImageCount}
                      max={autoImageCountMax}
                      onChange={onAutoImageCountChange}
                      disabled={selectedProducts.length === 0}
                    />
                  ) : undefined
                }
              />
            ) : undefined
          }
          hideBack
          // El video con influencer se genera con UN solo producto (ADR-563).
          singleSelect={singleProductMode}
          title={
            preset.kind === 'video-influencer' ? 'Elegí tu producto' : undefined
          }
        />
      ) : unaColumna ? (
        // v2 mobile: la MISMA card que la columna de desktop, en vez de los dos CTA
        // ("Subir nuevo producto" / "Elegir de mis productos") —
        // ADR-2026-08-23-modelo-v2-mobile. Muestra lo elegido, que los botones no hacían,
        // y abre la pantalla de productos, que en mobile es full-screen (`pick-product`).
        // Soltar archivos encima sigue subiendo, que es lo que hacía el primer CTA.
        // `mt-auto`: la card baja al PIE de la columna, apoyada sobre la barra, en vez de
        // quedar colgada arriba con todo el alto libre debajo. Se empuja con margen y no
        // con `justify-end` en el scroller: si algún día el contenido crece más que la
        // pantalla, un `justify-end` deja la primera parte inalcanzable, y el margen
        // colapsa solo (ADR-2026-09-04-modelo-mobile-la-card-de-productos-baja-al-pie).
        <div className="mt-auto">
          <CardProductos
            products={selectedProducts}
            onAbrirProductos={onShowProducts}
            onSubirArchivos={onStartUpload}
          />
        </div>
      ) : (
        <ChooseProductPanel
          onShowProducts={onShowProducts}
          onStartNewProduct={onStartNewProduct}
          presetKind={preset.kind}
          onSkipProduct={onSkipProduct}
        />
      )
    ) : effectiveStep === 'pick-product' ? (
      <PickProductPanelModelo
        variant={variant}
        onConfirm={onConfirmProducts}
        onBack={onBackToChoose}
        initialSelectedIds={selectedProducts.map((p) => p.id)}
        tiposProductoSugeridos={preset.tiposProductoSugeridos}
        rubro={rubro}
        singleSelect={singleProductMode}
      />
    ) : effectiveStep === 'upload-product' ? (
      <UploadProductPanel
        variant={variant}
        files={pendingProductFiles}
        name={pendingProductName}
        onNameChange={onPendingNameChange}
        onAddMore={onAddMoreFiles}
        onRemoveFile={onRemovePendingFile}
        onCancel={onCancelUpload}
        onConfirm={onConfirmUpload}
        uploading={uploadingProduct}
        error={uploadError}
        rubro={rubro}
        onBulkUpload={onBulkUpload}
      />
    ) : (
      <ConfigurePanel
        variant={variant}
        products={selectedProducts}
        imagenBase={imagenBase}
        onAddProducts={onAddProducts}
        kind={
          // PresetDetailSheet nunca renderiza ConfigurePanel para
          // `asistente` (el padre conmuta a AsistenteMarketingSheet). El
          // modo `video-influencer` REUSA el layout de plantilla-video: se
          // pasa como 'template' y la sección Gancho se inyecta vía
          // `showGancho`. El cast es defensivo.
          // `shorts-studio` tampoco pasa por acá (tiene su propia ruta /shorts-studio);
          // el cast lo mapea a 'template' solo para satisfacer el tipo.
          (preset.kind === 'video-influencer' || preset.kind === 'shorts-studio'
            ? 'template'
            : preset.kind || 'template') as Exclude<
            NonNullable<PresetV2['kind']>,
            | 'asistente'
            | 'prompt-libre'
            | 'creador-video'
            | 'video-influencer'
            | 'shorts-studio'
          >
        }
        showGancho={preset.kind === 'video-influencer'}
        ganchoId={ganchoId}
        sinProducto={sinProducto}
        dialogoLoading={dialogoLoading}
        dialogoError={dialogoError}
        onGanchoIdChange={onGanchoIdChange}
        voiceGuion={voiceGuion}
        onVoiceGuionChange={onVoiceGuionChange}
        onVideoDurationChange={onVideoDurationChange}
        acceptsModelo={
          !preset.inputsAceptados || preset.inputsAceptados.includes('modelo')
        }
        modelSelection={modelSelection}
        modelSelectionsSerie={modelSelectionsSerie}
        onToggleModelSerie={onToggleModelSerie}
        fondoVal={fondoVal}
        formatoVal={formatoVal}
        otraConfig={otraConfig}
        stickyGenerate={variant === 'desktop' || fullScreenConfigureMobile}
        // En desktop sólo design-modelo usa el botón Generar flotante
        // arriba a la derecha — ahí escondemos el sticky del panel.
        // design-auto vuelve al layout estándar (Generar sticky al pie
        // del card de Opciones, como en las plantillas).
        // A dos columnas NO hay Generar en esta columna: ni el flotante de arriba a la
        // derecha (caería sobre el panel de productos) ni el sticky del pie. El único
        // Generar es el CTA del panel de la izquierda, que en `configure` deja de decir
        // "Siguiente" — un solo botón de acción por pantalla.
        hideGenerateButton={variant === 'desktop' && isFullScreenConfigure}
        configureDosColumnas={configureDosColumnas}
        creditCost={creditCost}
        lockSlotCount={lockSlotCount}
        onClearProducts={onClearProducts}
        onModelChange={onModelChange}
        modelSelectionsExtra={modelSelectionsExtra}
        onModelChangeExtras={onModelChangeExtras}
        multiModelEnabled={multiModelEnabled}
        onMultiModelEnabledChange={onMultiModelEnabledChange}
        onOpenModelPicker={abrirPickerDeModelo}
        onOpenModelSheet={onOpenModelSheet}
        onOpenModelSheetForMultiSlot={onOpenModelSheetForMultiSlot}
        multiProductsPerSlot={multiProductsPerSlot}
        onTapSlotProducts={onTapSlotProducts}
        onOpenFondoSheet={onOpenFondoSheet}
        onFondoChange={onFondoChange}
        onFormatoChange={onFormatoChange}
        onOpenFormatPicker={onOpenFormatPicker}
        onOtraChange={onOtraChange}
        onGenerate={onGenerate}
        onGenerateProduction={onGenerateProduction}
        productionCreditCost={productionCreditCost}
        autoImageCount={autoImageCount}
        onAutoImageCountChange={onAutoImageCountChange}
        autoImageCountMax={autoImageCountMax}
        userModels={userModels}
        userAuthId={userAuthId}
        userCompanyId={userCompanyId}
        onModelCreated={onModelCreated}
        allPoses={allPoses}
        customPoses={customPoses}
        poseIds={poseIds}
        onPoseIdsChange={onPoseIdsChange}
        vistasProducto={vistasProducto}
        vistaIds={vistaIds}
        onVistaIdsChange={onVistaIdsChange}
        multiPoseEnabled={multiPoseEnabled}
        onOpenPosePicker={onOpenPosePicker}
        onSubirPose={onSubirPose}
        bibliotecaFondos={bibliotecaFondos}
        fondosGuardados={fondosGuardados}
        fondoSelection={fondoSelection}
        onFondoSelectionChange={onFondoSelectionChange}
        onOpenFondoPicker={onOpenFondoPicker}
        fondoView={fondoView}
        onFondoViewChange={onFondoViewChange}
        onFondoSaved={onFondoSaved}
        videoModeEnabled={isVideoModeActive}
        videoDuration={videoDuration}
        videoMultiScene={videoMultiScene}
        videoCreditCostPerImage={videoCreditCostPerImage}
        onOpenVideoConfig={onOpenVideoConfig}
        anuncioTitulo={anuncioTitulo}
        onAnuncioTituloChange={onAnuncioTituloChange}
        anuncioDescripcion={anuncioDescripcion}
        onAnuncioDescripcionChange={onAnuncioDescripcionChange}
        anuncioCta={anuncioCta}
        onAnuncioCtaChange={onAnuncioCtaChange}
        anuncioColors={anuncioColors}
        onAnuncioColorsChange={onAnuncioColorsChange}
        anuncioBrandColors={anuncioBrandColors}
      />
    );
  };

  // El mobile preset sheet se renderiza INLINE (sin portal) — choose-
  // product / configure se quedan debajo de la top bar de la página
  // como cualquier bottom sheet inline. SOLO el pick-product es full-
  // screen sobre la top bar (ese se rendiriza por separado más abajo
  // vía createPortal).
  return (
    <>
      {/* Vista picker fullscreen para el step `select-vistas` del modo
          catalogo-ia. Se monta sobre todo lo demás cuando aplica.
          Renderizamos 2 instancias responsivas — la variant define el
          layout (mobile = full inset, desktop = left: sidebarWidth). */}
      <div className="md:hidden">
        <VistaPickerFullscreen
          open={step === 'select-vistas'}
          variant="mobile"
          sidebarWidth={sidebarWidth}
          allPoses={allPoses}
          customPoses={customPoses}
          onUploadCustomPose={onUploadCustomPose}
          uploadingCustomPose={uploadingCustomPose}
          onDeleteCustomPose={onDeleteCustomPose}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          onChange={onCatalogoVistasChange}
          onBack={onBackToChoose}
          onNext={onConfirmCatalogoVistas}
          variantVistas={
            preset.kind === 'catalogo-muebles' ? 'muebles' : 'catalogo'
          }
          // El Catálogo pasó a ofrecer SÓLO los ángulos del producto: las poses con
          // modelo y las custom salieron del modo, no sólo de la pantalla de desktop.
          // Muebles no entra: su catálogo de vistas es otro (`variantVistas`).
          soloVistasDeProducto={preset.kind === 'catalogo-ia'}
        />
      </div>
      <div className="hidden md:block">
        <VistaPickerFullscreen
          // En `catalogo-ia` este paso dejó de ser un overlay: se dibuja embebido en la
          // columna, al lado del panel de productos. El overlay sigue vivo para muebles
          // (mismo step, otro catálogo de vistas) y para mobile, que no tiene dos
          // columnas donde ponerlo.
          open={step === 'select-vistas' && !catalogoVistasDosColumnas}
          variant="desktop"
          sidebarWidth={sidebarWidth}
          allPoses={allPoses}
          customPoses={customPoses}
          onUploadCustomPose={onUploadCustomPose}
          uploadingCustomPose={uploadingCustomPose}
          onDeleteCustomPose={onDeleteCustomPose}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          onChange={onCatalogoVistasChange}
          onBack={onBackToChoose}
          onNext={onConfirmCatalogoVistas}
          variantVistas={
            preset.kind === 'catalogo-muebles' ? 'muebles' : 'catalogo'
          }
          // El Catálogo pasó a ofrecer SÓLO los ángulos del producto: las poses con
          // modelo y las custom salieron del modo, no sólo de la pantalla de desktop.
          // Muebles no entra: su catálogo de vistas es otro (`variantVistas`).
          soloVistasDeProducto={preset.kind === 'catalogo-ia'}
        />
      </div>

      {/* Cards review fullscreen — paso final del flow catalogo-ia,
          reemplaza al ConfigurePanel cuando el preset es catalogo-ia
          y el step es 'configure'. Una instancia por breakpoint, igual
          que el VistaPicker. */}
      <div className="md:hidden">
        <CatalogoCardsFullscreen
          open={
            step === 'configure' &&
            (preset.kind === 'catalogo-ia' ||
              preset.kind === 'catalogo-muebles' ||
              preset.kind === 'infografia')
          }
          variant="mobile"
          sidebarWidth={sidebarWidth}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          allPoses={allPoses}
          customPoses={customPoses}
          onBack={onBackFromCatalogoConfigure}
          onGenerate={onGenerateCatalog}
          userCompanyId={userCompanyId}
          brandId={brandId}
          rubro={rubro}
          userPlan={userPlan}
          tiposProductoSugeridos={preset.tiposProductoSugeridos}
          onProductsRefreshed={onProductsRefreshed}
          fondoSelection={fondoSelection}
          modelSelection={modelSelection}
          modoMuebles={
            preset.kind === 'catalogo-muebles' || preset.kind === 'infografia'
          }
          soloInfografia={preset.kind === 'infografia'}
          // El Catálogo IA abre la BIBLIOTECA (opciones por categoría:
          // Automático · Añadir fondo · Fondo blanco · Mis fondos · Ciudad…),
          // igual que el picker de Imagen personalizada. Sin esto, en desktop
          // caía en la grilla plana con todos los fondos sueltos ('list'); en
          // mobile ya aterrizaba acá porque el sheet convierte 'list' → 'all'.
          onOpenFondo={() =>
            onOpenFondoSheet(
              usaBibliotecaDeFondos(preset.kind) ? 'all' : 'list',
            )
          }
          onOpenModel={() => onOpenModelSheet('list')}
        />
      </div>
      <div className="hidden md:block">
        <CatalogoCardsFullscreen
          open={
            step === 'configure' &&
            (preset.kind === 'catalogo-ia' ||
              preset.kind === 'catalogo-muebles' ||
              preset.kind === 'infografia')
          }
          variant="desktop"
          sidebarWidth={sidebarWidth}
          selectedProducts={selectedProducts}
          selectedVistas={catalogoVistas}
          allPoses={allPoses}
          customPoses={customPoses}
          onBack={onBackFromCatalogoConfigure}
          onGenerate={onGenerateCatalog}
          userCompanyId={userCompanyId}
          brandId={brandId}
          rubro={rubro}
          userPlan={userPlan}
          tiposProductoSugeridos={preset.tiposProductoSugeridos}
          onProductsRefreshed={onProductsRefreshed}
          fondoSelection={fondoSelection}
          modelSelection={modelSelection}
          modoMuebles={
            preset.kind === 'catalogo-muebles' || preset.kind === 'infografia'
          }
          soloInfografia={preset.kind === 'infografia'}
          // El Catálogo IA abre la BIBLIOTECA (opciones por categoría:
          // Automático · Añadir fondo · Fondo blanco · Mis fondos · Ciudad…),
          // igual que el picker de Imagen personalizada. Sin esto, en desktop
          // caía en la grilla plana con todos los fondos sueltos ('list'); en
          // mobile ya aterrizaba acá porque el sheet convierte 'list' → 'all'.
          onOpenFondo={() =>
            onOpenFondoSheet(
              usaBibliotecaDeFondos(preset.kind) ? 'all' : 'list',
            )
          }
          onOpenModel={() => onOpenModelSheet('list')}
        />
      </div>

      {/* Drawer de alta de producto — se monta sobre la pantalla del modo y vuelve al
          mismo lugar al cerrarse. Sólo existe en el layout apilado del Catálogo, que es
          donde la grilla ofrece la tarjeta que lo abre. */}
      <SubirProductoDrawer
        open={subirProductoAbierto}
        onClose={() => setSubirProductoAbierto(false)}
        userCompanyId={userCompanyId}
        brandId={brandId}
        rubro={rubro}
        userPlan={userPlan}
        onProductsRefreshed={onProductsRefreshed}
      />
      <ModalDeSubirProductos
        modo={subirEnVentana}
        onCerrar={() => setSubirEnVentana(null)}
        companyId={userCompanyId}
        brandId={brandId}
        userPlan={userPlan}
        onProductosNuevos={onProductsRefreshed}
      />
      {/* ── Mobile: SEGUNDA pantalla — Modelo y Pose ─────────────────────────
          Se llega con el "Siguiente" de la pantalla de productos
          (ADR-2026-08-24-modelo-v2-mobile-segunda-pantalla). Full-screen y con el mismo
          pie que las otras dos, con el Generar ya real: es la última parada del flujo.

          Las cards abren los sheets de `InterfazV2`, que en un teléfono es lo correcto —
          el panel lateral de desktop no tiene lugar acá. */}
      {unaColumna &&
        mobilePaso2 &&
        // Por PORTAL a `document.body`, igual que las otras pantallas full-screen de
        // mobile: montada dentro del árbol del sheet quedaba atrapada en su stacking
        // context —el sheet es `z-[101]`— y el `z-[36]` la dejaba DEBAJO, así que el
        // "Siguiente" parecía no hacer nada. En el body, el z vuelve a compararse contra
        // el resto de las capas: por encima de la de productos (35) y por debajo del
        // chrome (50).
        ReactDOM.createPortal(
        <div
          // Al terminar de ENTRAR se cierra el selector de productos que quedó abajo
          // (ADR-2026-09-04-el-confirmar-de-mobile-no-muestra-el-paso-1-al-pasar).
          // `currentTarget`: el evento burbujea desde cualquier animación de adentro, y
          // sólo interesa la de esta pantalla.
          onAnimationEnd={(e) => {
            if (e.target === e.currentTarget) cerrarPickerPendiente();
          }}
          className="md:hidden fixed inset-0 z-[36] bg-white flex flex-col page-slide-in-right"
          style={{
            // SIN `paddingBottom` con el safe-area: el home indicator ya lo respeta
            // `BarraInferiorMobile`, que es lo último de esta pantalla y tiene su propio
            // `env(safe-area-inset-bottom) + 0.75rem`. Puesto también acá, el inset se
            // contaba DOS veces y en PWA standalone —donde vale 34px, contra 0 en el
            // navegador con su barra— quedaban ~80px de blanco debajo del Generar
            // (ADR-2026-09-10-el-safe-area-del-pie-se-cuenta-una-sola-vez).
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
          }}
        >
          {/* SIN "Volver" propio: el chrome flotante dibuja el suyo justo encima de esta
              fila y las dos flechas hacían el mismo movimiento, una arriba de la otra. El
              back de arriba vuelve acá al paso 1 —ver el effect de `mobileBackOverride`—
              (ADR-2026-09-04-modelo-mobile-un-solo-back-en-el-paso-2). */}
          <div className="shrink-0 px-5 pb-4 flex items-center gap-2">
            <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
              {palabra.titulo} y pose
            </h2>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-3">
            {/* Lo elegido en el paso anterior, arriba de todo: sin esto la pantalla 2 no
                decía para qué producto se está eligiendo modelo y pose, y cambiarlo
                obligaba a volver con el botón de atrás
                (ADR-2026-08-24-modelo-v2-mobile-segunda-pantalla).

                Tocarla CIERRA esta pantalla y abre el selector: el de productos se monta
                en su propio portal por debajo de éste (z-35 contra z-36), así que dejar
                las dos abiertas lo escondería detrás. Al volver, el "Siguiente" trae de
                nuevo acá. */}
            <CardProductos
              products={selectedProducts}
              onAbrirProductos={() => {
                setMobilePaso2(false);
                onShowProducts();
              }}
              onSubirArchivos={onStartUpload}
            />
            <CardsModeloPose
              modelSelection={modelSelection}
              userModels={userModels}
              modelosElegidos={modelosElegidos}
              // Para que la card de Modelo muestre a TODOS los elegidos y no sólo al
              // primero (ADR-2026-08-28-la-card-de-modelo-muestra-a-todos). No dibuja la
              // lista de "Qué lleva cada modelo": ésa pide además `onTapProductosModelo`,
              // que en mobile no va —el picker por modelo vive en el panel lateral, que
              // acá no existe—.
              modelosGrupo={modelosGrupo}
              onOpenModelPicker={abrirPickerDeModelo}
              allPoses={allPoses}
              customPoses={customPoses}
              poseIds={poseIds}
              onOpenPosePicker={onOpenPosePicker}
              // Fondo como tercera card, sólo acá: en la columna de desktop ya está en
              // la lista de ajustes. Sigue estando además en el sheet de configuración
              // de mobile, que es de donde salen Calidad, Cantidad y Formato.
              fondoSelection={fondoSelection}
              onOpenFondoPicker={onOpenFondoPicker}
              apiladas
            />
            {/* La instrucción cierra la pantalla, debajo de las tres cards: es lo último
                que se ajusta antes de generar, y en desktop ocupa ese mismo lugar — con
                su "Recientes" incluido, que es de donde sale la mitad de lo que se
                escribe acá (ADR-2026-08-12-historial-instrucciones-reusables). */}
            <div className="relative">
              <textarea
                value={otraConfig}
                onChange={(e) => onOtraChange(e.target.value)}
                placeholder="Agregá indicaciones extras para afinar el resultado"
                rows={3}
                className="w-full rounded-2xl border border-brand-black/10 focus:border-brand-primary/40 focus:outline-none px-4 pt-3 pb-11 text-[14.5px] text-brand-black/90 placeholder:text-brand-black/35 resize-none transition-colors"
              />
              <PromptRecientesButton
                tipo="instruccion"
                onPick={onOtraChange}
                titulo="Instrucciones usadas antes"
                className="absolute bottom-2.5 right-2.5"
              />
            </div>
          </div>
          <BarraInferiorMobile
            calidad={calidad}
            onCalidadChange={setCalidad}
            formatoLabel={getFormatLabel(formatoVal)}
            onOpenFormato={onOpenFormatPicker}
            cantidad={autoImageCount}
            cantidadMax={autoImageCountMax}
            onCantidadChange={onAutoImageCountChange}
            // Con 2+ poses el contador se esconde: la tanda la fija el catálogo de poses
            // (ADR-2026-09-04-con-varias-poses-el-contador-se-esconde).
            contadorVisible={tandaV2.contadorAplica}
            imagenesDeLaTanda={tandaV2.total}
            creditCost={creditCost}
            onGenerate={() => {
              // Esta pantalla NO se cierra acá: se cierra cuando la generación
              // arrancó de verdad (ver el effect de `generacionEnCurso`,
              // ADR-2026-09-04-el-generar-de-mobile-espera-a-que-la-carga-este-arriba).
              // Cerrándola en el tap, durante el chequeo de plan y créditos —que es una
              // ida a la base— se veía el paso 1; y si el chequeo fallaba, el usuario
              // terminaba en el paso 1 con un modal en vez de donde estaba.
              onGenerate(creditCost != null && creditCost > 0 ? creditCost : 1);
            }}
            falta={
              selectedProducts.length === 0
                ? 'Elegí al menos un producto'
                : undefined
            }
          />
        </div>,
        document.body,
      )}
      <div
        className={`md:hidden fixed inset-0 z-[100] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-[101] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out rounded-none ${
          animated ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          // El sheet siempre llega hasta arriba (100dvh) para que el
          // header transparente flote encima sin franja del home
          // detrás. Los distintos steps acomodan su contenido (imagen
          // + panel) usando flex / max-height adentro. NO aplicamos
          // padding-bottom acá — en PWA standalone con viewport-fit=
          // cover queremos que la imagen full-bleed llegue hasta el
          // borde físico. El CTA flotante "Generar con mi producto"
          // ya maneja su propio `env(safe-area-inset-bottom)` para no
          // quedar debajo del home indicator.
          height: '100dvh',
        }}
      >

        {renderedStep === 'preview' ? (
          // ── Preview: scroll vertical con cards una por viewport ──────
          // Cada card es `min-h-full` (toma toda la altura del sheet) y
          // tiene `snap-start`. El onScroll mantiene `previewIndex`
          // sincronizado con la card visible → el footer fijo (afuera
          // del scroll) usa siempre el preset correcto al tapear
          // "Generar con mi producto".
          //
          // En multi-mode, mostramos un strip sticky abajo con las
          // plantillas seleccionadas (fuera del scroll, sibling del
          // mismo contenedor flex-col).
          <div className="flex-1 min-h-0 flex flex-col">
          <div
            ref={mobilePreviewScrollRef}
            className="flex-1 overflow-y-auto snap-y snap-mandatory"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.clientHeight <= 0) return;
              const idx = Math.round(el.scrollTop / el.clientHeight);
              if (idx !== previewIndex && idx >= 0 && idx < previewList.length) {
                setPreviewIndex(idx);
              }
            }}
          >
            {previewList.map((p) => (
              <div
                key={p.id}
                // Cada card ocupa todo el viewport del sheet. La imagen
                // hace full-bleed (object-cover); el footer/check fijos
                // viven afuera del scroll para no moverse al scrollear.
                className="snap-start min-h-full relative overflow-hidden"
              >
                {/* Imagen full-bleed. El button entero detecta tap solo en
                    multi-mode (sino el scroll es la única interacción). */}
                <button
                  type="button"
                  // Solo togglea cards multi-selectable — una pose
                  // multi-persona o una vista solo-producto no se pueden
                  // sumar al set (el payload multi no las soporta).
                  onClick={() =>
                    multiMode &&
                    isPresetMultiSelectable(p) &&
                    toggleMultiId(p.id)
                  }
                  disabled={!multiMode}
                  aria-label={
                    multiMode
                      ? isMultiSelected(p.id)
                        ? `Quitar ${p.title}`
                        : `Seleccionar ${p.title}`
                      : undefined
                  }
                  className="absolute inset-0 disabled:cursor-default"
                >
                  {p.outputType === 'video' ? (
                    <video
                      src={p.imageUrlFull || p.imageUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster={p.imageUrl}
                      className="absolute inset-0 w-full h-full object-cover bg-neutral-100"
                    />
                  ) : (
                    <img
                      src={p.imageUrlFull || p.imageUrl}
                      alt={p.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover bg-neutral-100"
                    />
                  )}
                </button>
              </div>
            ))}
          </div>
          {/* ───── Overlays fijos (no se mueven con el scroll) ───── */}
          {/* Gradient bottom para legibilidad del footer. */}
          <div
            className="absolute bottom-0 inset-x-0 h-44 bg-gradient-to-t from-black/65 via-black/25 to-transparent pointer-events-none z-10"
          />
          {/* Footer fijo. Comportamiento del botón:
              • single mode → "Generar con mi producto" (acción directa).
              • multi-mode + card visible NO seleccionada → "Seleccionar"
                (tap añade la card al set).
              • multi-mode + card visible seleccionada → "Generar (N)"
                (tap dispara handleConfirmMulti) + un pill "Seleccionada"
                arriba del botón como indicador visual.
              Se desliza ~88px arriba cuando el multi-strip aparece. */}
          {(() => {
            const currentPreset = previewList[previewIndex];
            const currentSelected =
              !!currentPreset && isMultiSelected(currentPreset.id);
            return (
              <div
                className="absolute inset-x-5 z-20 transition-[bottom] duration-300 ease-out"
                style={{
                  bottom:
                    multiMode && multiSelectedPresets.length > 0
                      ? 'calc(env(safe-area-inset-bottom, 0px) + 112px)'
                      : 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                }}
              >
                <div className="flex items-center gap-2.5">
                  {isPresetMultiSelectable(currentPreset) && !multiMode && currentPreset && (
                    <button
                      type="button"
                      onClick={() => enterMultiMode(currentPreset.id)}
                      aria-label="Seleccionar varias"
                      className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/95 backdrop-blur-sm border border-white/40 text-brand-black/85 shadow-sm transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (!currentPreset) return;
                      if (!multiMode) {
                        onUtilizarPreset(currentPreset);
                        return;
                      }
                      // Multi-mode: la card visible decide la acción.
                      // Sin seleccionar → añadirla. Ya seleccionada →
                      // confirmar generación con todo el set.
                      // Si la card actual NO es multi-selectable (e.g.
                      // pose multi-persona) y aún no está en el set,
                      // no la sumamos: si ya hay seleccionadas confirma,
                      // sino utiliza ese preset como single.
                      const canAddCurrent =
                        isPresetMultiSelectable(currentPreset);
                      if (!canAddCurrent && !currentSelected) {
                        if (multiSelectedPresets.length > 0) {
                          handleConfirmMulti();
                        } else {
                          onUtilizarPreset(currentPreset);
                        }
                        return;
                      }
                      if (currentSelected) {
                        handleConfirmMulti();
                      } else {
                        toggleMultiId(currentPreset.id);
                      }
                    }}
                    disabled={multiMode && !currentPreset}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-md transition-colors"
                  >
                    {/* Sparkles para "Generar"; Plus para "Seleccionar"
                        — el ícono refleja la acción (añadir vs generar). */}
                    {multiMode &&
                    !currentSelected &&
                    isPresetMultiSelectable(currentPreset) ? (
                      <Plus className="w-5 h-5" strokeWidth={2.5} />
                    ) : (
                      <Sparkles className="w-5 h-5" />
                    )}
                    <span>
                      {!multiMode
                        ? 'Generar con mi producto'
                        : currentSelected
                          ? 'Generar'
                          : isPresetMultiSelectable(currentPreset)
                            ? 'Seleccionar'
                            : multiSelectedPresets.length > 0
                              ? 'Generar'
                              : 'Generar con mi producto'}
                    </span>
                    {/* Badge circular blanco con el conteo — solo cuando
                        el label efectivo es "Generar" en multi-mode con
                        N items. */}
                    {multiMode &&
                      multiSelectedIds.length > 0 &&
                      (currentSelected ||
                        !isPresetMultiSelectable(currentPreset)) && (
                        <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-white text-brand-primary text-[13px] font-bold leading-none tabular-nums">
                          {multiSelectedIds.length}
                        </span>
                      )}
                  </button>
                </div>
              </div>
            );
          })()}
          {/* Strip flotante con las seleccionadas — overlay absolute para
              no empujar la UI ni crear scroll extra. Aparece solo cuando
              hay al menos una seleccionada, con un fade+slide-up sutil
              (animate-mode-swap, 200ms) al primer monte. */}
          {multiMode && multiSelectedPresets.length > 0 && (
            <div
              className="absolute left-3 right-3 z-30 pointer-events-none animate-mode-swap"
              style={{
                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="pointer-events-auto">
                <MultiSelectedStrip
                  selected={multiSelectedPresets}
                  onRemove={toggleMultiId}
                  onCancel={exitMultiMode}
                />
              </div>
            </div>
          )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {/* v2 mobile: el título encabeza la pantalla, arriba de la imagen — el mismo
                que en desktop (ADR-2026-08-23-modelo-v2-mobile). El `pt` deja pasar los
                botones flotantes del header transparente (volver / menú), que en mobile
                se dibujan sobre esta zona. */}
            {unaColumna && step === 'choose-product' && (
              <div
                // `pb-1`: el aire entre la bajada y la imagen lo pone el contenedor de la
                // imagen, que abajo pierde su `pt` en v2 para que los dos bloques se lean
                // como una sola unidad.
                //
                // `mt-auto` acá y `mb-auto` en el panel: entre los dos márgenes
                // automáticos se reparten el alto sobrante y el grupo —título, imagen y
                // card— queda CENTRADO en lo que deja libre la barra del pie, en vez de
                // pegado arriba con un hueco de 250px encima de ella.
                className="mt-auto shrink-0 px-6 pb-4 text-center"
                style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
              >
                <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                  Elegí tus productos, {palabra.singular} y poses
                </h2>
                <p className="text-[14px] text-[#3C3C43]/60 leading-relaxed mt-1">
                  Atlas te arma tu escena en segundos
                </p>
              </div>
            )}
            {/* Imagen del preset arriba — en 'design-modelo' se oculta
                completamente al entrar a 'configure' (el modo tiene
                muchas opciones y la imagen no aporta). En design-modelo
                non-configure usamos fondo blanco + object-contain con
                padding (la portada del modo se ve más prolija así, no
                full-bleed como en templates de moda — el `contain` es
                además lo que deja entrar entera la portada 3:2 de
                ADR-2026-08-04-modelo-portada-unica). */}
            <div
              className={`transition-all duration-300 ease-out overflow-hidden ${
                fullScreenConfigureMobile && step === 'configure'
                  ? 'h-0 opacity-0'
                  : smallCenteredPreview
                    ? `bg-white flex items-center justify-center opacity-100 ${
                        // v2 mobile: el contenedor mide LO QUE LA IMAGEN (`shrink-0`) en
                        // vez de estirarse a `flex-1`. Estirado, sobraba alto y el
                        // `items-center` lo repartía arriba y abajo: ~75px entre la bajada
                        // y la foto que ningún padding podía cerrar. El alto que sobra se
                        // lo queda el panel de abajo, que es donde se opera.
                        unaColumna
                          ? // `mb-auto` cierra el grupo centrado que abre el encabezado
                            // (ver su `mt-auto`): el alto sobrante se reparte entre el
                            // techo y el hueco imagen↔card, en vez de amontonarse debajo
                            // de la card y dejarla flotando lejos de la barra del pie.
                            'shrink-0 mb-auto px-6 pb-5 pt-0'
                          : 'flex-1 min-h-0 p-6'
                      }`
                    : 'relative bg-neutral-100 flex-1 min-h-0 opacity-100'
              }`}
            >
              {smallCenteredPreview ? (
                heroCollage ? (
                  <ProductHeroCollage
                    original={heroCollage.original}
                    results={heroCollage.results}
                    layout={heroCollage.layout}
                    className="max-w-[320px]"
                  />
                ) : (
                  <img
                    src={preset.imageUrlFull || preset.imageUrl}
                    alt={preset.title}
                    // `max-h-full` mantiene el contain de design-modelo/auto
                    // (landscape). Para catalogo-ia (portrait) el max-w
                    // limita el ancho — y como hay `flex items-center`,
                    // queda visualmente similar al resto.
                    className={`w-full h-auto object-contain ${
                      unaColumna
                        ? 'max-w-[280px] max-h-[32dvh]'
                        : 'max-w-[300px] max-h-full'
                    }`}
                  />
                )
              ) : isMultiPreset ? (
                <MultiPresetCarousel presets={multiPresets} fit="cover" />
              ) : preset.outputType === 'video' ? (
                <video
                  src={preset.imageUrlFull || preset.imageUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster={preset.imageUrl}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <img
                  src={preset.imageUrlFull || preset.imageUrl}
                  alt={preset.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
            </div>

            {/* La altura del panel adentro del preset sheet cambia según
                el step:
                • choose-product               → 55dvh (panel chico)
                • upload-product · configure   → 78dvh (más espacio para
                                                   previews + form / opts)
                • design-modelo                → ocupa todo el sheet
                pick-product NO afecta este panel — ese caso se maneja con
                un sheet aparte (portal) sobre la top bar. */}
            <div
              // El panel arranca con max-height: 0 (panelOpen=false en
              // el primer render post-mount) y en el siguiente frame
              // se anima a su valor final. Esto cambia el flex-space
              // disponible para la imagen (flex-1 arriba) → la imagen
              // anima su altura sin script extra. design-modelo +
              // configure usa flex-1 sin max-height (panel ocupa
              // todo).
              className={`relative z-10 bg-white flex flex-col overflow-hidden transition-all duration-300 ease-out ${
                fullScreenConfigureMobile && step === 'configure'
                  ? 'flex-1 min-h-0'
                  : 'shrink-0 -mt-6 rounded-t-3xl'
              }`}
              style={{
                maxHeight:
                  fullScreenConfigureMobile && step === 'configure'
                    ? undefined
                    : !panelOpen
                      ? '0px'
                      : step === 'configure' || step === 'upload-product'
                        ? '78dvh'
                        : '55dvh',
                transition: 'max-height 300ms ease-out',
              }}
            >
              <div
                key={step}
                // En design-modelo + configure el panel ocupa todo el
                // sheet (sin imagen del preset arriba), así que el
                // contenido arranca pegado al borde superior y los
                // botones flotantes (back / menu, del header
                // transparente) tapan el primer chip. Agregamos
                // padding superior + safe-area-top en ese caso para
                // que el primer elemento quede debajo de los botones.
                //
                // pb-6 default — pero en design-flow + configure lo
                // sacamos porque el ConfigurePanel renderiza un Generar
                // sticky con su propio padding interno; cualquier pb
                // del wrapper deja un gap visible entre el botón y el
                // borde inferior.
                className={`${swapAnimClass} flex-1 overflow-y-auto px-5 ${
                  fullScreenConfigureMobile && step === 'configure'
                    ? ''
                    : step === 'choose-product'
                      ? // Con `BarraInferiorMobile` en flujo, la card de productos se
                        // baja al pie de la columna (`mt-auto` en su wrapper) en vez de
                        // quedar colgada arriba con todo el alto libre debajo. Para que
                        // ese `mt-auto` tenga contra qué empujar, el scroller tiene que
                        // ser una columna flex — ver
                        // ADR-2026-09-04-modelo-mobile-la-card-de-productos-baja-al-pie.
                        `flex flex-col ${barraEnFlujo ? 'pb-6' : ''}`
                      : 'pb-6'
                }`}
                style={{
                  paddingTop:
                    fullScreenConfigureMobile && step === 'configure'
                      ? 'calc(env(safe-area-inset-top, 0px) + 5rem)'
                      : '1.75rem', // pt-7 default
                  // En el primer paso del modo la barra inferior sigue a la vista
                  // (ADR-2026-08-04-el-modo-se-lleva-la-barra) y se dibuja ENCIMA de
                  // este panel: el shell la monta afuera del contenido, con un z que
                  // le gana. Sin este hueco, "Elegí de mis productos" —el segundo
                  // CTA— queda medio tapado. Son los 24px de `pb-6` más lo que ocupa
                  // la barra.
                  //
                  // NO cuando la barra es `BarraInferiorMobile`: ésa se monta como
                  // hermana de este scroller, en el flujo, así que no tapa nada y el
                  // hueco quedaba como 64px de aire muerto entre la card y el pie.
                  paddingBottom:
                    step === 'choose-product' && !barraEnFlujo
                      ? `calc(1.5rem + ${ALTO_NAV_INFERIOR})`
                      : undefined,
                }}
              >
                {renderStep('mobile')}
              </div>
            </div>
            {/* v2 mobile: los ajustes y el Generar, fijos al pie
                (ADR-2026-08-23-modelo-v2-mobile).

                Fuera del scroll del panel —son la acción de la pantalla, no parte de la
                lista que se recorre— y adentro del sheet, para que el `safe-area` del
                home indicator lo maneje esta caja y no el contenido. Reemplazan a la
                barra de navegación, que en esta pantalla se aparta. */}
            {/* SIN productos elegidos la barra no se dibuja —
                ADR-2026-09-04-en-mobile-el-siguiente-aparece-con-el-primer-producto.

                En el paso 1 la barra es sólo el CTA y su nota (`soloCta`), y sin nada
                elegido ese CTA está apagado: un botón grande que no responde, con un
                cartel debajo repitiendo lo que la card de arriba ya pide. Al elegir el
                primer producto aparece, y al VOLVER desde "Modelo y pose" también está —
                ahí ya hay productos—, que es lo que hace falta para seguir hacia
                adelante. */}
            {unaColumna &&
              step === 'choose-product' &&
              selectedProducts.length > 0 && (
              <BarraInferiorMobile
                calidad={calidad}
                onCalidadChange={setCalidad}
                formatoLabel={getFormatLabel(formatoVal)}
                onOpenFormato={onOpenFormatPicker}
                cantidad={autoImageCount}
                cantidadMax={autoImageCountMax}
                onCantidadChange={onAutoImageCountChange}
                contadorVisible={tandaV2.contadorAplica}
                imagenesDeLaTanda={tandaV2.total}
                creditCost={creditCost}
                // "Siguiente" y no "Generar": el paso que sigue es Modelo y pose, que es
                // donde vive el Generar real. Acá el CTA generaba, así que al volver
                // desde esa pantalla el pie decía "Generar 3 imágenes" y no quedaba
                // ningún camino de vuelta hacia adelante — la única forma de volver a
                // Modelo y pose era pasar otra vez por el selector de productos
                // (ADR-2026-09-03-el-paso-1-de-modelo-mobile-siempre-lleva-a-modelo-y-pose).
                ctaLabel="Siguiente"
                ocultarCosto
                // Sólo el botón y su nota: Calidad, Formato y el contador aparecen en el
                // paso 2 (ADR-2026-09-04-los-ajustes-de-mobile-aparecen-en-el-paso-2).
                soloCta
                onGenerate={() => setMobilePaso2(true)}
                falta={
                  selectedProducts.length === 0
                    ? 'Elegí al menos un producto'
                    : undefined
                }
              />
            )}
          </div>
        )}
      </div>

      {/* ── Mobile-only: pick-product como PANTALLA completa ── */}
      {/* Cuando step === 'pick-product' en mobile, renderizamos una pantalla
          full-screen (no un bottom sheet) a través de portal a document.body.
          Va POR DEBAJO del chrome del Sidebar (z-50) — z-[35] queda encima
          del contenido de la página (shell z-30) pero deja visibles y
          clickeables los botones de navegación flotantes (volver + menú). Por
          eso el contenido arranca debajo de la top bar (padding-top =
          safe-area + 3.5rem, la altura del header mobile) y ocultamos el link
          "Volver" interno del panel (`hideBack`): el back lo maneja la top bar
          vía `mobileBackOverride`. Usamos la variante `desktop` para tener el
          mismo layout que en escritorio: header + grilla + botón grande de
          Confirmar al pie. La animación `page-slide-in-right` le da el feel de
          "navegar a otra pantalla". */}
      {step === 'pick-product' &&
        ReactDOM.createPortal(
          <div
            className="md:hidden fixed inset-0 z-[35] bg-white flex flex-col page-slide-in-right"
            style={{
              // Sin safe-area abajo: lo pone `BarraInferiorMobile`, igual que en el paso 2
              // (ADR-2026-09-10-el-safe-area-del-pie-se-cuenta-una-sola-vez).
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
            }}
          >
            {/* El panel arma su layout sobre un `h-full`, así que sin un contenedor
                acotado se lleva TODO el alto del portal y deja a la barra del pie fuera
                de la pantalla. Mismo `[&>div]:` que en el panel lateral de desktop: le da
                al raíz del panel el `flex-1 min-h-0` que su `h-full` no le da. */}
            <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">
            <PickProductPanelModelo
              variant="desktop"
              hideBack
              // En mobile el alto es poco: el header "Elegí los productos" va
              // dentro del scroll para que se vaya con las cards y quede solo
              // el buscador pegado arriba.
              scrollHeader
              onConfirm={onConfirmProducts}
              onBack={onBackToChoose}
              initialSelectedIds={selectedProducts.map((p) => p.id)}
              tiposProductoSugeridos={preset.tiposProductoSugeridos}
              rubro={rubro}
              singleSelect={singleProductMode}
              title={
                preset.kind === 'video-influencer'
                  ? 'Elegí tu producto'
                  : undefined
              }
              // v2: la MISMA grilla que el panel lateral de desktop —tres por fila, la
              // tarjeta sin marco y el nombre sobre la foto—. Las dos pantallas muestran
              // los mismos productos; que se vieran distinto era una diferencia sin
              // motivo. Ver ADR-2026-08-24-modelo-v2-grilla-consistente.
              //
              // `hideFooter` + `onSelectionChange`: el "Confirmar" propio del panel se va
              // y lo reemplaza la barra de abajo, la misma del paso 1
              // (ADR-2026-08-24-modelo-v2-mobile-barra-en-las-dos-pantallas). Con los dos
              // habría dos CTA compitiendo al pie. La selección viaja en vivo, que es lo
              // que ese `hideFooter` pide a cambio.
              {...(unaColumna
                ? {
                    // La pantalla de mobile va de a DOS, no con las tres del panel
                    // lateral de desktop — ver `COLUMNAS_PRODUCTOS_MOBILE`.
                    columnasFijas: COLUMNAS_PRODUCTOS_MOBILE,
                    nombreSobreLaFoto: true,
                    ocultarBloqueSubida: true,
                    onSubirProducto: () => setSubirEnVentana('uno'),
                    onSubirVarios: () => setSubirEnVentana('varios'),
                    hideFooter: true,
                    onSelectionChange: onProductosEnVivo ?? onConfirmProducts,
                  }
                : {})}
            />
            </div>
            {unaColumna && (
              <BarraInferiorMobile
                calidad={calidad}
                onCalidadChange={setCalidad}
                formatoLabel={getFormatLabel(formatoVal)}
                onOpenFormato={onOpenFormatPicker}
                cantidad={autoImageCount}
                cantidadMax={autoImageCountMax}
                onCantidadChange={onAutoImageCountChange}
                contadorVisible={tandaV2.contadorAplica}
                imagenesDeLaTanda={tandaV2.total}
                creditCost={creditCost}
                // Acá el CTA NO genera: cierra la pantalla y devuelve al paso donde están
                // Modelo, Pose y los ajustes, que es lo que falta elegir después del
                // producto (ADR-2026-08-24-modelo-v2-mobile-modelo-y-pose). Generar desde
                // la grilla se saltaba esas tres decisiones sin decirlo.
                //
                // "Confirmar" y no "Siguiente": esta pantalla es el SELECTOR —se entra a
                // elegir productos y se sale con lo elegido—, y el botón cierra esa
                // elección. El "Siguiente" queda para el pie del paso 1, que sí avanza a
                // otra pantalla del flujo.
                ctaLabel="Confirmar"
                ocultarCosto
                // Igual que el paso 1: acá se está eligiendo QUÉ generar, así que la
                // barra es sólo el botón y su nota
                // (ADR-2026-09-04-los-ajustes-de-mobile-aparecen-en-el-paso-2). Los tres
                // controles encima de la grilla se comían el tercio de abajo de la
                // pantalla justo donde hay que mirar los productos.
                soloCta
                onGenerate={() => {
                  // El orden importa: primero se abre el paso 2 y recién cuando terminó
                  // de entrar se cierra este selector (ver `salirDelPickerRef`).
                  salirDelPickerRef.current = onBackToChoose;
                  setMobilePaso2(true);
                  // Red por si la animación no llega a disparar `animationend` —
                  // `prefers-reduced-motion`, una pestaña en segundo plano—: sin esto el
                  // selector quedaría montado (invisible, pero el "atrás" volvería a él).
                  window.setTimeout(cerrarPickerPendiente, 400);
                }}
                falta={
                  selectedProducts.length === 0
                    ? 'Elegí al menos un producto'
                    : undefined
                }
              />
            )}
          </div>,
          document.body,
        )}

      {/* ── Mobile-only: upload-product como PANTALLA completa ── */}
      {/* Mismo patrón que pick-product: pantalla full-screen por debajo del
          chrome (z-[35] < z-50), contenido debajo de la top bar y sin link
          "Volver" interno (el back lo da la top bar vía mobileBackOverride,
          que en upload-product vuelve a choose-product). Variante `desktop`
          para el layout header + scroll + footer con Confirmar. */}
      {step === 'upload-product' &&
        ReactDOM.createPortal(
          <div
            className="md:hidden fixed inset-0 z-[35] bg-[#F9F9F9] flex flex-col page-slide-in-right"
            style={{
              paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <UploadProductPanel
              variant="desktop"
              hideBack
              files={pendingProductFiles}
              name={pendingProductName}
              onNameChange={onPendingNameChange}
              onAddMore={onAddMoreFiles}
              onRemoveFile={onRemovePendingFile}
              onCancel={onCancelUpload}
              onConfirm={onConfirmUpload}
              uploading={uploadingProduct}
              error={uploadError}
              rubro={rubro}
              onBulkUpload={onBulkUpload}
            />
          </div>,
          document.body,
        )}

      {/* ── Desktop: layout tipo "página" con dos cards ─────────── */}
      {/* En vez del overlay edge-to-edge, simulamos una página normal:
          fondo gris del shell (igual a `EditarImagen`), padding alrededor,
          y dos cards blancas (imagen + opciones) flotando una al lado de
          la otra. El botón "Volver" vive en un header arriba de las
          cards, no dentro de ellas, para que ambos se sientan como
          contenedores parejos. */}
      <div
        // El marco flotante sale de `PantallaChrome`, compartido con el asistente y
        // los pickers full-screen (ADR-2026-08-03-chrome-de-pantalla-compartido):
        // retocarlo acá y no en los otros tres era exactamente cómo se separaban.
        // Los modos-sección van sin superficie ni borde: se apoyan en el lienzo del
        // shell, como `/plantillas` (ADR-2026-08-03-anuncio-se-ve-como-seccion). El
        // resto de los modos conserva el marco flotante.
        //
        // El aire de arriba de una sección del shell son 72px —32px del wrapper de
        // contenido + 40px de la fila del título—, que en `/plantillas` salen de dos
        // reglas de `.shell-seccion` que a este panel no lo alcanzan (las dos excluyen
        // a los hijos `fixed`). Se reparten en 52px de padding del PANEL y los 20px que
        // el encabezado ya trae… salvo en el paso que scrollea
        // (ADR-2026-08-04-el-encabezado-pegado-se-lleva-el-aire): ahí los 72px van
        // ENTEROS al encabezado.
        //
        // El motivo hoy es que el aire de arriba tiene que scrollear con el contenido:
        // el padding del panel queda FUERA del scroller, así que dejaría una franja
        // muerta arriba —el contenido cortado 52px abajo del borde— apenas se scrollea.
        // Adentro del encabezado, esos 72px se van con el resto. En los pasos que NO
        // scrollean el reparto viejo queda igual: ahí el panel no scrollea y el padding
        // no molesta a nadie.
        //
        // `lg:!right-0` hace que el panel mida EXACTAMENTE lo que `<main>`, que es de
        // lo que depende `COLUMNA_SECCION`: la columna es un 90% centrado, así que si
        // el panel midiera 8px menos (el marco flotante) su 5% daría distinto y la
        // columna del modo quedaría corrida contra la de `/inicio`. Se puede sacar
        // porque en la presentación de sección el marco no se ve.
        //
        // Todo con `lg:`: entre 768 y 1024px esta pantalla se monta en
        // `DashboardLayout`, que tiene su propia geometría.
        className={`hidden md:flex fixed inset-y-0 right-0 ${
          cederZIndex ? 'z-30' : 'z-[100]'
        } rounded-tl-[26px] rounded-bl-[26px] ${
          esSeccion
            ? `${contenidoScrollea ? '' : 'lg:pt-[52px]'} lg:!right-0`
            : `bg-surface-content ${BORDE_PANTALLA}`
        } ${MARCO_PANTALLA} flex-col transition-all duration-300 ease-out ${
          animated ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.99]'
        } ${
          // El panel NUNCA es el scroller — ADR-2026-08-04-el-encabezado-vive-fuera-del-scroller.
          // En el paso `configure` lo era y el scroll se mudó al wrapper de abajo,
          // donde sigue: el panel es `fixed` CON `transform` (la animación de entrada),
          // y eso hace fragil su propio `overflow`.
          //
          // `overscroll-contain` va de la mano de `overflow-hidden`: éste SÍ es un
          // contenedor de scroll (scrolleable por código, sin barra), así que sin eso
          // la rueda encadena al `<main>` de atrás y la barra de créditos —que asoma
          // arriba— se iría scrolleando sola.
          contenidoScrollea
            ? 'overflow-hidden overscroll-contain'
            : // En los modos-sección el panel NO scrollea: mide lo que la ventana y el
              // scroll pasa a vivir adentro del contenido, que ya lo tiene
              // (ADR-2026-08-03-anuncio-panel-no-scrollea). `overscroll-contain` va de
              // la mano: `overflow-hidden` SÍ es un contenedor de scroll (scrolleable
              // por código, sin barra), así que sin esto la rueda encadena al `<main>`
              // de atrás y la barra de créditos —que ahora asoma arriba— se iría
              // scrolleando sola.
              esSeccion
              ? 'overflow-hidden overscroll-contain'
              : ''
        }`}
        style={{
          left: sidebarWidth,
          // El panel arranca DEBAJO de la barra de créditos del shell en vez de
          // taparla (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra). La
          // variable la publica `EstiloMinimal` midiendo la barra; vale 0 cuando no
          // está, y no existe abajo de 1024px (shell viejo), donde el `0px` de
          // respaldo es exactamente el `inset-y-0` que la clase ya pone.
          ...(esSeccion ? { top: 'var(--shell-barra-creditos, 0px)' } : null),
          // Estos modos no llevan superficie propia, pero tampoco pueden ser
          // transparentes: el panel se monta ENCIMA del contenido del shell y sin
          // fondo el breadcrumb y el título quedaban dibujados sobre lo que pasa por
          // debajo (ADR-2026-08-03-regla-del-titulo-no-toca-paneles-fixed). El color
          // es el del lienzo, así que se sigue leyendo como sección y no como
          // tarjeta. La variable la define `.shell-seccion`; el fallback es el lienzo
          // del shell viejo, donde esta pantalla se monta entre 768 y 1024px.
          ...(esSeccion
            ? { background: 'var(--plantillas-sticky-bg, #FBFBFD)' }
            : null),
        }}
      >
        {/* Lo que SCROLLEA — ADR-2026-08-04-el-encabezado-vive-fuera-del-scroller,
            revisado por ADR-2026-08-04-el-breadcrumb-no-se-pega.
            En el paso `configure` el scroll no es del panel sino de este wrapper (el
            panel es `fixed` CON `transform` por la animación de entrada, y ésa es
            justo la combinación que rompe el scroll interno en varios motores). El
            encabezado va ADENTRO: es contenido que scrollea como cualquier otro, no
            una franja pegada arriba.

            En los pasos que no scrollean acá es `display: contents`, o sea que el
            wrapper desaparece del layout y sus hijos siguen siendo hijos directos del
            panel, exactamente como antes. */}
        <div
          className={
            contenidoScrollea
              ? 'flex flex-1 min-h-0 flex-col overflow-y-auto scrollbar-hide'
              : 'contents'
          }
        >

        {/* Encabezado compartido (ADR-2026-08-03-chrome-de-pantalla-compartido):
            pone el breadcrumb —o el "Volver" de respaldo, para las pantallas que no
            son un modo—. Las acciones de la derecha (toggle Imagen/Video, pill de
            Generar) siguen siendo de esta pantalla y van como children.

            Con `cabeceraEnColumna` este de acá no se dibuja: el breadcrumb baja a la
            columna de la imagen (ADR-2026-08-05-la-cabecera-de-seccion-baja-a-su-columna),
            y lo pinta `cabeceraSeccion` más abajo. Los children —el toggle
            Imagen/Video— son del paso `preview`, que nunca cae en ese caso. */}
        {!cabeceraEnColumna && !modoConDisenoNuevo && (
        <EncabezadoPantalla
          migas={migas}
          // En la presentación de sección el encabezado se alinea con el CONTENIDO de
          // abajo (`px-8` de la fila) y no con el `px-6` del resto de los modos: el
          // título de una sección arranca donde arranca lo que titula. Antes era
          // `!px-0` —el borde del panel— porque el panel no estaba quedando `fixed` y
          // su borde ERA la columna del shell
          // (ADR-2026-08-03-regla-del-titulo-no-toca-paneles-fixed); ya de vuelta en
          // su lugar, eso deja el título pegado al canto, a 8px de la sidebar.
          // Va con `!` porque pelea contra el `px-6` propio del componente, y quién
          // gana sin eso depende del orden en la hoja de estilos.
          // `lg:!pb-4` son los 16px que `/plantillas` deja entre el breadcrumb y el
          // `<h1>` (el `mb-4` de la variante `seccion` de `Migas`); el `pb-3` propio
          // del encabezado dejaba 12.
          // `lg:!pt-[72px]` en el paso que scrollea: los 52px que dejó de poner el panel
          // más los 20px propios del encabezado
          // (ADR-2026-08-04-el-encabezado-pegado-se-lleva-el-aire). El aire total arriba
          // del breadcrumb no cambia, y como ahora el encabezado vive DENTRO del
          // scroller, esa franja se va con el scroll en vez de quedar como un borde
          // muerto arriba del contenido.
          className={
            // Dentro de la columna izquierda (ADR-2026-08-05-la-cabecera-de-seccion-baja-a-su-columna)
            // el ancho y el margen los pone la columna: acá sólo queda el aire propio.
            cabeceraEnColumna
              ? '!px-0 !pt-0 lg:!pb-4'
              : esSeccion
                ? `!px-8 lg:!pb-4 ${contenidoScrollea ? 'lg:!pt-[72px]' : ''} ${COLUMNA_SECCION}`
                : ''
          }
          respaldo={
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver
            </button>
          }
        >
          {/* Toggle Imagen/Video centrado en el header — solo en el step
              'preview', cuando hay carousel de plantillas para elegir. En
              otros steps (choose-product, configure, etc.) ya se decidió
              qué se va a generar, así que el toggle no aplica. */}
          {step === 'preview' && (
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 inline-grid grid-cols-2 p-0.5 rounded-full bg-white shadow-sm ring-1 ring-black/[0.06] pointer-events-auto"
            >
              <div
                aria-hidden
                className="absolute top-0.5 bottom-0.5 left-0.5 right-1/2 rounded-full bg-brand-primary transition-transform duration-300 ease-out"
                style={{
                  transform:
                    previewMediaTab === 'imagen'
                      ? 'translateX(0)'
                      : 'translateX(100%)',
                }}
              />
              <button
                type="button"
                onClick={() => onPreviewMediaTabChange('imagen')}
                className={`relative z-10 inline-flex items-center justify-center px-5 h-8 rounded-full text-[14px] font-semibold transition-colors duration-300 ${
                  previewMediaTab === 'imagen'
                    ? 'text-white'
                    : 'text-brand-black/65'
                }`}
              >
                Imagen
              </button>
              <button
                type="button"
                onClick={() => onPreviewMediaTabChange('video')}
                className={`relative z-10 inline-flex items-center justify-center px-5 h-8 rounded-full text-[14px] font-semibold transition-colors duration-300 ${
                  previewMediaTab === 'video'
                    ? 'text-white'
                    : 'text-brand-black/65'
                }`}
              >
                Video
              </button>
            </div>
          )}
        </EncabezadoPantalla>
        )}

        {/* Título de la pantalla — sólo en la presentación de sección
            (ADR-2026-08-03-anuncio-se-ve-como-seccion). Mismo tamaño y peso que el
            `<h1>` de `/plantillas`, y el mismo `px-8` que el breadcrumb de arriba y
            que la fila de abajo: los tres arrancan en la misma línea.
            El breadcrumb NO se duplica: lo sigue poniendo `EncabezadoPantalla`, que
            arriba de todo queda ya sólo con eso. */}
        {esSeccion && (
          // Acciones del paso `configure`, a la derecha y a la altura del título —
          // ADR-2026-08-04-las-acciones-si-se-pegan-el-breadcrumb-no.
          //
          // Se PEGAN arriba (`sticky`) para que "Generar" siga a mano con la pantalla
          // scrolleada; el breadcrumb, en cambio, se va con el contenido
          // (ADR-2026-08-04-el-breadcrumb-no-se-pega). El sticky funciona acá porque el
          // scroller es este wrapper —un div común— y no el panel `fixed` con
          // `transform`, que es donde no aguantaba
          // (ADR-2026-08-04-el-encabezado-vive-fuera-del-scroller).
          //
          // No necesita fondo: los dos controles son opacos (pastilla blanca + botón
          // azul) y las tarjetas pasan por debajo sin cruzarse con ningún texto suelto.
          //
          // La fila mide CERO y su contenido desborda hacia abajo (`h-0`): así se
          // superpone a la fila del título sin empujarla ni un pixel. Poner el `<h1>`
          // adentro de esta fila sería lo natural, pero la haría el contenedor directo
          // de un `<h1>` dentro de `.shell-seccion`, y esa regla —la que le da 40px de
          // aire a las filas de título— le caería encima con `!important`, corriendo el
          // título hacia abajo en TODOS los modos-sección. El `<h1>` sigue colgando del
          // panel, que la regla excluye por ser `fixed`.
          <div
            // `top-4`: 16px de aire contra el borde de arriba del panel una vez pegados.
            // Sólo en el paso que scrollea — en los otros no hay scroll que seguir y
            // `sticky` no cambiaría nada, pero tampoco tiene por qué estar.
            className={`${
              contenidoScrollea ? 'sticky top-4 z-20' : 'relative z-10'
            } flex h-0 items-start justify-end px-8 ${COLUMNA_SECCION}`}
          >
            {/* Generar flotante (pill arriba a la derecha) — para los modos
                full-screen (design-modelo / anuncio / video-influencer).
                design-auto renderiza el Generar pegado al bottom del card de
                Opciones vía ConfigurePanel. En video-influencer, cuando falta
                algo (gancho / voz / guion) el pill se atenúa, muestra el hint
                debajo (como el sticky de mobile) y al clickearlo scrollea
                hasta el componente faltante y lo sacude (ADR-552). */}
            {configureFullScreen && (() => {
              // Qué falta en el modo influencer — misma prioridad que el hint
              // del ConfigurePanel (gancho → modelo → voz → guion) + id DOM del
              // target para el scroll+shake. Los ids existen SOLO en el render
              // desktop (prop `domIds`, evita duplicados con el panel mobile).
              const influencerBlock = faltaEnInfluencer;
              // "Fotos de producto" sin ninguna vista elegida: mismo tratamiento
              // que el influencer sin gancho — el pill se atenúa y al tocarlo
              // scrollea a la grilla y la sacude. El id existe solo en el render
              // DESKTOP del panel (el mobile usa su propio sufijo).
              const bloqueoGenerar =
                preset.kind === 'fotos-producto'
                  ? vistaIds.length === 0
                    ? {
                        msg: 'Elegí al menos una vista para generar.',
                        targetId: 'vistas-producto-grid-desktop',
                      }
                    : null
                  : influencerBlock;
              const handleGenerateClick = () => {
                if (bloqueoGenerar) {
                  // Scroll suave + sacudida fuerte al terminar (ADR-553).
                  scrollToAndShake(bloqueoGenerar.targetId);
                  return;
                }
                onGenerate(creditCost != null && creditCost > 0 ? creditCost : 1);
              };
              const generateButton = (
                <button
                  type="button"
                  onClick={handleGenerateClick}
                  aria-disabled={!!bloqueoGenerar}
                  className={`inline-flex items-center gap-2 py-2.5 pl-5 pr-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors pointer-events-auto ${
                    bloqueoGenerar ? 'opacity-60' : ''
                  }`}
                >
                  {preset.kind === 'video-influencer' ? (
                    <Play className="w-4 h-4" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  <span>
                    {preset.kind === 'video-influencer'
                      ? 'Generar video'
                      : 'Generar'}
                  </span>
                  {creditCost != null && creditCost > 0 && (
                    <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
                      <img
                        src="/images/token_icon.png"
                        alt=""
                        aria-hidden="true"
                        className="w-3.5 h-3.5 object-contain"
                      />
                      {creditCost}
                    </span>
                  )}
                </button>
              );
              // Bloqueado: card blanca EXACTAMENTE del ancho del botón (sin
              // marco extra alrededor del pill) que se extiende por detrás
              // hacia abajo con el hint. Siempre montada y colapsada con
              // transición de opacity/max-height para aparecer/desaparecer
              // suave. Durante el fade-out se muestra el último mensaje
              // (ref) para que el texto no desaparezca de golpe.
              if (bloqueoGenerar) {
                lastInfluencerHintRef.current = bloqueoGenerar.msg;
              }
              const generateBlock = (
                <div className="relative inline-flex flex-col items-stretch pointer-events-none">
                  {/* Capa card — mismo radio que el pill (22px), pegada a sus
                      bordes; solo se ve la parte que asoma abajo. */}
                  <div
                    aria-hidden
                    className={`absolute inset-0 rounded-[22px] bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] transition-opacity duration-300 ${
                      bloqueoGenerar ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <div className="relative z-10">{generateButton}</div>
                  {/* Hint — `w-0 min-w-full` lo clava al ancho del botón (no
                      ensancha la card); colapsa con max-height. */}
                  <div
                    className={`relative z-10 w-0 min-w-full overflow-hidden transition-all duration-300 ease-out ${
                      bloqueoGenerar ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="pt-2 pb-1.5 px-3 text-center text-[12.5px] leading-snug text-[#3C3C43]/70">
                      {bloqueoGenerar?.msg ?? lastInfluencerHintRef.current}
                    </p>
                  </div>
                </div>
              );
              // Sin contador (otros modos full-screen): el Generar va solo, como antes.
              // En design-modelo el contador va SIEMPRE: en v2 la cantidad multiplica
              // aunque haya varias poses o varios modelos
              // (ADR-2026-08-28-modelo-v2-la-cantidad-manda).
              if (!esDesignModelo(preset.kind)) {
                return generateBlock;
              }
              // Contador "cantidad de imágenes" a la IZQUIERDA del Generar —
              // píldora blanca con el mismo lenguaje que el botón "Volver"
              // (bg-white/85 + backdrop-blur + ring). `items-start` para que
              // quede alineado al pill aunque el hint se despliegue abajo.
              const n = tandaV2.count;
              return (
                <div className="inline-flex items-start gap-2">
                  <div className="inline-flex items-center gap-1 p-1.5 rounded-full bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] pointer-events-auto">
                    <button
                      type="button"
                      onClick={() => onAutoImageCountChange(Math.max(1, n - 1))}
                      disabled={n <= 1}
                      aria-label="Generar una imagen menos"
                      className="w-7 h-7 inline-flex items-center justify-center rounded-full text-brand-black/70 hover:bg-black/[0.05] hover:text-brand-primary disabled:opacity-35 disabled:pointer-events-none transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="min-w-[76px] text-center text-[13.5px] font-semibold text-brand-black/85 tabular-nums">
                      {n} {n === 1 ? 'imagen' : 'imágenes'}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onAutoImageCountChange(Math.min(autoImageCountMax, n + 1))
                      }
                      disabled={n >= autoImageCountMax}
                      aria-label="Generar una imagen más"
                      className="w-7 h-7 inline-flex items-center justify-center rounded-full text-brand-black/70 hover:bg-black/[0.05] hover:text-brand-primary disabled:opacity-35 disabled:pointer-events-none transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {generateBlock}
                </div>
              );
            })()}
          </div>
        )}

        {esSeccion && !cabeceraEnColumna && !modoConDisenoNuevo && (
          <h1
            className={`shrink-0 px-8 pb-4 text-[32px] font-semibold leading-tight text-brand-black/90 ${COLUMNA_SECCION}`}
          >
            {tituloSeccion}
          </h1>
        )}

        {step === 'preview' ? (
          // ── Desktop preview: carousel transicional ────────────────────
          // Cards absolute positioned con transform basado en (i - index).
          // Flechas a los costados de la imagen central (absolute dentro
          // del carousel area, vertical centerd). Soporte de wheel/swipe
          // horizontal para navegar — listener attached via useEffect.
          //
          // El container es `relative` para que el MultiSelectedStrip
          // pueda flotar absoluto en el bottom sin empujar la UI. Usamos
          // pb-28 SIEMPRE (no condicional) — con justify-center, ese
          // padding sube la columna a una posición donde queda lugar
          // para el strip flotante, y al aparecer/desaparecer el strip
          // la UI no se mueve.
          <div className="flex-1 px-6 pb-20 flex flex-col gap-5 min-h-0 justify-center items-center relative">
            {/* Toggle Imagen/Video vive ahora en el `<header>` arriba de
                este container — para mantener la altura del carousel +
                title intactas y reusar el espacio que ya queda libre
                arriba al lado de "Volver". */}
            <div
              ref={carouselRef}
              className="relative w-full flex-1 min-h-0 max-h-[72dvh] flex items-center justify-center overflow-hidden"
            >
              {previewList.map((p, i) => {
                const rel = i - previewIndex;
                if (Math.abs(rel) > 2) return null;
                const isCenter = rel === 0;
                // ±1 mantiene los valores anteriores. ±2 va al lado de
                // ±1 con MISMO tamaño (scale 0.42) y separación similar
                // a la que ±1 tiene con la central. Pueden quedar
                // parcialmente clippeadas por el overflow-hidden del
                // container — eso da el efecto "peek" sin overflow hacia
                // la sidebar.
                const absRel = Math.abs(rel);
                const translateX =
                  Math.sign(rel) * (absRel === 2 ? 712 : absRel === 1 ? 470 : 0);
                const scale = absRel === 0 ? 1 : 0.42;
                const opacity = absRel === 2 ? 0.4 : absRel === 1 ? 0.5 : 1;
                // Click en cualquier card LATERAL = saltar a esa (la
                // pone como centro). Click en la CENTRAL = utilizar en
                // single mode, o toggle selección en multi-mode.
                const onClick = !isCenter
                  ? () => setPreviewIndex(i)
                  : multiMode
                    ? // Igual que en mobile: solo las cards multi-selectable
                      // togglean; el resto no hace nada en multi-mode.
                      () => isPresetMultiSelectable(p) && toggleMultiId(p.id)
                    : () => onUtilizarPreset(p);
                const ariaLabel = !isCenter
                  ? `Ir a ${p.title}`
                  : multiMode
                    ? `${isMultiSelected(p.id) ? 'Quitar' : 'Seleccionar'} ${p.title}`
                    : `Utilizar ${p.title}`;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={onClick}
                    aria-label={ariaLabel}
                    className="absolute h-full max-h-[72dvh] aspect-[3/4] rounded-2xl overflow-hidden bg-neutral-100 ring-1 ring-black/[0.04] shadow-md transition-all duration-500 ease-out"
                    style={{
                      transform: `translateX(${translateX}px) scale(${scale})`,
                      opacity,
                      zIndex: 10 - absRel,
                      // Todas las visibles son clickeables — incluso las
                      // de rel=2 que asoman parcialmente, así el user
                      // puede tocarlas para saltar a esa plantilla.
                      pointerEvents: 'auto',
                      cursor: 'pointer',
                    }}
                  >
                    {p.outputType === 'video' ? (
                      <video
                        src={isCenter ? p.imageUrlFull || p.imageUrl : p.imageUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        poster={p.imageUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        // Centro usa full (se ve grande); laterales usan
                        // opt porque están escaladas a ~0.42 y el opt
                        // alcanza para esa resolución.
                        src={isCenter ? p.imageUrlFull || p.imageUrl : p.imageUrl}
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {multiMode && isPresetMultiSelectable(p) && (
                      <span
                        className={`absolute top-3 right-3 w-8 h-8 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white transition-colors ${
                          isMultiSelected(p.id)
                            ? 'bg-brand-primary text-white'
                            : 'bg-white/85 backdrop-blur-sm text-transparent'
                        }`}
                      >
                        <Check className="w-4 h-4" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}

              {/* Flechas — absolute al costado de la imagen central. La
                  central tiene 580px de ancho DOM (aspect 3/4 scaled 1),
                  centrada en el container. Posicionamos las flechas a
                  ~340px del centro para que queden a los lados de la
                  imagen, encima de las laterales (z-20). */}
              {/* Vertical center sin transform: inset-y-0 + my-auto.
                  Antes usábamos `top-1/2 -translate-y-1/2` y el transform
                  parece que en algunos browsers se "resetea" en :active
                  o interfiere con el hit testing (el user reportaba que
                  el botón "se movía para abajo" al presionarlo). Sin
                  transform, la posición es 100% estática.

                  Wrapper más grande con padding para hit area cómoda;
                  visualmente el círculo es 44×44 pero el target táctil
                  es ~56×56. */}
              <button
                type="button"
                onClick={() => canPrev && setPreviewIndex((i) => i - 1)}
                disabled={!canPrev}
                aria-label="Anterior"
                style={{ left: 'calc(50% - 346px)' }}
                className="absolute inset-y-0 my-auto h-11 z-20 inline-flex items-center justify-center p-1.5 box-content rounded-full hover:bg-neutral-50/0 disabled:cursor-not-allowed transition-colors"
              >
                <span
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-brand-black/70 shadow-md ring-1 ring-black/[0.04] transition-colors ${
                    canPrev ? 'hover:bg-neutral-50' : 'opacity-30'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </span>
              </button>
              <button
                type="button"
                onClick={() => canNext && setPreviewIndex((i) => i + 1)}
                disabled={!canNext}
                aria-label="Siguiente"
                style={{ right: 'calc(50% - 346px)' }}
                className="absolute inset-y-0 my-auto h-11 z-20 inline-flex items-center justify-center p-1.5 box-content rounded-full disabled:cursor-not-allowed transition-colors"
              >
                <span
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-brand-black/70 shadow-md ring-1 ring-black/[0.04] transition-colors ${
                    canNext ? 'hover:bg-neutral-50' : 'opacity-30'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </span>
              </button>
            </div>

            {/* Title row debajo, sin flechas ahora (viven arriba al lado
                de la imagen). El title se anima con animate-title-slide
                en cada cambio de previewIndex. */}
            <div className="w-[580px] max-w-full flex items-center justify-between gap-2.5">
              <p
                key={desktopPreviewPreset.id}
                className="text-[19px] font-semibold text-brand-black/90 truncate min-w-0 flex-1 animate-title-slide"
              >
                {desktopPreviewPreset.title}
              </p>
              {/* "Seleccionar varias" pill (ícono Copy) — solo cuando NO
                  estamos en multi mode y el preset actual lo admite. Una
                  vez en multi mode, no hay botón separado para entrar/
                  salir — el botón principal de abajo absorbe la lógica
                  (mismo patrón que en mobile). */}
              {!multiMode &&
                isPresetMultiSelectable(desktopPreviewPreset) && (
                  <button
                    type="button"
                    onClick={() => enterMultiMode(desktopPreviewPreset.id)}
                    aria-label="Seleccionar varias"
                    className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              {/* Botón de acción único — mismo patrón que mobile:
                  • !multiMode → "Generar con mi producto" (utiliza single)
                  • multiMode + currentSelected → "Generar (N)" (confirma)
                  • multiMode + !currentSelected + multi-selectable →
                    "Seleccionar" (añade al set)
                  • multiMode + !multi-selectable → confirma N si hay,
                    sino cae a "Generar con mi producto" single. */}
              <button
                type="button"
                onClick={() => {
                  if (!multiMode) {
                    onUtilizarPreset(desktopPreviewPreset);
                    return;
                  }
                  const canAdd = isPresetMultiSelectable(desktopPreviewPreset);
                  const isSel = isMultiSelected(desktopPreviewPreset.id);
                  if (!canAdd && !isSel) {
                    if (multiSelectedPresets.length > 0) handleConfirmMulti();
                    else onUtilizarPreset(desktopPreviewPreset);
                    return;
                  }
                  if (isSel) handleConfirmMulti();
                  else toggleMultiId(desktopPreviewPreset.id);
                }}
                disabled={multiMode && multiSelectedIds.length === 0 &&
                  !isPresetMultiSelectable(desktopPreviewPreset)}
                className={`shrink-0 inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-md transition-colors ${
                  multiMode &&
                  isMultiSelected(desktopPreviewPreset.id) &&
                  multiSelectedIds.length > 0
                    ? 'pl-5 pr-3'
                    : 'px-6'
                }`}
              >
                {multiMode &&
                !isMultiSelected(desktopPreviewPreset.id) &&
                isPresetMultiSelectable(desktopPreviewPreset) ? (
                  <Plus className="w-5 h-5" strokeWidth={2.5} />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
                <span>
                  {!multiMode
                    ? 'Generar con mi producto'
                    : isMultiSelected(desktopPreviewPreset.id)
                      ? 'Generar'
                      : isPresetMultiSelectable(desktopPreviewPreset)
                        ? 'Seleccionar'
                        : multiSelectedPresets.length > 0
                          ? 'Generar'
                          : 'Generar con mi producto'}
                </span>
                {/* Badge circular blanco con el conteo — solo cuando el
                    label efectivo es "Generar" en multi-mode con N items. */}
                {multiMode &&
                  multiSelectedIds.length > 0 &&
                  (isMultiSelected(desktopPreviewPreset.id) ||
                    !isPresetMultiSelectable(desktopPreviewPreset)) && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-white text-brand-primary text-[13px] font-bold leading-none tabular-nums">
                      {multiSelectedIds.length}
                    </span>
                  )}
              </button>
            </div>
            {multiMode && multiSelectedPresets.length > 0 && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-4 z-30 w-[640px] max-w-[calc(100%-3rem)] pointer-events-none">
                <div className="pointer-events-auto">
                  <MultiSelectedStrip
                    selected={multiSelectedPresets}
                    onRemove={toggleMultiId}
                    onCancel={exitMultiMode}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          // ── Desktop flujo normal: dos cards (imagen + opciones) ───────
          // En el modo 'design-modelo' a partir del step 'configure'
          // ocultamos la imagen y dejamos solo el aside, centrado y más
          // amplio — son muchas opciones (Modelo + Pose + Fondo + Formato)
          // y la imagen de la izquierda no aporta. La transición es vía
          // `transition-all` sobre width + opacity de la imagen.
          <div
            ref={areaDosColumnasRef}
            className={`relative px-8 pb-6 flex gap-6 ${
              // Con el panel a un ancho PROPIO, `justify-center` dejaba las dos columnas
              // como un bloque centrado que se corría a la derecha en cada monitor más
              // ancho. Arrancan pegadas al borde IZQUIERDO de la fila —que ya está
              // centrada por `COLUMNA_SECCION`—, así que el panel queda un poco más a la
              // izquierda y sobre todo SIEMPRE en el mismo lugar
              // (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660).
              //
              // `justify-end` y no `justify-start`: estos modos van con
              // `flex-row-reverse`, así que el eje está dado vuelta y el "final" es el
              // borde izquierdo de la pantalla.
              modoConDisenoNuevo ? 'justify-end' : 'justify-center'
            } ${
              // El panel de productos va a la IZQUIERDA. Se invierte con
              // `flex-row-reverse` y no reordenando el JSX porque el orden del DOM es el
              // que importa para el foco y los lectores de pantalla: primero la
              // presentación del modo, después el panel con el que se opera.
              //
              // Vale para TODOS los pasos del rediseño, no sólo para el de elegir
              // producto: el panel se queda quieto de un paso al siguiente, que es
              // justamente lo que hace que `configure` se lea como un avance sobre la
              // misma pantalla y no como una pantalla nueva.
              modoConDisenoNuevo ? 'flex-row-reverse' : 'flex-row'
            } ${
              // La MISMA clase con la muestra del modo y con las tandas, a propósito:
              // que cambiara entre un estado y el otro es lo que movía el panel al
              // generar (ADR-2026-09-06-la-columna-de-resultados-ocupa-la-misma-caja).
              // Los modos del rediseño arrancan a 52px de la sidebar —el mismo aire que
              // tienen arriba— y llegan al borde derecho
              // (ADR-2026-09-06-la-fila-del-modo-arranca-a-52px-y-llega-al-borde); los
              // otros modos-sección siguen con el 90% centrado, que es lo que alinea su
              // encabezado cruzado con el resto del shell.
              esSeccion
                ? modoConDisenoNuevo
                  ? FILA_MODO_REDISENO
                  : COLUMNA_SECCION
                : ''
            } ${
              configureFullScreen
                ? 'items-start'
                : 'flex-1 min-h-0 items-stretch'
            }`}
          >
            {smallCenteredPreview && !configureFullScreen ? (
              // Layout especial para todos los steps de design-modelo /
              // design-auto / catalogo-ia que muestran la imagen
              // (choose-product, pick-product, upload-product): imagen
              // chica + texto explicativo JUSTO debajo, todo centrado.
              // El card crece a flex-1 para igualar el alto del sidebar.
              // En 'configure' el card se oculta entero (otra rama).
              <div
                // Cuando esta columna es la que sostiene la CONFIGURACIÓN —el paso 2 a dos
                // columnas, y Automático, donde la config vive acá y no en el aside—, mide
                // lo mismo que el panel en el resto de los pasos: es el mismo panel
                // (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660). Mostrando
                // la muestra del modo o las tandas sigue tomando el ancho que sobra.
                // …salvo con las TANDAS: ahí no sostiene la configuración sino una
                // grilla de resultados, que crece con lo que haya. Fijada en 660 dejaba
                // ~280px del monitor en blanco a su derecha y una card menos por fila
                // (ADR-2026-09-04-la-columna-de-resultados-toma-el-ancho-que-sobra). Es
                // el mismo arreglo que ya tiene el otro fork del sheet: este modo pasa
                // por acá y por eso le faltaba.
                style={
                  columnaConfiguracion
                    ? undefined
                    : pasoDosColumnas
                      ? { flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }
                      : undefined
                }
                className={`min-w-0 flex flex-col ${
                  pasoDosColumnas && !columnaConfiguracion
                    ? 'grow-0 shrink-0'
                    : 'flex-1'
                } ${
                  // Sólo con las TANDAS: la columna se sale de los paddings y llega al
                  // marco arriba, abajo y a la derecha
                  // (ADR-2026-09-06-la-columna-de-resultados-llega-al-marco). Con la
                  // muestra del modo o con la configuración adentro se queda en su caja
                  // —lo que se está mirando ahí es una sola pieza centrada, no una
                  // grilla que gana con el ancho—.
                  columnaResultadosAlBorde ? COLUMNA_RESULTADOS_AL_BORDE : ''
                }`}
              >
                {/* La cabecera vive ACÁ, no cruzando la pantalla
                    (ADR-2026-08-05-la-cabecera-de-seccion-baja-a-su-columna): el
                    breadcrumb, el título y la imagen son una sola presentación del modo y
                    ocupan su mitad, mientras la selección de productos usa la altura
                    entera de la suya. */}
                {cabeceraEnColumna && (
                  <>
                    <EncabezadoPantalla
                      migas={migas}
                      className="!px-0 !pt-0 lg:!pb-4"
                      respaldo={
                        <button
                          type="button"
                          onClick={onClose}
                          className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Volver
                        </button>
                      }
                    />
                    <h1 className="shrink-0 pb-4 text-[32px] font-semibold leading-tight text-brand-black/90">
                      {tituloSeccion}
                    </h1>
                  </>
                )}
                {/* `columnaConfiguracion` gana SIEMPRE que venga, no sólo en el paso 2:
                    en automático no hay paso 2 y las tandas tienen que ocupar igual esta
                    columna, que es donde estaba la muestra del modo
                    (ADR-2026-08-21-automatico-con-el-diseno-nuevo). En los otros modos no
                    cambia nada: ahí sólo llega con el paso 2 abierto. */}
                {/* La columna sigue mostrando la MUESTRA del modo hasta que haya algo
                    generado — supersede el "queda vacía" de
                    ADR-2026-08-21-modelo-v2-una-sola-columna; ver
                    ADR-2026-08-21-modelo-v2-la-muestra-se-queda. Un lienzo en blanco
                    dejaba media pantalla sin decir nada durante todo el rato en que el
                    usuario todavía no generó, que es justo cuando le falta saber qué hace
                    el modo. Las tandas la reemplazan en cuanto existen. */}
                {pasoDosColumnas || columnaConfiguracion ? (
                  // La configuración ocupa el lugar de la muestra. Scrollea por su
                  // cuenta (`overflow-y-auto`): el wrapper de afuera ya no es el
                  // scroller —`contenidoScrollea` es false a dos columnas— y sin esto la
                  // columna crecería más que la pantalla.
                  //
                  // El picker de vistas es la excepción: scrollea ADENTRO (su caja tiene
                  // las pills pegadas arriba), así que acá sólo se le da el alto.
                  <div
                    key="paso-configurar"
                    className={`flex-1 min-h-0 ${
                      catalogoVistasDosColumnas ? 'flex flex-col' : 'overflow-y-auto'
                    } ${
                      // El aire que `COLUMNA_RESULTADOS_AL_BORDE` sacó afuera vuelve acá
                      // adentro, donde scrollea con las cards en vez de ser una franja
                      // fija arriba.
                      columnaResultadosAlBorde ? COLUMNA_RESULTADOS_AIRE_INTERNO : ''
                    } ${pasoAnimClass}`}
                  >
                    {catalogoVistasDosColumnas ? (
                      <VistaPickerFullscreen
                        open
                        embebido
                        variant="desktop"
                        sidebarWidth={sidebarWidth}
                        allPoses={allPoses}
                        customPoses={customPoses}
                        onUploadCustomPose={onUploadCustomPose}
                        uploadingCustomPose={uploadingCustomPose}
                        onDeleteCustomPose={onDeleteCustomPose}
                        selectedProducts={selectedProducts}
                        selectedVistas={catalogoVistas}
                        onChange={onCatalogoVistasChange}
                        onBack={onBackToChoose}
                        // El CTA vive en el panel de al lado; el picker embebido no
                        // dibuja el suyo, pero la prop es obligatoria.
                        onNext={onConfirmCatalogoVistas}
                        soloVistasDeProducto
                      />
                    ) : (
                      columnaConfiguracion ?? renderStep('desktop')
                    )}
                  </div>
                ) : (
                <div
                  key="paso-presentacion"
                  className={`flex-1 min-h-0 flex flex-col items-center justify-center gap-2 ${
                    modoConDisenoNuevo ? pasoAnimClass : ''
                  }`}
                >
                {/* v2: el texto ENCABEZA la muestra en vez de ir de pie de foto — el
                    pedido es que se lea como el título de la pantalla. Va arriba, en
                    negrita, con la bajada abajo; el resto de los modos conserva su
                    párrafo debajo de la imagen (la otra rama, más abajo).
                    Ver ADR-2026-08-21-modelo-v2-la-muestra-se-queda. */}
                {unaColumna && (
                  <div className="shrink-0 text-center max-w-[440px] mb-6">
                    <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                      Elegí tus productos, {palabra.singular} y poses
                    </h2>
                    <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                      Atlas te arma tu escena en segundos
                    </p>
                  </div>
                )}
                {heroCollage ? (
                  <ProductHeroCollage
                    original={heroCollage.original}
                    results={heroCollage.results}
                    layout={heroCollage.layout}
                  />
                ) : (
                  <img
                    src={preset.imageUrlFull || preset.imageUrl}
                    alt={preset.title}
                    // `max-h-[360px]` evita que un asset portrait (como el
                    // hero de catalogo-ia) se vea desproporcionalmente alto
                    // comparado con los landscape de los otros modos. El hero
                    // de anuncio es un collage que se ve mejor un poco más
                    // chico, así que sólo ese baja de tamaño en desktop.
                    className={`${
                      disenoElegirProducto
                        ? // Más grande que en el resto de los modos (que están topeados
                          // en 480px): es lo que explica qué hace el Catálogo y el pedido
                          // fue darle protagonismo. El ancho de la columna la limita
                          // antes que este tope en pantallas chicas.
                          'max-w-[560px] max-h-[480px]'
                        : preset.kind === 'anuncio'
                          ? 'max-w-[380px] max-h-[330px]'
                          : unaColumna
                            ? // Modelo: un punto más grande que el resto. Es un collage
                              // (prenda + fondo + cara → resultado) y a 480px las cuatro
                              // piezas de la izquierda se leen chicas.
                              'max-w-[560px] max-h-[480px]'
                            : 'max-w-[480px] max-h-[420px]'
                    } w-full h-auto object-contain`}
                  />
                )}
                <p
                  className={`text-[#3C3C43]/60 leading-relaxed text-center text-balance ${
                    // En v2 el texto se mudó ARRIBA de la imagen: acá se esconde en vez
                    // de sacarlo del árbol para no tocar el resto de la cadena de kinds,
                    // que sigue sirviendo a los otros ocho modos.
                    unaColumna
                      ? 'hidden'
                      : disenoElegirProducto
                      ? 'text-[15.5px] max-w-[440px] mt-1'
                      : 'text-[12.5px] max-w-[360px]'
                  }`}
                >
                  {preset.kind === 'design-auto'
                    ? 'Atlas elige la escena por vos. Subí tu producto y la IA arma la imagen final en segundos — sin configurar nada.'
                    : // Bajadas CORTAS de los modos con el rediseño — la muestra pasó a
                      // ocupar la columna entera y el párrafo largo competía con ella.
                      // Una por modo: la mecánica que explican no es la misma.
                      preset.kind === 'catalogo-ia'
                      ? 'Elegí vistas y poses — Atlas genera una imagen por cada combinación.'
                      : esDesignModelo(preset.kind)
                        ? `Elegí ${palabra.singular}, pose y fondo — Atlas arma la escena en segundos.`
                        : esCatalogoIA(preset.kind)
                      ? 'Generá photoshoots de catálogo para cada producto en un solo proceso. Elegí qué vistas y poses querés generar — Atlas arma una imagen por cada combinación.'
                      : preset.kind === 'catalogo-muebles'
                        ? 'Subí tu producto y Atlas arma el catálogo completo: packshot, foto en contexto e infografía con medidas — todo en un solo proceso.'
                        : preset.kind === 'infografia'
                          ? 'Subí tu producto y Atlas genera la infografía con sus medidas y características, lista para tu ficha.'
                          : preset.kind === 'anuncio'
                            ? 'Armá un anuncio listo para publicar — texto, colores y formato definidos por vos o por Atlas.'
                            : preset.kind === 'video-influencer'
                              ? preset.description
                              : preset.kind === 'fotos-producto'
                                ? `Fotos de tu producto solo, sin ${palabra.singular}. Elegí desde qué vistas querés verlo y si va sobre fondo blanco o en una escena.`
                                : `Generá una imagen de tu producto sobre un ${palabra.singular} de IA. Elegí el ${palabra.singular}, la pose y el fondo — Atlas arma la escena en segundos.`}
                </p>
                </div>
                )}
              </div>
            ) : (
              <div
                className={`relative bg-white rounded-2xl overflow-hidden shadow-sm ring-1 ring-black/[0.04] ${
                  configureFullScreen
                    ? 'hidden'
                    : isMultiPreset
                      ? 'flex-1'
                      : preset.kind === 'template'
                        ? // Plantillas: preview en 4:5. Estira al alto
                          // disponible (items-stretch del row) y el ancho
                          // queda dado por la relación 4:5.
                          'shrink-0 aspect-[4/5] max-w-[60%]'
                        : 'shrink-0 max-w-[60%]'
                }`}
              >
                {isMultiPreset ? (
                  <MultiPresetCarousel presets={multiPresets} fit="cover" />
                ) : preset.outputType === 'video' ? (
                  <video
                    src={preset.imageUrlFull || preset.imageUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster={preset.imageUrl}
                    className={
                      preset.kind === 'template'
                        ? 'absolute inset-0 w-full h-full object-cover'
                        : 'block h-full w-auto max-w-full object-cover'
                    }
                  />
                ) : (
                  <img
                    src={preset.imageUrlFull || preset.imageUrl}
                    alt={preset.title}
                    className={
                      preset.kind === 'template'
                        ? 'absolute inset-0 w-full h-full object-cover'
                        : 'block h-full w-auto max-w-full object-cover'
                    }
                  />
                )}
              </div>
            )}
            <aside
              // El ancho, en `style` y no en clases: es el mismo valor para el panel
              // entero (`ANCHO_PANEL_CONFIG`) y lo comparten las dos ramas que lo dibujan.
              style={
                configureFullScreen
                  ? { maxWidth: ANCHO_PANEL_CONFIG }
                  : panelComprimido
                    ? undefined
                    : modoConDisenoNuevo
                      ? { flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }
                      : undefined
              }
              // Lo mide el panel lateral flotante para saber dónde arrancar sin taparlo
              // — ver `offsetPanelLateral`.
              ref={panelConfigRef}
              className={`shrink-0 flex flex-col ${
                configureFullScreen
                  ? 'w-full bg-transparent'
                  : `bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden ${
                      panelComprimido
                        ? // Paso 2: el panel se comprime a una preview y le cede el ancho
                          // a la configuración. Ancho FIJO en px y no un porcentaje: lo
                          // que tiene que entrar son dos miniaturas y las filas de
                          // ajustes, que miden lo mismo en cualquier pantalla — con un
                          // porcentaje, en un monitor grande la preview se agrandaba sin
                          // mostrar nada más.
                          'basis-[280px] grow-0'
                        : modoConDisenoNuevo
                          ? // El ancho de `ANCHO_PANEL_CONFIG` (el `style` de arriba), el
                            // mismo en todos los pasos y en los tres modos. Antes era
                            // `basis-[35%]`: un porcentaje del CONTENEDOR, así que el panel
                            // medía distinto en cada paso según lo que hubiera al lado.
                            'grow-0 shrink-0'
                          : 'flex-1'
                    }`
              }`}
            >
              {unaColumna && eligiendoProductos ? (
                // PASO 1: la grilla de productos, desplegada, ocupa la columna — el mismo
                // panel (y el mismo "Siguiente") con el que Catálogo y Fotos de producto
                // abren los suyos. El panel trae su propia cabecera, scroller y pie, así
                // que este wrapper sólo le da el alto.
                <div className="flex-1 min-h-0 flex flex-col">
                  {renderStep('desktop', 'choose-product')}
                </div>
              ) : unaColumna ? (
                // PASO 2: la columna es la CONFIGURACIÓN entera, no la grilla de productos
                // (ADR-2026-08-21-modelo-v2-una-sola-columna). La grilla vive en el panel
                // lateral que abre la card de productos de acá adentro.
                //
                // Scrollea por su cuenta: el contenido crece con lo elegido (las
                // miniaturas de los productos se apilan) y el wrapper de afuera no es el
                // scroller en el layout de dos columnas.
                <>
                {/* El título de la pantalla, FUERA del scroller: antes lo dibujaba el
                    panel de productos (llegaba como su prop `cabecera`) y al sacar ese
                    panel de la columna se fue con él. Acá arriba y fijo, que es donde
                    estaba: es el nombre del modo, no un encabezado de la lista que
                    scrollea debajo. */}
                {cabeceraEnPanel}
                {/* El scroll dejó de vivir acá: lo maneja `PanelCompacto`, que scrollea
                    sólo su mitad de arriba y deja los ajustes y el Generar fijos al pie
                    (ADR-2026-08-31-modelo-v2-ajustes-y-generar-fijos-abajo). Este wrapper
                    ya sólo pone el alto y los márgenes. */}
                <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-2">
                  <PanelCompacto
                    products={selectedProducts}
                    onAbrirProductos={() =>
                      setPanelLateral((p) => (p === 'productos' ? null : 'productos'))
                    }
                    onSubirArchivos={onStartUpload}
                    modelSelection={modelSelection}
                    modelosElegidos={modelosElegidos}
                    // Quién lleva qué: sólo se dibuja con más de un modelo. El índice de
                    // cada fila es el del slot en `modelo_config`, así que el picker le
                    // asigna los productos al modelo correcto.
                    modelosGrupo={modelosGrupo}
                    productosPorModelo={multiProductsPerSlot}
                    // Al panel lateral de la derecha, NO al sheet full-screen que usa el
                    // resto del modo: acá la fila que se tocó queda a la vista mientras
                    // se elige, que es lo que deja entender de quién es el outfit que se
                    // está armando (ADR-2026-08-25-que-lleva-cada-modelo-en-el-panel).
                    // Interruptor, como las otras cards de la columna.
                    onTapProductosModelo={(idx) => {
                      // Los dos `set` afuera del updater: llamar a un setter DENTRO del
                      // updater de otro no es puro y en StrictMode corre dos veces.
                      const mismo =
                        panelLateral === 'productos-modelo' &&
                        slotProductosLateral === idx;
                      setSlotProductosLateral(mismo ? null : idx);
                      setPanelLateral(mismo ? null : 'productos-modelo');
                    }}
                    // Al panel lateral, NO al sheet de `InterfazV2`: el sheet se monta a
                    // pantalla completa y tapa la columna desde la que se lo abrió. La
                    // card es un INTERRUPTOR: el mismo tap que lo abre lo cierra
                    // (ADR-2026-08-24-la-card-abre-y-cierra-su-panel).
                    onOpenModelPicker={() =>
                      setPanelLateral((p) => {
                        if (p === 'modelo') return null;
                        setModelViewLateral('list');
                        return 'modelo';
                      })
                    }
                    userModels={userModels}
                    allPoses={allPoses}
                    customPoses={customPoses}
                    poseIds={poseIds}
                    onOpenPosePicker={() =>
                      setPanelLateral((p) => (p === 'pose' ? null : 'pose'))
                    }
                    calidad={calidad}
                    onCalidadChange={setCalidad}
                    cantidad={autoImageCount}
                    cantidadMax={autoImageCountMax}
                    onCantidadChange={onAutoImageCountChange}
                    fondoSelection={fondoSelection}
                    // Al panel lateral, NO al sheet: el sheet de fondos en desktop
                    // muestra la grilla plana con TODOS los fondos sueltos, mientras que
                    // el picker inline los agrupa por CATEGORÍA —que es como se ven en
                    // mobile y lo que se pidió. Ver
                    // ADR-2026-08-24-modelo-v2-fondos-por-categoria.
                    onOpenFondo={() =>
                      setPanelLateral((p) => (p === 'fondo' ? null : 'fondo'))
                    }
                    // Moodboard, sólo para las marcas que los ven (Moda), como el tab de
                    // Mi marca: al panel lateral, igual que el resto.
                    {...(veMoodboards
                      ? {
                          moodboard,
                          onOpenMoodboard: () =>
                            setPanelLateral((p) => (p === 'moodboard' ? null : 'moodboard')),
                        }
                      : {})}
                    formatoLabel={getFormatLabel(formatoVal)}
                    // Al panel lateral, igual que modelo, pose y fondo: el sheet de
                    // formato se monta a pantalla completa y tapaba la columna desde la
                    // que se lo abría (ADR-2026-08-24-formato-en-el-panel-lateral).
                    onOpenFormato={() =>
                      setPanelLateral((p) => (p === 'formato' ? null : 'formato'))
                    }
                    otraConfig={otraConfig}
                    onOtraChange={onOtraChange}
                    // El botón dice el TOTAL real de la tanda, que es el mismo número
                    // que cobra el pill de créditos: los dos salen de `tandaV2`.
                    cantidadImagenes={tandaV2.total}
                    // Con 2+ poses el contador se esconde: la tanda la fija el catálogo
                    // de poses (ADR-2026-09-04-con-varias-poses-el-contador-se-esconde).
                    contadorVisible={tandaV2.contadorAplica}
                    creditCost={creditCost}
                    onGenerate={() => {
                      // El panel lateral se cierra al generar: la tanda aparece JUSTO
                      // debajo de donde está abierto —ocupa esa misma área— y dejarlo
                      // tapaba lo que el usuario acaba de pedir.
                      setPanelLateral(null);
                      onGenerate(
                        creditCost != null && creditCost > 0 ? creditCost : 1,
                      );
                    }}
                    // El orden de las faltas sigue al de la columna: primero el producto
                    // —lo primero que se pide— y recién después modelo y pose.
                    falta={
                      selectedProducts.length === 0
                        ? 'Elegí al menos un producto.'
                        : modelosElegidos === 0
                          ? `Elegí al menos un ${palabra.singular}.`
                          : undefined
                    }
                  />
                </div>
                </>
              ) : pasoDosColumnas ? (
                // El panel de productos NO se va al pasar de paso: se queda igual
                // que en el paso anterior —misma grilla, mismos ajustes, mismo pie— y lo
                // único que cambia es la columna de al lado. Su "Siguiente" pasa a
                // re-confirmar: si acá se agrega o saca un producto, se vuelve a aplicar
                // sobre la configuración que ya está abierta.
                <div className="flex-1 min-h-0 flex flex-col">
                  {renderStep('desktop', 'choose-product')}
                </div>
              ) : step === 'pick-product' ||
              step === 'upload-product' ||
              step === 'choose-product' ? (
                // Estos paneles manejan su propio layout (header / scroll
                // / footer fijo). NO les metemos padding ni overflow al
                // wrapper — sería doble scroll y el footer no se vería
                // sticky. Incluye el choose-product FUSIONADO de desktop
                // (PickProductPanel con header de upload), tanto en modos de
                // diseño como en plantillas.
                <div key={step} className={`${swapAnimClass} flex-1 min-h-0 flex flex-col`}>
                  {renderStep('desktop')}
                </div>
              ) : (
                // Resto de los steps: wrapper con padding + overflow-y
                // por si el contenido es más alto. `justify-center` solo
                // en choose-product (contenido corto, queda mejor centrado).
                // En design-modelo configure NO scrolleamos internamente
                // — la ventana entera es la scrolleable.
                <div
                  key={step}
                  // pb-6 default — pero en design-flow + configure lo
                  // sacamos para que el Generar sticky del ConfigurePanel
                  // llegue al borde del aside sin gap (mismo fix que en
                  // mobile). El modo 'video-influencer' (que reusa este panel
                  // como plantilla-video con Generar sticky) también lo saca:
                  // sino el `pb-6` deja una franja vacía debajo del botón. ADR-505.
                  className={`${swapAnimClass} px-6 pt-6 flex flex-col ${
                    fullScreenConfigureMobile && step === 'configure'
                      ? ''
                      : 'pb-6'
                  } ${
                    configureFullScreen ? '' : 'flex-1 min-h-0 overflow-y-auto'
                  } ${step === 'choose-product' ? 'justify-center' : ''}`}
                >
                  {renderStep('desktop')}
                </div>
              )}
            </aside>
            {/* El panel lateral de productos — ADR-2026-08-21-modelo-v2-una-sola-columna.
                Vive DENTRO del contenedor de las dos columnas para poder dejar libre la
                de configuración: se abre sobre el área de la derecha, que es la que está
                vacía (o con las tandas), y lo de abajo sigue respondiendo. */}
            {unaColumna && (
              <ProductosLateral
                abierto={panelLateral !== null}
                onCerrar={() => {
                  setPanelLateral(null);
                  setSubiendoProductoEnPanel(false);
                }}
                titulo={
                  subiendoProductoEnPanel
                    ? 'Subir nuevo producto'
                    : panelLateral === 'productos-modelo'
                    ? // De quién es el outfit que se está armando. Sin el nombre, el
                      // panel es idéntico al de los productos de la foto y no habría
                      // forma de saber a cuál de los tres se le está poniendo el buzo.
                      `Qué lleva ${
                        slotProductosLateral !== null &&
                        modelosGrupo[slotProductosLateral]
                          ? modelSelectionLabel(
                              modelosGrupo[slotProductosLateral],
                            )
                          : `este ${palabra.singular}`
                      }`
                    : panelLateral === 'modelo'
                    ? palabra.titulo
                    : panelLateral === 'pose'
                      ? 'Pose'
                      : panelLateral === 'fondo'
                        ? 'Fondo'
                        : panelLateral === 'formato'
                          ? 'Formato'
                          : panelLateral === 'moodboard'
                            ? 'Estilo'
                            : 'Productos'
                }
                subtitulo={
                  // La regla del tope sólo aplica con "Utilizar varios modelos" prendido:
                  // apagado se elige de a uno y "podés elegir hasta 3" prometía algo que
                  // la grilla no hace (ADR-2026-08-28-modelo-v2-varios-modelos).
                  panelLateral === 'modelo' && variosModelos
                    ? // La regla, y su estado. Antes de llegar al tope dice cuántos
                      // entran —el usuario no tiene por qué descubrir el límite
                      // chocándose con cards apagadas—; llegado al tope explica por qué
                      // el resto dejó de responder y cuál es la salida
                      // (ADR-2026-08-25-tres-modelos-como-maximo).
                      modelosElegidos >= MAX_MODELOS_SERIE
                      ? `Llegaste al máximo de ${MAX_MODELOS_SERIE} ${palabra.plural}. Sacá uno para elegir otro.`
                      : `Podés elegir hasta ${MAX_MODELOS_SERIE} ${palabra.plural}.`
                    : undefined
                }
                maxAncho={ANCHO_PANEL_LATERAL}
                // Dónde termina la columna de configuración, que el panel NO tapa.
                // Medido, no escrito — ver `offsetPanelLateral`. El `calc` queda de
                // respaldo para el primer render, antes de que la medición exista.
                offsetIzquierdo={offsetPanelLateral ?? 'calc(35% + 2rem)'}
                contenedorRef={areaDosColumnasRef}
              >
                {panelLateral === 'modelo' ? (
                  // La MISMA grilla de modelos del paso 2 de las otras variantes
                  // (`grilla`), acá dentro del panel.
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    {/* El interruptor que pasa la grilla de selección ÚNICA a
                        multi-select, igual que en el Catálogo
                        (ADR-2026-08-28-modelo-v2-varios-modelos). Antes la grilla
                        acumulaba SIEMPRE: cada tap sumaba una persona a la foto, cambiaba
                        el catálogo de poses (pasan a ser las de N personas) y movía el
                        costo, sin que en ningún momento se hubiera pedido una foto grupal.

                        `modelViewLateral === 'list'`: en create/upload el panel es un
                        formulario que ocupa todo, y ahí no hay grilla que
                        multi-seleccionar. */}
                    {onVariosModelosChange && modelViewLateral === 'list' && (
                      <SwitchSeleccionMultiple
                        titulo={`Utilizar varios ${palabra.plural}`}
                        activo={variosModelos}
                        onChange={onVariosModelosChange}
                        // Acá "varios" es UNA foto con todos adentro, no una foto por
                        // modelo: la bajada lo dice antes del primer tap, que es cuando
                        // todavía se puede elegir otra cosa. El tope y cómo salir de él
                        // siguen en el subtítulo del panel, que no se va con el scroll
                        // (ADR-2026-08-25-tres-modelos-como-maximo).
                        bajada={
                          !variosModelos
                            ? `Salen todos en la misma foto. Hasta ${MAX_MODELOS_SERIE}.`
                            : `${modelosElegidos} ${
                                modelosElegidos === 1
                                  ? `${palabra.singular} elegido`
                                  : `${palabra.plural} elegidos`
                              } — salen todos en la misma foto.`
                        }
                      />
                    )}
                    <ModelPickerInline
                      selection={modelSelection}
                      // Apagado, el tap REEMPLAZA (`onChange`); prendido, ACUMULA
                      // (`onToggleSelection`). Es la misma grilla: lo único que cambia es
                      // qué hace el tap.
                      onChange={(sel) => {
                        modeloElegidoPorElUsuario.current = true;
                        onModelChange(sel);
                      }}
                      view={modelViewLateral}
                      onViewChange={setModelViewLateral}
                      userModels={userModels}
                      userId={userAuthId}
                      companyId={userCompanyId}
                      onModelCreated={onModelCreated}
                      hideLabel
                      grilla
                      columnasGrilla={COLUMNAS_PANEL_LATERAL}
                      // Sin el interruptor prendido la grilla marca UNO: pasarle el grupo
                      // dibujaría checks en cards que el próximo tap va a reemplazar.
                      selectionsSerie={variosModelos ? modelSelectionsSerie : []}
                      onToggleSelection={
                        variosModelos && onToggleModelSerie
                          ? (sel) => {
                              modeloElegidoPorElUsuario.current = true;
                              onToggleModelSerie(sel);
                            }
                          : undefined
                      }
                    />
                  </div>
                ) : panelLateral === 'fondo' ? (
                  // Las categorías como PILLS y los fondos en la misma caja, igual que el
                  // picker de poses (ADR-2026-08-25-fondos-con-pills-como-poses). El
                  // `FondoPickerInline` mostraba cada carpeta como una card grande que
                  // salía al sheet full-screen: elegir un fondo obligaba a abandonar el
                  // panel, y con él la columna que se estaba armando.
                  //
                  // Describir y subir siguen saliendo al sheet: son formularios propios,
                  // no una grilla que elegir.
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    <FondoGrilla
                      selection={fondoSelection}
                      onChange={onFondoSelectionChange}
                      biblioteca={bibliotecaFondos}
                      guardados={fondosGuardados}
                      // La subida ocurre DENTRO del panel, sin saltar al sheet
                      // (ADR-2026-08-25-el-fondo-se-sube-en-el-panel).
                      companyId={userCompanyId}
                      onFondoSaved={onFondoSaved}
                      // El contexto del "Sugerime ideas": con qué productos y en qué
                      // rubro, para que las ideas no sean genéricas.
                      selectedProductNames={selectedProducts.map((p) => p.name)}
                      rubro={rubro}
                      // Aplicar una descripción CIERRA el panel: es el final del paso,
                      // como el "Listo" del panel de productos.
                      onAplicar={() => setPanelLateral(null)}
                    />
                  </div>
                ) : panelLateral === 'formato' ? (
                  // La grilla de formatos, en el mismo panel que los otros pickers
                  // (ADR-2026-08-24-formato-en-el-panel-lateral). Faltaba esta rama: sin
                  // ella, "Formato" caía en el `else` y abría la grilla de PRODUCTOS con
                  // el título "Formato" — ver
                  // ADR-2026-08-25-el-panel-lateral-no-cae-en-productos-por-descarte.
                  //
                  // Elegir CIERRA el panel: es una decisión de un solo tap, sin nada más
                  // que ajustar ahí adentro, así que quedarse abierto obligaría a un
                  // gesto de más para volver a la columna.
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    <FormatPickerGrid
                      selectedId={formatoVal}
                      onPick={(id) => {
                        onFormatoChange(id);
                        setPanelLateral(null);
                      }}
                      columnas={COLUMNAS_PANEL_LATERAL}
                    />
                  </div>
                ) : panelLateral === 'moodboard' ? (
                  // Las carpetas de Mi marca: elegir una entera o algunas fotos, y armarlas
                  // acá mismo (ADR-2026-10-18-moodboard-en-el-modo-modelo). Su propia rama:
                  // sin ella caería en la grilla de productos.
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    <PanelMoodboardModelo
                      companyId={userCompanyId}
                      brandId={brandId}
                      usuarioId={userAuthId}
                      seleccion={moodboard}
                      onCambiarSeleccion={setMoodboard}
                    />
                  </div>
                ) : panelLateral === 'pose' ? (
                  <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
                    <PosePickerInline
                      poses={allPoses}
                      customPoses={customPoses}
                      selectedIds={poseIds}
                      onChange={onPoseIdsChange}
                      // "Ver más" y la tarjeta de subir siguen saliendo al sheet de
                      // poses: son flujos propios (el catálogo completo con filtros, y
                      // la subida de un archivo) que no entran en esta caja.
                      onOpenSheet={onOpenPosePicker}
                      onSubirPose={onSubirPose}
                      // El alta ocurre DENTRO del panel, sin saltar al sheet
                      // (ADR-2026-08-24-subir-pose-sin-salir-del-panel).
                      onUploadCustomPose={onUploadCustomPose}
                      uploadingCustomPose={uploadingCustomPose}
                      hideLabel
                      grilla
                      columnasGrilla={COLUMNAS_PANEL_LATERAL}
                      // El toggle REAL, con su interruptor a la vista arriba de la
                      // grilla — ADR-2026-09-04-el-interruptor-de-varias-poses-tambien-en-desktop.
                      //
                      // Acá iba `multiPoseEnabled` en `true` fijo, porque el interruptor
                      // sólo existía en el sheet full-screen (que en desktop no se abre)
                      // y con el valor de `InterfazV2` la grilla quedaba en selección
                      // única sin ningún control para cambiarlo. Ahora el control está
                      // acá, así que el estado compartido puede mandar: es el mismo que
                      // ve mobile, y lo que se prende en una pantalla vale en la otra.
                      multiPoseEnabled={multiPoseEnabled}
                      onMultiPoseEnabledChange={onMultiPoseEnabledChange}
                      // NI `permitirVacio` NI `opcionAutomatica` —
                      // ADR-2026-09-04-el-modo-modelo-siempre-arranca-con-pose.
                      //
                      // Acá iban los dos: la tarjeta "Automática" encabezaba la grilla y
                      // el cero era un estado válido. El modo ahora arranca con la
                      // primera pose recomendada puesta, así que "Automática" no
                      // describe ningún estado alcanzable, y sin ella la única forma de
                      // llegar al cero era destildar la última pose — que deja el CTA
                      // apagado y la card sin nada que decir. Sin `permitirVacio`, ese
                      // tap no hace nada y siempre queda una pose elegida.
                    />
                  </div>
                ) : subiendoProductoEnPanel ? (
                  // El alta EN LUGAR de la grilla, no encima: se sale con "Cancelar" del
                  // propio panel o al terminar de subir, y ahí vuelve la grilla con el
                  // producto nuevo primero.
                  <div className="flex-1 min-h-0 flex flex-col">
                    <SubirProductoInline
                      userCompanyId={userCompanyId}
                      brandId={brandId}
                      rubro={rubro}
                      userPlan={userPlan}
                      onProductsRefreshed={onProductsRefreshed}
                      onSalir={() => setSubiendoProductoEnPanel(false)}
                    />
                  </div>
                ) : panelLateral === 'productos' ||
                  panelLateral === 'productos-modelo' ? (
                // El panel de productos arma su propio layout (header + grilla
                // scrolleable + pie) y su raíz es un `h-full` SIN `min-h-0`. Metido en un
                // flex column, ese `min-height: auto` implícito le deja crecer con la
                // grilla en vez de comprimirse: el raíz desbordaba, el `max-h` del panel
                // lo recortaba y el scroller de adentro se quedaba sin alto que repartir
                // —la grilla se veía cortada y no scrolleaba—.
                //
                // El `[&>div]:` alcanza a ese raíz y lo hace un flex item que se estira y
                // se comprime. Va acá y no editando `PickProductPanelModelo` porque el
                // `h-full` es correcto donde ese panel se usa normalmente (una columna con
                // altura propia); el que cambia las reglas es este contenedor.
                <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">
                <PickProductPanelModelo
                  // El MISMO panel que el de los productos de la foto, con otra
                  // selección adentro: abierto desde "Qué lleva cada modelo" escribe el
                  // outfit de ESE modelo y no el global
                  // (ADR-2026-08-25-que-lleva-cada-modelo-en-el-panel).
                  //
                  // La `key` no es decorativa: el panel siembra su selección UNA vez
                  // (`useState(() => initialSelectedIds)`). Sin remontar, pasar del
                  // modelo 1 al 2 dejaría marcado el outfit del primero.
                  key={
                    panelLateral === 'productos-modelo'
                      ? `slot-${slotProductosLateral}`
                      : 'foto'
                  }
                  variant="desktop"
                  // Selección EN VIVO: no hay paso de confirmación en esta pantalla, así
                  // que cada tap ya deja el producto puesto en la columna de al lado. El
                  // panel se cierra con su propio CTA, no eligiendo.
                  onSelectionChange={
                    panelLateral === 'productos-modelo' &&
                    slotProductosLateral !== null
                      ? (prods) => {
                          // Si lo elegido coincide con lo heredado, se BORRA la
                          // asignación en vez de guardarla igual. Dos razones: guardar
                          // "lo mismo que la foto" congela al modelo —cambiar los
                          // productos de la foto después ya no lo alcanzaría—, y el
                          // panel emite su selección al montar, así que abrir una fila
                          // y cerrarla sin tocar nada dejaba al modelo con una
                          // asignación propia vacía y en "Sin productos"
                          // (ADR-2026-08-25-que-lleva-cada-modelo-en-el-panel).
                          const heredado = selectedProducts
                            .map((p) => p.id)
                            .join('|');
                          const elegido = prods.map((p) => p.id).join('|');
                          onSlotProductsChange?.(
                            slotProductosLateral,
                            elegido === heredado ? null : prods,
                          );
                        }
                      : onProductosEnVivo ?? onConfirmProducts
                  }
                  onConfirm={() => setPanelLateral(null)}
                  // Sin footer: la selección es EN VIVO —cada tap ya deja el producto
                  // puesto en la columna de al lado—, así que "Listo" no confirmaba nada;
                  // sólo cerraba el panel, que es lo que hacen la X y la card que lo
                  // abrió. Un CTA que no decide nada se lee como si hiciera falta tocarlo.
                  hideFooter
                  onBack={() => setPanelLateral(null)}
                  // Un modelo SIN asignación propia hereda los productos de la foto —es
                  // lo que hace el payload (`modelo_config[idx]` cae al `variantIds`
                  // global)—, así que el panel abre mostrando eso y no vacío: lo que se
                  // ve es lo que se va a generar.
                  initialSelectedIds={(panelLateral === 'productos-modelo' &&
                  slotProductosLateral !== null
                    ? multiProductsPerSlot[slotProductosLateral] ??
                      selectedProducts
                    : selectedProducts
                  ).map((p) => p.id)}
                  tiposProductoSugeridos={preset.tiposProductoSugeridos}
                  rubro={rubro}
                  onStartUpload={onStartUpload}
                  onStartNewProduct={onStartNewProduct}
                  presetKind={preset.kind}
                  onSkipProduct={onSkipProduct}
                  // Tres por fila, las mismas que modelo y pose: los tres pickers se
                  // abren en el mismo panel y del mismo ancho, así que una grilla más
                  // densa que las otras hacía que el panel pareciera otro.
                  columnasFijas={COLUMNAS_PANEL_LATERAL}
                  ocultarBloqueSubida
                  nombreSobreLaFoto
                  onSubirProducto={() => setSubirEnVentana('uno')}
                  onSubirVarios={() => setSubirEnVentana('varios')}
                  pistasDeUso
                />
                </div>
                ) : null}
              </ProductosLateral>
            )}
          </div>
        )}
        </div>
      </div>
    </>
  );
};
